<template>
  <div class="supplier-orders-list-container">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>订单管理</span>
      </div>
    </div>
    
    <!-- 搜索区域 -->
    <div class="search-area">
      <el-input
        v-model="searchParams.orderId"
        placeholder="订单编号"
        clearable
        class="search-input"
      ></el-input>
      
      <el-input
        v-model="searchParams.orderTitle"
        placeholder="订单标题"
        clearable
        class="search-input"
        prefix-icon="el-icon-search"
      ></el-input>
      
      <el-select
        v-model="searchParams.status"
        placeholder="订单状态"
        clearable
        class="search-input"
      >
        <el-option
          v-for="item in statusOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        ></el-option>
      </el-select>
      
      <el-select
        v-model="searchParams.orderType"
        placeholder="订单类型"
        clearable
        class="search-input"
      >
        <el-option
          v-for="item in orderTypeOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        ></el-option>
      </el-select>
      
      <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
      
      <!-- 创建订单按钮 -->
      <el-button v-permission="'orders:supplier-orders-list:create'" type="success" @click="showCreateOrderDialog" icon="el-icon-plus" style="margin-left: 10px;">创建订单</el-button>
      
      <!-- 转发订单按钮 -->
      <el-button v-permission="'orders:supplier-orders-list:forward'" type="warning" @click="showForwardOrderDialog" icon="el-icon-share" style="margin-left: 10px;">转发订单</el-button>
    </div>
    
    <!-- 订单列表 -->
    <el-table
      :data="tableData"
      border
      style="width: 100%"
      v-loading="loading"
      :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
    >
      <el-table-column
        prop="orderId"
        label="订单编号"
        width="180"
      ></el-table-column>
      
      <el-table-column
        prop="orderTitle"
        label="订单标题"
        min-width="200"
      ></el-table-column>
      
      <el-table-column
        label="订单类型"
        width="100"
        align="center"
      >
        <template slot-scope="scope">
          <el-tag 
            :type="getOrderTypeTagType(scope.row.orderType)"
            size="small"
          >
            {{ getOrderTypeText(scope.row.orderType) }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column
        label="合同信息"
        min-width="180"
        align="center"
      >
        <template slot-scope="scope">
          <div v-if="scope.row.contractDetails && scope.row.contractDetails.length > 0">
            {{ scope.row.contractDetails.map(contract => contract.contractName || '--').join('，') }}
          </div>
          <div v-else-if="scope.row.contractName">
            {{ scope.row.contractName }}
          </div>
          <div v-else>--</div>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="productsTotal"
        label="产品总数"
        width="100"
        align="center"
      ></el-table-column>
      
      <el-table-column
        prop="priceTotal"
        label="总价"
        width="120"
        align="center"
      >
        <template slot-scope="scope">
          {{ scope.row.priceTotal | formatCurrency }}
        </template>
      </el-table-column>
      
      <el-table-column
        prop="status"
        label="订单状态"
        width="120"
        align="center"
      >
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.status)">
            {{ scope.row.status | formatStatus }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="createTime"
        label="创建时间"
        width="150"
        align="center"
      >
        <template slot-scope="scope">
          {{ scope.row.createTime | formatDate }}
        </template>
      </el-table-column>
      
      <el-table-column
        prop="creatorName"
        label="创建人"
        width="120"
        align="center"
      >
        <template slot-scope="scope">
          {{ maskName(scope.row.creatorName) || '--' }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="操作"
        width="220"
        align="center"
      >
        <template slot-scope="scope">
          <!-- 退货订单 - 只能查看，待确认状态才能删除 -->
          <div v-if="scope.row.orderType === '02'">
            <el-button
              type="primary"
              size="mini"
              @click="viewOrder(scope.row.id)"
            >查看</el-button>
            <el-button
              v-if="scope.row.status === 'ACTIVE'"
              type="danger"
              size="mini"
              @click="deleteOrder(scope.row.id)"
            >删除</el-button>
          </div>
          
          <!-- 待确认状态 -->
          <div v-else-if="scope.row.status === 'ACTIVE'">
            <el-button
              type="primary"
              size="mini"
              @click="viewOrder(scope.row.id)"
            >查看</el-button>
            <!-- 只有当前用户创建的订单才能编辑 -->
            <el-button
              v-if="isCurrentUserOrder(scope.row)"
              v-permission="'orders:supplier-orders-list:edit'"
              type="warning"
              size="mini"
              @click="editOrder(scope.row.id)"
            >编辑</el-button>
            <el-button
              v-permission="'orders:supplier-orders-list:delete'"
              type="danger"
              size="mini"
              @click="deleteOrder(scope.row.id)"
            >删除</el-button>
          </div>
          
          <!-- 已发货状态 -->
          <div v-else-if="scope.row.status === 'SENDING'">
            <el-button
              type="primary"
              size="mini"
              @click="viewOrder(scope.row.id)"
            >查看</el-button>
            <el-button
              v-permission="'orders:supplier-orders-list:return'"
              type="warning"
              size="mini"
              @click="returnOrder(scope.row.id)"
            >退货</el-button>
          </div>
          
          <!-- 进行中状态 -->
          <div v-else-if="scope.row.status === 'EXECUTING'">
            <el-button
              type="primary"
              size="mini"
              @click="viewOrder(scope.row.id)"
            >查看</el-button>
            <el-button
              v-if="canRevokeOrder(scope.row)"
              type="danger"
              size="mini"
              @click="revokeOrder(scope.row.id)"
            >撤销</el-button>
            <el-button
              v-permission="'orders:supplier-orders-list:return'"
              type="warning"
              size="mini"
              @click="returnOrder(scope.row.id)"
            >退货</el-button>
          </div>
          
          <!-- 已收货状态 -->
          <div v-else-if="scope.row.status === 'RECEIVING'">
            <el-button
              type="primary"
              size="mini"
              @click="viewOrder(scope.row.id)"
            >查看</el-button>
            <el-button
              v-permission="'orders:supplier-orders-list:return'"
              type="warning"
              size="mini"
              @click="returnOrder(scope.row.id)"
            >退货</el-button>
          </div>
          
          <!-- 已结束状态 -->
          <div v-else-if="scope.row.status === 'END'">
            <el-button
              type="primary"
              size="mini"
              @click="viewOrder(scope.row.id)"
            >查看</el-button>
            <el-button
              v-permission="'orders:supplier-orders-list:return'"
              type="warning"
              size="mini"
              @click="returnOrder(scope.row.id)"
            >退货</el-button>
          </div>
          
          <!-- 其他状态 -->
          <div v-else>
            <el-button
              type="primary"
              size="mini"
              @click="viewOrder(scope.row.id)"
            >查看</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      ></el-pagination>
    </div>
    
    <!-- 创建/编辑订单对话框 -->
    <el-dialog
      v-dialog-drag
      :title="isEditMode ? '编辑订单' : '创建订单'"
      :visible.sync="createOrderDialogVisible"
      width="70%"
      custom-class="order-form-dialog"
      :close-on-click-modal="false"
      :before-close="handleDialogClose"
    >
      <el-form :model="orderForm" ref="orderForm" label-width="100px" :rules="orderFormRules">
        <!-- 创建模式选择 -->
        <el-form-item label="创建模式" v-if="!isEditMode">
          <el-radio-group v-model="createMode" @change="handleCreateModeChange">
            <el-radio label="supplier">通过供应商创建</el-radio>
            <el-radio label="contract">通过合同创建</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <!-- 供应商选择模式 -->
        <div v-if="createMode === 'supplier' && !isEditMode">
          <el-form-item label="选择供应商" prop="selected_supplier_id">
            <el-select 
              v-model="orderForm.selected_supplier_id" 
              placeholder="请先选择供应商" 
              filterable 
              @change="handleSupplierChange"
              style="width: 100%;"
            >
              <el-option 
                v-for="item in supplierOptions" 
                :key="item.id" 
                :label="item.unitName" 
                :value="item.id"
              >
                <div>
                  <span>{{ item.unitName }}</span>
                  <div style="font-size: 12px; color: #999;">
                    <span>简称: {{ item.unitShortName || '无' }}</span>
                    <span style="float: right;">联系人: {{ maskName(item.contactPerson) || '无' }}</span>
                  </div>
                </div>
              </el-option>
            </el-select>
          </el-form-item>
          
          <!-- 供应商的合同选择（多选） -->
          <el-form-item label="选择合同" prop="contract_ids" v-if="orderForm.selected_supplier_id">
            <el-select 
              v-model="orderForm.contract_ids" 
              placeholder="请选择该供应商的合同（可多选）" 
              filterable 
              multiple
              @change="handleMultipleContractChange"
              style="width: 100%;"
            >
              <el-option 
                v-for="item in supplierContractOptions" 
                :key="item.id" 
                :label="`${item.contractName} - ${item.supplierUnit?.unitName || '未知供应商'}`" 
                :value="item.id"
              >
                <div>
                  <span>{{ item.contractName }} - {{ item.supplierUnit?.unitName || '未知供应商' }}</span>
                  <div style="font-size: 12px; color: #999;">
                    <span>合同编号: {{ item.contractNumber || item.id }}</span>
                    <span style="float: right;">{{ item.createTime | formatDate }}</span>
                  </div>
                </div>
              </el-option>
            </el-select>
            <div class="form-tip">提示：多选合同时，系统会自动验证所选合同是否属于同一供应商且计价方式相同</div>
          </el-form-item>
        </div>
        
        <!-- 合同选择模式 -->
        <div v-if="createMode === 'contract' && !isEditMode">
          <el-form-item label="选择合同" prop="contract_ids">
            <el-select 
              v-model="orderForm.contract_ids" 
              placeholder="请选择合同（可多选）" 
              filterable 
              multiple
              @change="handleMultipleContractChange"
              style="width: 100%;"
            >
              <el-option 
                v-for="item in contractOptions" 
                :key="item.id" 
                :label="`${item.contractName} - ${item.supplierUnit?.unitName || '未知供应商'}`" 
                :value="item.id"
              >
                <div>
                  <span>{{ item.contractName }} - {{ item.supplierUnit?.unitName || '未知供应商' }}</span>
                  <div style="font-size: 12px; color: #999;">
                    <span>合同编号: {{ item.contractNumber || item.id }}</span>
                    <span style="float: right;">{{ item.createTime | formatDate }}</span>
                  </div>
                </div>
              </el-option>
            </el-select>
            <div class="form-tip">提示：多选合同时，系统会自动验证所选合同是否属于同一供应商且计价方式相同</div>
          </el-form-item>
        </div>
        
        <!-- 编辑模式下的合同选择 -->
        <el-form-item label="选择合同" prop="contract_id" v-if="isEditMode">
          <el-select 
            v-model="orderForm.contract_ids" 
            placeholder="请选择合同" 
            multiple
            filterable 
            @change="handleMultipleContractChange"
            style="width: 100%;"
          >
            <el-option 
              v-for="item in contractOptions" 
              :key="item.id" 
              :label="`${item.contractName} - ${item.supplierUnit?.unitName || '未知供应商'}`" 
              :value="item.id"
            >
              <div>
                <span>{{ item.contractName }} - {{ item.supplierUnit?.unitName || '未知供应商' }}</span>
                <div style="font-size: 12px; color: #999;">
                  <span>合同编号: {{ item.contractNumber || item.id }}</span>
                  <span style="float: right;">{{ item.createTime | formatDate }}</span>
                </div>
              </div>
            </el-option>
          </el-select>
          <div class="form-tip">编辑模式下可以调整合同选择，系统会自动验证所选合同是否属于同一供应商且计价方式相同</div>
        </el-form-item>
        
        <!-- 订单信息展示区域 -->
        <div v-if="orderForm.order_title || (orderForm.contract_ids && orderForm.contract_ids.length > 0) || orderForm.contract_id || (isEditMode && orderForm.contract_ids && orderForm.contract_ids.length > 0)" class="order-info-display">
          <!-- 订单标题 -->
          <el-form-item label="订单标题" prop="order_title">
            <el-input v-model="orderForm.order_title" placeholder="请输入订单标题" class="order-title-input"></el-input>
          </el-form-item>
          
          <!-- 合同信息展示 -->
          <div class="contract-info-display">
            <!-- 第一行：需求单位，合同名称 -->
            <el-row :gutter="20" class="info-row">
              <el-col :span="12">
                <div class="info-item">
                  <label>需求单位：</label>
                  <span>{{ displayContractUnits }}</span>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="info-item">
                  <label>合同名称：</label>
                  <span>{{ displayContractNames }}</span>
                </div>
              </el-col>
            </el-row>
            
            <!-- 第二行：二级机构，合同编号 -->
            <el-row :gutter="20" class="info-row">
              <el-col :span="12">
                <div class="info-item">
                  <label>二级机构：</label>
                  <span>{{ displayProjectNames }}</span>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="info-item">
                  <label>合同编号：</label>
                  <span>{{ displayContractNumbers }}</span>
                </div>
              </el-col>
            </el-row>
            
            <!-- 第三行：收货地址，供货单位 -->
            <el-row :gutter="20" class="info-row">
              <el-col :span="12">
                <div class="info-item">
                  <label>收货地址：</label>
                  <div class="address-selector">
                    <!-- 地址来源选择器（项目人员仅可选项目地址，不显示切换） -->
                    <el-radio-group 
                      v-if="!isProjectPersonnel" 
                      v-model="orderForm.address_source" 
                      @change="handleAddressSourceChange"
                      size="small"
                      style="margin-bottom: 8px;"
                    >
                      <el-radio label="project">使用项目地址</el-radio>
                      <el-radio label="unit">使用单位地址</el-radio>
                    </el-radio-group>
                    <el-input 
                      v-model="orderForm.contact_address" 
                      placeholder="请输入收货地址" 
                      size="small"
                    ></el-input>
                    <!-- 显示可用的地址选项 -->
                    <div v-if="orderForm.available_addresses && (orderForm.available_addresses.unit_address || (orderForm.available_addresses.project_addresses && orderForm.available_addresses.project_addresses.length))" class="address-options">
                      <div
                        v-for="proj in orderForm.available_addresses.project_addresses"
                        :key="proj.id"
                        class="address-option"
                      >
                        <span class="address-label">{{ proj.projectName }}：</span>
                        <span class="address-text">{{ proj.address }}</span>
                        <el-button
                          type="text"
                          size="mini"
                          @click="useProjectAddress(proj)"
                          v-if="orderForm.address_source !== 'project' || orderForm.selected_project_id !== proj.id"
                        >使用</el-button>
                      </div>
                      <div v-if="!isProjectPersonnel && orderForm.available_addresses && orderForm.available_addresses.unit_address" class="address-option">
                        <span class="address-label">单位地址：</span>
                        <span class="address-text">{{ orderForm.available_addresses.unit_address }}</span>
                        <el-button 
                          type="text" 
                          size="mini" 
                          @click="useAddress('unit')"
                          v-if="orderForm.address_source !== 'unit'"
                        >使用</el-button>
                      </div>
                    </div>
                  </div>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="info-item">
                  <label>供货单位：</label>
                  <span>{{ orderForm.supplier_name || '未选择' }}</span>
                </div>
              </el-col>
            </el-row>
            
            <!-- 第四行：联系人 -->
            <el-row :gutter="20" class="info-row">
              <el-col :span="12">
                <div class="info-item">
                  <label>联系人：</label>
                  <el-select 
                    v-model="selectedContactId" 
                    placeholder="请选择联系人" 
                    filterable
                    @change="handleContactChange"
                    size="small"
                    style="width: 100%;"
                  >
                    <el-option 
                      v-for="contact in unitContacts" 
                      :key="contact.id || contact.userId" 
                      :label="`${maskName(contact.realName || contact.username || '未知')}${contact.phone ? ' - ' + maskPhone(contact.phone) : ''}`" 
                      :value="contact.id || contact.userId"
                    >
                      <div>
                        <span>{{ maskName(contact.realName || contact.username || '未知') }}</span>
                        <span v-if="contact.phone" style="color: #909399; font-size: 12px; margin-left: 8px;">{{ maskPhone(contact.phone) }}</span>
                      </div>
                    </el-option>
                  </el-select>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="info-item">
                  <label>供应商联系人：</label>
                  <el-select 
                    v-model="selectedSupplierContactId" 
                    placeholder="请选择供应商联系人" 
                    filterable
                    @change="handleSupplierContactChange"
                    size="small"
                    style="width: 100%;"
                    :disabled="!orderForm.supplier_id"
                  >
                    <el-option 
                      v-for="contact in supplierUnitContacts" 
                      :key="contact.id || contact.userId" 
                      :label="`${maskName(contact.realName || contact.username || '未知')}${contact.phone ? ' - ' + maskPhone(contact.phone) : ''}`" 
                      :value="contact.id || contact.userId"
                    >
                      <div>
                        <span>{{ maskName(contact.realName || contact.username || '未知') }}</span>
                        <span v-if="contact.phone" style="color: #909399; font-size: 12px; margin-left: 8px;">{{ maskPhone(contact.phone) }}</span>
                      </div>
                    </el-option>
                  </el-select>
                </div>
              </el-col>
            </el-row>
            
            <!-- 第五行：联系电话 -->
            <el-row :gutter="20" class="info-row">
              <el-col :span="12">
                <div class="info-item">
                  <label>联系电话：</label>
                  <el-input v-model="orderForm.contract_unit_phone" placeholder="联系电话（自动填充）" size="small" readonly></el-input>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="info-item">
                  <label>供应商电话：</label>
                  <el-input v-model="orderForm.supplier_phone" placeholder="供应商电话（自动填充）" size="small" readonly></el-input>
                </div>
              </el-col>
            </el-row>
          </div>
        </div>
        
        <!-- 材料清单 -->
        <el-form-item label="合同清单" class="contract-items-form-item">
          <div v-if="orderForm.order_items.length > 0" class="contract-items-filter">
            <el-input
              v-model="contractItemFilterKeyword"
              placeholder="筛选材料（名称、规格、材质、单位、备注）"
              clearable
              prefix-icon="el-icon-search"
              size="small"
              class="contract-items-filter-input"
            ></el-input>
            <span v-if="contractItemFilterKeyword" class="contract-items-filter-tip">
              显示 {{ filteredOrderItems.length }} / {{ orderForm.order_items.length }} 项
            </span>
          </div>
          <el-table
            :data="filteredOrderItems"
            border
            fit
            stripe
            highlight-current-row
            class="contract-items-table"
            :max-height="orderItemsTableMaxHeight"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
            v-loading="materialsLoading"
          >
            <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
            <el-table-column prop="productName" label="材料名称" min-width="120"></el-table-column>
            <el-table-column prop="specification" label="规格型号" width="150">
              <template slot-scope="scope">
                <el-tooltip
                  v-if="scope.row.specification"
                  :content="scope.row.specification"
                  placement="top"
                  effect="dark"
                  popper-class="spec-tooltip"
                >
                  <span>{{ truncateText(scope.row.specification, 20) }}</span>
                </el-tooltip>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="materialRequirement" label="材质要求" width="120"></el-table-column>
            <el-table-column prop="unit" label="单位" width="80" align="center"></el-table-column>
            <el-table-column label="数量" width="200" align="center">
              <template slot-scope="scope">
                <div style="display: flex; align-items: center;">
                  <el-button 
                    icon="el-icon-minus" 
                    size="mini" 
                    @click="decreaseQuantity(scope.row)"
                    :disabled="!scope.row.quantity || parseFloat(scope.row.quantity) <= 0"
                    style="flex-shrink: 0;"
                  ></el-button>
                  <el-input 
                    :value="scope.row.quantity === null || scope.row.quantity === undefined ? '' : scope.row.quantity" 
                    type="number"
                    :min="0" 
                    :max="999999" 
                    :step="0.0001"
                    size="small"
                    placeholder="请输入数量"
                    @blur="handleQuantityBlur(scope.row)"
                    @input="handleQuantityInput(scope.row, $event)"
                    style="flex: 1; margin: 0 4px;"
                  ></el-input>
                  <el-button 
                    icon="el-icon-plus" 
                    size="mini" 
                    @click="increaseQuantity(scope.row)"
                    style="flex-shrink: 0;"
                  ></el-button>
                </div>
              </template>
            </el-table-column>
            <!-- 固定价合同显示合同价 -->
            <el-table-column label="合同价" width="120" align="center" v-if="!hasFloatingPriceContract">
              <template slot-scope="scope">
                {{ formatUnitPrice(scope.row.taxInclusiveUnitPrice) }}
              </template>
            </el-table-column>
            <!-- 变动价合同显示含税固定价 -->
            <el-table-column label="含税固定价(元)" width="120" align="center" v-if="hasFloatingPriceContract">
              <template slot-scope="scope">
                {{ formatAmount(scope.row.taxInclusiveBasePrice) }}
              </template>
            </el-table-column>
            <!-- 变动价合同显示含税变动价 -->
            <el-table-column label="含税变动价(元)" width="120" align="center" v-if="hasFloatingPriceContract">
              <template slot-scope="scope">
                {{ formatAmount(scope.row.taxInclusiveFloatingPrice) }}
              </template>
            </el-table-column>
            <el-table-column label="到货日期" width="150" align="center">
              <template slot-scope="scope">
                <el-date-picker
                  v-model="scope.row.deliveryDate"
                  type="date"
                  placeholder="选择日期"
                  format="yyyy-MM-dd"
                  value-format="yyyy-MM-dd"
                  size="small"
                  style="width: 100%"
                ></el-date-picker>
              </template>
            </el-table-column>
            <!-- 多合同订单时显示来源合同信息 -->
            <el-table-column label="来源合同" width="150" align="center" v-if="!isEditMode && orderForm.contract_ids && orderForm.contract_ids.length > 1">
              <template slot-scope="scope">
                <el-tooltip 
                  :content="scope.row.sourceContracts ? scope.row.sourceContracts.join(', ') : '未知'" 
                  placement="top"
                  v-if="scope.row.sourceContracts && scope.row.sourceContracts.length > 0"
                >
                  <span class="source-contracts">
                    {{ scope.row.sourceContracts.length > 1 ? `${scope.row.sourceContracts.length}个合同` : scope.row.sourceContracts[0] }}
                  </span>
                </el-tooltip>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column label="备注" min-width="120">
              <template slot-scope="scope">
                <el-input 
                  v-model="scope.row.remark" 
                  placeholder="请输入备注"
                  size="small"
                ></el-input>
              </template>
            </el-table-column>
            <!-- 操作列 -->
            <el-table-column label="操作" width="100" align="center">
              <template slot-scope="scope">
                <el-button
                  type="danger"
                  size="mini"
                  icon="el-icon-delete"
                  @click="removeMaterialItem(scope.row)"
                  title="删除此产品"
                >删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="total-info" v-if="orderForm.order_items.length > 0">
            <span>合计数量: {{ formatQuantity(totalQuantity) }}</span>
            <span style="margin-left: 20px;">合计金额: {{ formatAmount(totalPrice) }} 元</span>
            <!-- 多合同订单时显示合同信息 -->
            <div v-if="!isEditMode && orderForm.contract_ids && orderForm.contract_ids.length > 1" class="contract-info">
              <span>涉及合同: {{ orderForm.contract_ids.length }} 个</span>
            </div>
          </div>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleDialogClose">取 消</el-button>
        <el-button type="primary" @click="submitOrderForm" :loading="submitLoading">{{ isEditMode ? '更 新' : '保 存' }}</el-button>
      </div>
    </el-dialog>
    
    <!-- 转发订单对话框 -->
    <el-dialog
      v-dialog-drag
      title="转发订单"
      :visible.sync="forwardOrderDialogVisible"
      width="70%"
      custom-class="order-form-dialog"
      :close-on-click-modal="false"
      :before-close="handleForwardDialogClose"
    >
      <el-form :model="forwardForm" ref="forwardForm" label-width="100px" :rules="forwardFormRules">
        <!-- 第一步：选择执行中的采购合同（支持多选） -->
        <el-form-item label="选择合同" prop="contractIds">
          <el-select 
            v-model="forwardForm.contractIds" 
            placeholder="请选择执行中的采购合同（可多选）" 
            filterable 
            multiple
            @change="handleMultipleContractSelect"
            style="width: 100%;"
          >
            <el-option 
              v-for="item in executingContracts" 
              :key="item.id" 
              :label="`${item.contractName} - ${item.supplierUnit?.unitName || '未知供应商'}`" 
              :value="item.id"
            >
              <div>
                <span>{{ item.contractName }} - {{ item.supplierUnit?.unitName || '未知供应商' }}</span>
                <div style="font-size: 12px; color: #999;">
                  <span>合同编号: {{ item.contractNumber || item.id }}</span>
                  <span style="float: right;">{{ item.createTime | formatDate }}</span>
                </div>
              </div>
            </el-option>
          </el-select>
          <div class="form-tip">提示：多选合同时，系统会自动验证所选合同是否属于同一供应商且计价方式相同</div>
        </el-form-item>
        
        <!-- 第二步：选择要转发的销售订单 -->
        <el-form-item label="选择订单" prop="orderId">
          <!-- 已选订单显示 -->
          <div v-if="forwardForm.orderId && selectedOrderForDisplay" style="margin-bottom: 10px; padding: 10px; background: #f5f7fa; border-radius: 4px;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div>
                <div style="font-weight: 500; margin-bottom: 4px;">{{ selectedOrderForDisplay.orderTitle || selectedOrderForDisplay.orderId }}</div>
                <div style="font-size: 12px; color: #666;">
                  <span>订单编号: {{ selectedOrderForDisplay.orderId || selectedOrderForDisplay.id }}</span>
                  <span style="margin-left: 16px;">状态: {{ selectedOrderForDisplay.status | formatStatus }}</span>
                  <span v-if="selectedOrderForDisplay.priceTotal" style="margin-left: 16px;">总价: {{ selectedOrderForDisplay.priceTotal | formatCurrency }}</span>
                </div>
              </div>
              <el-button type="text" size="small" @click="clearSelectedOrder">清除选择</el-button>
            </div>
          </div>
          
          <!-- 订单列表表格 -->
          <div style="border: 1px solid #dcdfe6; border-radius: 4px; max-height: 400px; overflow: auto;">
            <el-table
              ref="orderSelectTable"
              :data="filteredSalesOrders"
              border
              highlight-current-row
              @current-change="handleOrderTableSelect"
              v-loading="salesOrdersLoading"
              :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
              style="width: 100%"
              :row-class-name="getOrderRowClassName"
            >
              <el-table-column label="选择" width="80" align="center">
                <template slot-scope="scope">
                  <el-radio
                    :value="forwardForm.orderId"
                    :label="scope.row.id"
                    @change="handleOrderTableSelect(scope.row)"
                  >
                    &nbsp;
                  </el-radio>
                </template>
              </el-table-column>
              
              <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
              
              <el-table-column
                prop="orderId"
                label="订单编号"
                width="180"
                show-overflow-tooltip
              ></el-table-column>
              
              <el-table-column
                prop="orderTitle"
                label="订单标题"
                min-width="250"
                show-overflow-tooltip
              ></el-table-column>
              
              <el-table-column
                label="订单状态"
                width="120"
                align="center"
              >
                <template slot-scope="scope">
                  <el-tag :type="getStatusType(scope.row.status)" size="small">
                    {{ scope.row.status | formatStatus }}
                  </el-tag>
                </template>
              </el-table-column>
              
              <el-table-column
                label="总价"
                width="120"
                align="right"
              >
                <template slot-scope="scope">
                  {{ scope.row.priceTotal | formatCurrency }}
                </template>
              </el-table-column>
              
              <el-table-column
                prop="productsTotal"
                label="产品数"
                width="100"
                align="center"
              ></el-table-column>
              
              <el-table-column
                prop="createTime"
                label="创建时间"
                width="160"
                align="center"
              >
                <template slot-scope="scope">
                  {{ scope.row.createTime | formatDate }}
                </template>
              </el-table-column>
            </el-table>
          </div>
          
          <!-- 搜索框 -->
          <div style="margin-top: 10px;">
            <el-input
              v-model="orderSearchKeyword"
              placeholder="搜索订单编号、订单标题..."
              prefix-icon="el-icon-search"
              clearable
              size="small"
            ></el-input>
          </div>
        </el-form-item>
        
        <!-- 订单基本信息 -->
        <div v-if="forwardForm.contractIds.length > 0 && forwardForm.orderId" class="order-info-display">
          <!-- 订单标题 -->
          <el-form-item label="订单标题" prop="orderTitle">
            <el-input v-model="forwardForm.orderTitle" placeholder="请输入订单标题" class="order-title-input"></el-input>
          </el-form-item>
          
          <!-- 合同信息展示 -->
          <div class="contract-info-display">
            <!-- 单合同显示 -->
            <div v-if="!hasMultipleSuppliers">
              <!-- 第一行：需求单位，合同名称 -->
              <el-row :gutter="20" class="info-row">
                <el-col :span="12">
                  <div class="info-item">
                    <label>需求单位：</label>
                    <span>{{ getCurrentUnitName() }}</span>
                    <span style="color: #909399; font-size: 12px; margin-left: 8px;">(转发后变更为当前单位)</span>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="info-item">
                    <label>合同名称：</label>
                    <span>{{ selectedContracts[0]?.contractName || '未知合同' }}</span>
                  </div>
                </el-col>
              </el-row>
              
              <!-- 第二行：二级机构，合同编号 -->
              <el-row :gutter="20" class="info-row">
                <el-col :span="12">
                  <div class="info-item">
                    <label>二级机构：</label>
                    <span>{{ getOriginalProjectNames() }}</span>
                    <span style="color: #909399; font-size: 12px; margin-left: 8px;">(保持原订单项目)</span>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="info-item">
                    <label>合同编号：</label>
                    <span>{{ selectedContracts[0]?.contractNumber || selectedContracts[0]?.id || '未知编号' }}</span>
                  </div>
                </el-col>
              </el-row>
              
              <!-- 第三行：收货地址，供货单位 -->
              <el-row :gutter="20" class="info-row">
                <el-col :span="12">
                  <div class="info-item">
                    <label>收货地址：</label>
                    <el-input v-model="forwardForm.deliveryAddress" placeholder="请输入收货地址" size="small"></el-input>
                    <span style="color: #909399; font-size: 12px; margin-left: 8px;">(保持原订单地址)</span>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="info-item">
                    <label>供货单位：</label>
                    <span>{{ selectedContracts[0]?.supplierUnit?.unitName || '未知供应商' }}</span>
                  </div>
                </el-col>
              </el-row>
              
              <!-- 第四行：联系人，供应商联系人 -->
              <el-row :gutter="20" class="info-row">
                <el-col :span="12">
                  <div class="info-item">
                    <label>联系人：</label>
                    <div style="flex: 1;">
                      <el-select 
                        v-model="forwardSelectedContactId" 
                        placeholder="请选择联系人" 
                        filterable
                        @change="handleForwardContactChange"
                        size="small"
                        style="width: 100%;"
                      >
                        <el-option 
                          v-for="contact in unitContacts" 
                          :key="contact.id || contact.userId" 
                          :label="`${maskName(contact.realName || contact.username || '未知')}${contact.phone ? ' - ' + maskPhone(contact.phone) : ''}`" 
                          :value="contact.id || contact.userId"
                        >
                          <div>
                            <span>{{ maskName(contact.realName || contact.username || '未知') }}</span>
                            <span v-if="contact.phone" style="color: #909399; font-size: 12px; margin-left: 8px;">{{ maskPhone(contact.phone) }}</span>
                          </div>
                        </el-option>
                      </el-select>
                    </div>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="info-item">
                    <label>供应商联系人：</label>
                    <el-select
                      v-model="forwardSelectedSupplierContactId"
                      placeholder="请选择供应商联系人"
                      filterable
                      @change="handleForwardSupplierContactChange"
                      size="small"
                      style="width: 100%;"
                      :disabled="!selectedContracts[0]?.supplierUnitId"
                    >
                      <el-option
                        v-for="contact in forwardSupplierUnitContacts"
                        :key="contact.id || contact.userId"
                        :label="`${maskName(contact.realName || contact.username || '未知')}${contact.phone ? ' - ' + maskPhone(contact.phone) : ''}`"
                        :value="contact.id || contact.userId"
                      >
                        <div>
                          <span>{{ maskName(contact.realName || contact.username || '未知') }}</span>
                          <span v-if="contact.phone" style="color: #909399; font-size: 12px; margin-left: 8px;">{{ maskPhone(contact.phone) }}</span>
                        </div>
                      </el-option>
                    </el-select>
                  </div>
                </el-col>
              </el-row>
              
              <!-- 第五行：联系电话，供应商电话 -->
              <el-row :gutter="20" class="info-row">
                <el-col :span="12">
                  <div class="info-item">
                    <label>联系电话：</label>
                    <el-input v-model="forwardForm.demandContactPhone" placeholder="请输入联系电话" size="small"></el-input>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="info-item">
                    <label>供应商电话：</label>
                    <el-input v-model="forwardForm.supplierContactPhone" placeholder="供应商电话（自动填充）" size="small" readonly></el-input>
                  </div>
                </el-col>
              </el-row>
            </div>
            
            <!-- 多合同显示 -->
            <div v-else class="multi-contract-display">
              <div class="multi-contract-header">
                <h4>多合同信息</h4>
                <span style="color: #909399; font-size: 12px;">检测到多个供应商，将分别创建订单</span>
              </div>
              
              <!-- 基本信息（所有合同共用） -->
              <div class="common-info">
                <el-row :gutter="20" class="info-row">
                  <el-col :span="12">
                    <div class="info-item">
                      <label>需求单位：</label>
                      <span>{{ getCurrentUnitName() }}</span>
                      <span style="color: #909399; font-size: 12px; margin-left: 8px;">(转发后变更为当前单位)</span>
                    </div>
                  </el-col>
                  <el-col :span="12">
                    <div class="info-item">
                      <label>二级机构：</label>
                      <span>{{ getOriginalProjectNames() }}</span>
                      <span style="color: #909399; font-size: 12px; margin-left: 8px;">(保持原订单项目)</span>
                    </div>
                  </el-col>
                </el-row>
                
                <el-row :gutter="20" class="info-row">
                  <el-col :span="12">
                    <div class="info-item">
                      <label>收货地址：</label>
                      <el-input v-model="forwardForm.deliveryAddress" placeholder="请输入收货地址" size="small"></el-input>
                      <span style="color: #909399; font-size: 12px; margin-left: 8px;">(保持原订单地址)</span>
                    </div>
                  </el-col>
                  <el-col :span="12">
                    <div class="info-item">
                      <label>联系人：</label>
                      <div style="flex: 1;">
                        <el-select 
                          v-model="forwardSelectedContactId" 
                          placeholder="请选择联系人" 
                          filterable
                          @change="handleForwardContactChange"
                          size="small"
                          style="width: 100%;"
                        >
                          <el-option 
                            v-for="contact in unitContacts" 
                            :key="contact.id || contact.userId" 
                            :label="`${maskName(contact.realName || contact.username || '未知')}${contact.phone ? ' - ' + maskPhone(contact.phone) : ''}`" 
                            :value="contact.id || contact.userId"
                          >
                            <div>
                              <span>{{ contact.realName || contact.username || '未知' }}</span>
                              <span v-if="contact.phone" style="color: #909399; font-size: 12px; margin-left: 8px;">{{ contact.phone }}</span>
                            </div>
                          </el-option>
                        </el-select>
                      </div>
                    </div>
                  </el-col>
                </el-row>
                
                <el-row :gutter="20" class="info-row">
                  <el-col :span="12">
                    <div class="info-item">
                      <label>联系电话：</label>
                      <el-input v-model="forwardForm.demandContactPhone" placeholder="请输入联系电话" size="small"></el-input>
                    </div>
                  </el-col>
                </el-row>
              </div>
              
              <!-- 各供应商合同信息 -->
              <div class="supplier-contracts">
                <div v-for="(contract, index) in selectedContracts" :key="contract.id" class="supplier-contract-item">
                  <div class="supplier-contract-header">
                    <span class="supplier-name">{{ contract.supplierUnit?.unitName || '未知供应商' }}</span>
                    <span class="contract-count">（合同 {{ index + 1 }}）</span>
                  </div>
                  
                  <div class="supplier-contract-details">
                    <el-row :gutter="20" class="info-row">
                      <el-col :span="12">
                        <div class="info-item">
                          <label>合同名称：</label>
                          <span>{{ contract.contractName || '未知合同' }}</span>
                        </div>
                      </el-col>
                      <el-col :span="12">
                        <div class="info-item">
                          <label>合同编号：</label>
                          <span>{{ contract.contractNumber || contract.id || '未知编号' }}</span>
                        </div>
                      </el-col>
                    </el-row>
                    
                    <el-row :gutter="20" class="info-row">
                      <el-col :span="12">
                        <div class="info-item">
                          <label>供应商联系人：</label>
                          <span>{{ maskName(contract.supplierUnit?.contactPerson) || '未知联系人' }}</span>
                        </div>
                      </el-col>
                      <el-col :span="12">
                        <div class="info-item">
                          <label>供应商电话：</label>
                          <span>{{ maskPhone(contract.supplierUnit?.phone) || '未知电话' }}</span>
                        </div>
                      </el-col>
                    </el-row>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 产品清单对比和验证 -->
        <div v-if="forwardForm.contractIds.length > 0 && forwardForm.orderId" class="product-comparison">
          <h4>产品清单对比</h4>
          
          <!-- 多需求方提示 -->
          <div v-if="hasMultipleCustomers" class="multi-customer-tip">
            <el-alert
              title="检测到多个需求方"
              :description="`当前合同包含 ${availableCustomers.length} 个需求方，请为每个产品选择对应的需求方`"
              type="warning"
              :closable="false"
              show-icon
            ></el-alert>
          </div>
          
          <div v-if="validationResult.isValid" class="validation-success">
            <i class="el-icon-success"></i>
            <span>产品清单验证通过，可以转发订单</span>
          </div>
          <div v-else class="validation-error">
            <i class="el-icon-error"></i>
            <span>{{ validationResult.message }}</span>
          </div>
          
          <!-- 产品对比表格 -->
          <el-table
            :data="productComparison"
            border
            style="width: 100%; margin-top: 15px;"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
          >
            <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
            <el-table-column prop="productName" label="产品名称" min-width="150"></el-table-column>
            <el-table-column prop="specification" label="规格型号" width="150">
              <template slot-scope="scope">
                <el-tooltip
                  v-if="scope.row.specification"
                  :content="scope.row.specification"
                  placement="top"
                  effect="dark"
                  popper-class="spec-tooltip"
                >
                  <span>{{ truncateText(scope.row.specification, 20) }}</span>
                </el-tooltip>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="materialRequirement" label="材质要求" min-width="180">
              <template slot-scope="scope">
                <el-tooltip
                  v-if="scope.row.materialRequirement"
                  :content="scope.row.materialRequirement"
                  placement="top"
                  effect="dark"
                  popper-class="spec-tooltip"
                >
                  <span>{{ truncateText(scope.row.materialRequirement, 25) }}</span>
                </el-tooltip>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="unit" label="单位" width="80" align="center"></el-table-column>
            <el-table-column label="转发订单数量" width="120" align="center">
              <template slot-scope="scope">
                {{ scope.row.forwardQuantity }}
              </template>
            </el-table-column>
            <el-table-column label="合同可用数量" width="120" align="center">
              <template slot-scope="scope">
                {{ scope.row.contractQuantity }}
              </template>
            </el-table-column>
            <el-table-column label="转发订单单价" width="120" align="center">
              <template slot-scope="scope">
                {{ formatAmount(scope.row.forwardPrice) }}
              </template>
            </el-table-column>
            <el-table-column label="合同单价" width="120" align="center">
              <template slot-scope="scope">
                {{ formatAmount(scope.row.contractPrice) }}
              </template>
            </el-table-column>
            
            <!-- 新增：供应商名称列 -->
            <el-table-column label="供应商名称" width="150" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.supplierName || '未知供应商' }}</span>
              </template>
            </el-table-column>
            
            <!-- 新增需求方选择列 -->
            <el-table-column label="需求方" width="200" align="center" v-if="hasMultipleCustomers">
              <template slot-scope="scope">
                <el-select 
                  v-model="scope.row.selectedCustomerId" 
                  placeholder="请选择需求方"
                  size="small"
                  @change="handleCustomerChange(scope.$index, scope.row)"
                >
                  <el-option
                    v-for="customer in availableCustomers"
                    :key="customer.id"
                    :label="customer.unitName || customer.unit_name"
                    :value="customer.id"
                  ></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="100" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.isValid ? 'success' : 'danger'" size="small">
                  {{ scope.row.isValid ? '通过' : '不足' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
          
          <!-- 订单分组预览 -->
          <div v-if="hasMultipleCustomers && groupedOrders.length > 0" class="order-group-preview">
            <h4>订单分组预览</h4>
            <div v-for="(group, index) in groupedOrders" :key="index" class="order-group">
              <div class="group-header">
                <span class="group-title">订单 {{ index + 1 }}：{{ group.customerName }}</span>
                <span class="group-count">{{ group.items.length }} 个产品</span>
              </div>
              <el-table
                :data="group.items"
                size="mini"
                border
                style="width: 100%; margin-top: 10px;"
              >
                <el-table-column prop="productName" label="产品名称" min-width="120"></el-table-column>
                <el-table-column prop="specification" label="规格型号" width="150">
                  <template slot-scope="scope">
                    <el-tooltip
                      v-if="scope.row.specification"
                      :content="scope.row.specification"
                      placement="top"
                      effect="dark"
                      popper-class="spec-tooltip"
                    >
                      <span>{{ truncateText(scope.row.specification, 20) }}</span>
                    </el-tooltip>
                    <span v-else>-</span>
                  </template>
                </el-table-column>
                <el-table-column prop="quantity" label="数量" width="80" align="center"></el-table-column>
                <el-table-column prop="unit" label="单位" width="60" align="center"></el-table-column>
                <el-table-column label="单价" width="100" align="center">
                  <template slot-scope="scope">
                    {{ formatAmount(scope.row.price) }}
                  </template>
                </el-table-column>
                <el-table-column label="总价" width="100" align="center">
                  <template slot-scope="scope">
                    {{ formatAmount(scope.row.totalPrice) }}
                  </template>
                </el-table-column>
          </el-table>
          
          <!-- 多供应商分组预览 -->
          <div v-if="hasMultipleSuppliers && validationResult.isValid" class="supplier-groups-preview">
            <h4>供应商分组预览</h4>
            <div class="supplier-groups">
              <div v-for="group in supplierGroups" :key="group.supplierId" class="supplier-group">
                <div class="group-header">
                  <span class="supplier-name">{{ group.supplierName }}</span>
                  <span class="product-count">（{{ group.products.length }} 个产品）</span>
                </div>
                <div class="group-products">
                  <div v-for="product in group.products" :key="product.productName" class="product-item">
                    <span class="product-name">{{ product.productName }}</span>
                    <span class="product-spec">{{ product.specification }}</span>
                    <span class="product-quantity">{{ product.forwardQuantity }} {{ product.unit }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
          </div>
        </div>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleForwardDialogClose">取 消</el-button>
        <el-button 
          type="primary" 
          @click="submitForwardOrder" 
          :loading="forwardSubmitLoading"
          :disabled="!validationResult.isValid"
        >
          确认转发
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { query_purchase_orders, query_sales_orders, create_order, delete_order, get_order_detail, update_order_status } from '@/api/modules/orders.js';
import { 
  query_purchase_contracts_all,
  query_customer_contracts_pagination,
  getContractById
} from '@/api/modules/contracts.js';
import { getAllSuppliersByUnitId } from '@/api/modules/unit.js';
import { getProjectById } from '@/api/modules/projects.js';
import { query_user_by_id } from '@/api/modules/user.js';
import { getUsersByUnitId, getUsersByProjectIds } from '@/api/modules/users.js';
import { getUserInfo } from '@/utils/auth.js';
import { maskName as maskNameUtil } from '@/utils/mask.js';

export default {
  name: 'SupplierOrdersList',
  filters: {
    // 日期格式化过滤器
    formatDate(dateString) {
      if (!dateString) return '';
      
      try {
        const date = new Date(dateString);
        // 检查日期是否有效
        if (isNaN(date.getTime())) return dateString;
        
        // 格式化为 YYYY-MM-DD
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
      } catch (error) {
        console.error('日期格式化错误:', error);
        return dateString;
      }
    },
    // 货币格式化过滤器
      formatCurrency(value) {
        if (!value && value !== 0) return '0.00';
        
        try {
          const num = parseFloat(value);
          if (isNaN(num)) return '0.00';
          
          return num.toFixed(2);
        } catch (error) {
          console.error('货币格式化错误:', error);
          return '0.00';
        }
      },
      // 状态格式化过滤器
      formatStatus(status) {
        const statusMap = {
          'ACTIVE': '待确认',
          'EXECUTING': '进行中',
          'SENDING': '已发货',
          'RECEIVING': '已收货',
          'END': '已结束'
        };
        return statusMap[status] || status;
      }
  },
  data() {
    return {
      searchParams: {
        orderId: '', // 订单编号
        orderTitle: '', // 订单标题，与API对应
        status: '',
        orderType: '', // 订单类型
      },
      statusOptions: [
        { value: 'ACTIVE', label: '待确认' },
        { value: 'EXECUTING', label: '进行中' },
        { value: 'SENDING', label: '已发货' },
        { value: 'RECEIVING', label: '已收货' },
        { value: 'END', label: '已结束' }
      ],
      orderTypeOptions: [
        { value: '01', label: '正常订单' },
        { value: '02', label: '退货订单' }
      ],
      loading: false,
      orderList: [], // 主订单列表
      tableData: [], // 表格显示数据（包含主订单和展开的子订单）
      pagination: {
        page: 1, // 修改为与API参数对应
        pageSize: 10,
        total: 0
      },
      
      // 创建订单相关
      createOrderDialogVisible: false,
      createMode: 'supplier', // 创建模式：supplier（通过供应商）或 contract（通过合同）
      contractOptions: [],
      supplierOptions: [], // 供应商选项
      supplierContractOptions: [], // 选中供应商的合同选项
      materialsLoading: false,
      contractItemFilterKeyword: '', // 合同清单筛选关键词
      submitLoading: false,
      currentUnitId: null,
      currentUserId: null,
      // 选中的合同信息显示
      selectedContractName: '',
      selectedContractNumber: '',
      orderForm: {
        order_type: '直采', // 默认固定为直采
        contract_id: '', // 编辑模式使用
        contract_ids: [], // 多选合同ID数组
        selected_supplier_id: '', // 选中的供应商ID
        contract_type: '', // 合同类型，从选择的合同中获取
        supplier_id: '',
        supplier_info: null,
        supplier_name: '',
        supplier_short_name: '', // 供应商简称
        customer_id: '',
        customer_name: '', // 客户名称
        create_id: '',
        order_title: '',
        contact_person: '',
        contact_phone: '',
        contact_address: '',
        // 合同单位联系信息
        contract_unit_contact_person: '',
        contract_unit_phone: '',
        // 供应商联系信息
        supplier_contact_person: '',
        supplier_phone: '',
        status: '待接受',
        order_items: [],
        // 地址选择相关
        address_source: 'unit', // 'unit' 或 'project'，默认使用单位地址
        selected_project_id: null, // 选中的项目地址对应的项目ID
        available_addresses: {
          unit_address: '',
          project_addresses: []
        }
      },
      orderFormRules: {
        contract_id: [
          { required: true, message: '请选择合同', trigger: 'change' }
        ],
        contract_ids: [
          { required: true, message: '请选择合同', trigger: 'change' }
        ],
        selected_supplier_id: [
          { required: true, message: '请选择供应商', trigger: 'change' }
        ],
        order_title: [
          { required: true, message: '订单标题不能为空', trigger: 'blur' }
        ],
        contact_person: [
          { required: true, message: '请输入联系人', trigger: 'blur' }
        ],
        contact_phone: [
          { required: true, message: '请输入联系电话', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ],
        contact_address: [
          { required: true, message: '请输入收货地址', trigger: 'blur' }
        ],
        contract_unit_contact_person: [
          { required: true, message: '请选择联系人', trigger: 'change' }
        ],
        contract_unit_phone: [
          { required: true, message: '联系电话不能为空', trigger: 'blur' }
        ]
      },
      totalQuantity: 0,
      totalPrice: 0,
      isEditMode: false,
      currentOrderData: null, // 存储当前编辑的订单数据
      
      // 转发订单相关
      forwardOrderDialogVisible: false,
      forwardForm: {
        contractIds: [], // 多选合同ID数组
        orderId: '',
        orderTitle: '',
        deliveryAddress: '',
        demandContactPerson: '',
        demandContactPhone: '',
        supplierContactPerson: '', // 供应商联系人
        supplierContactPhone: '' // 供应商电话
      },
      forwardFormRules: {
        contractIds: [
          { required: true, message: '请选择执行中的采购合同', trigger: 'change' }
        ],
        orderId: [
          { required: true, message: '请选择要转发的销售订单', trigger: 'change' }
        ],
        orderTitle: [
          { required: true, message: '请输入订单标题', trigger: 'blur' }
        ],
        deliveryAddress: [
          { required: true, message: '请输入收货地址', trigger: 'blur' }
        ],
        demandContactPerson: [
          { required: true, message: '请输入联系人', trigger: 'blur' }
        ],
        demandContactPhone: [
          { required: true, message: '请输入联系电话', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ]
      },
      executingContracts: [], // 执行中的采购合同列表
      salesOrders: [], // 销售订单列表
      salesOrdersLoading: false,
      forwardSubmitLoading: false,
      selectedContracts: [], // 选中的多个合同详情
      selectedOrder: null, // 选中的订单详情
      orderSearchKeyword: '', // 订单搜索关键词
      productComparison: [], // 产品对比数据
      validationResult: {
        isValid: false,
        message: ''
      },
      // 多需求方相关
      availableCustomers: [], // 可用需求方列表
      hasMultipleCustomers: false, // 是否有多个需求方
      groupedOrders: [], // 按需求方分组的订单
      
      // 多供应商相关
      supplierGroups: [], // 按供应商分组的订单
      hasMultipleSuppliers: false, // 是否有多个供应商
      // 用户角色相关
      currentUserRoleId: null, // 当前用户角色ID
      currentUserProjectIds: [], // 当前用户关联的项目ID数组
      currentProjectInfo: null, // 当前项目信息（用于项目用户）
      // 同单位联系人相关
      unitContacts: [], // 同单位联系人列表（包含当前用户）
      selectedContactId: null, // 选中的联系人ID（普通订单创建）
      forwardSelectedContactId: null, // 选中的联系人ID（转发订单）
      // 供应商单位联系人相关
      supplierUnitContacts: [], // 供应商单位联系人列表（创建订单用）
      selectedSupplierContactId: null, // 选中的供应商联系人ID（创建订单用）
      forwardSupplierUnitContacts: [], // 供应商单位联系人列表（转发订单用）
      forwardSelectedSupplierContactId: null // 选中的供应商联系人ID（转发订单用）
    };
  },
  computed: {
    // 当前用户是否为项目人员（关联了项目则只能选项目地址）
    isProjectPersonnel() {
      return this.currentUserProjectIds && this.currentUserProjectIds.length > 0;
    },

    // 创建订单弹窗内合同清单表格高度（约 2 屏，表体内部滚动）
    orderItemsTableMaxHeight() {
      const screenHeight = window.innerHeight;
      return Math.max(600, screenHeight * 2 - 300);
    },

    // 合同清单筛选结果（按名称、规格、材质、单位、备注匹配）
    filteredOrderItems() {
      const keyword = (this.contractItemFilterKeyword || '').trim().toLowerCase();
      if (!keyword) {
        return this.orderForm.order_items;
      }
      return this.orderForm.order_items.filter(item => {
        const fields = [
          item.productName,
          item.specification,
          item.materialRequirement,
          item.unit,
          item.remark
        ];
        return fields.some(field => {
          if (field == null || field === '') {
            return false;
          }
          return String(field).toLowerCase().includes(keyword);
        });
      });
    },

    // 显示合同单位名称（多个合同用逗号隔开）
    displayContractUnits() {
      // 编辑模式：优先从订单数据中获取需求单位信息
      if (this.isEditMode && this.currentOrderData) {
        // 优先使用demandUnitNames（如果存在）
        if (this.currentOrderData.demandUnitNames && this.currentOrderData.demandUnitNames.length > 0) {
          return this.currentOrderData.demandUnitNames.join('，');
        }
        
        // 从contractDetails中获取需求单位信息
        if (this.currentOrderData.contractDetails && this.currentOrderData.contractDetails.length > 0) {
          const allUnits = [];
          this.currentOrderData.contractDetails.forEach(contract => {
            if (contract.demandUnits && contract.demandUnits.length > 0) {
              allUnits.push(...contract.demandUnits.map(unit => unit.unitName));
            }
          });
          
          // 去重
          const uniqueUnits = [...new Set(allUnits)];
          return uniqueUnits.length > 0 ? uniqueUnits.join('，') : '未知单位';
        }
        
        // 如果都没有，使用creatorUnitName作为备选
        if (this.currentOrderData.creatorUnitName) {
          return this.currentOrderData.creatorUnitName;
        }
      }
      
      if (this.isEditMode && this.orderForm.contract_id) {
        const contract = this.contractOptions.find(item => item.id === this.orderForm.contract_id);
        // 从demandUnits中获取需求单位名称（不是contractUnit）
        if (contract && contract.demandUnits && contract.demandUnits.length > 0) {
          return contract.demandUnits.map(unit => unit.unitName).join('，');
        }
        return '未知单位';
      } else if (this.orderForm.contract_ids && this.orderForm.contract_ids.length > 0) {
        const contracts = this.createMode === 'supplier' ? 
          this.supplierContractOptions : 
          this.contractOptions;
        const selectedContracts = contracts.filter(contract => 
          this.orderForm.contract_ids.includes(contract.id)
        );
        
        // 从demandUnits中获取需求单位名称（不是contractUnit）
        const allUnits = [];
        selectedContracts.forEach(contract => {
          if (contract.demandUnits && contract.demandUnits.length > 0) {
            allUnits.push(...contract.demandUnits.map(unit => unit.unitName));
          }
        });
        
        // 去重
        const uniqueUnits = [...new Set(allUnits)];
        return uniqueUnits.length > 0 ? uniqueUnits.join('，') : '未知单位';
      }
      return '未选择';
    },
    
    // 显示合同名称（多个合同用逗号隔开）
    displayContractNames() {
      // 编辑模式：优先从订单数据中获取合同信息
      if (this.isEditMode && this.currentOrderData && this.currentOrderData.contractDetails && this.currentOrderData.contractDetails.length > 0) {
        return this.currentOrderData.contractDetails.map(contract => contract.contractName).join('，');
      }
      
      if (this.orderForm.contract_ids && this.orderForm.contract_ids.length > 0) {
        // 优先使用contract_ids（支持多合同）
        const contracts = this.createMode === 'supplier' ? 
          this.supplierContractOptions : 
          this.contractOptions;
        const selectedContracts = contracts.filter(contract => 
          this.orderForm.contract_ids.includes(contract.id)
        );
        return selectedContracts.map(contract => contract.contractName).join('，');
      } else if (this.isEditMode && this.orderForm.contract_id) {
        // 编辑模式：从单个合同获取（向后兼容）
        const contract = this.contractOptions.find(item => item.id === this.orderForm.contract_id);
        return contract ? contract.contractName : '未知合同';
      }
      return '未选择';
    },
    
    // 显示合同编号（多个合同用逗号隔开）
    displayContractNumbers() {
      // 编辑模式：优先从订单数据中获取合同信息
      if (this.isEditMode && this.currentOrderData && this.currentOrderData.contractDetails && this.currentOrderData.contractDetails.length > 0) {
        return this.currentOrderData.contractDetails.map(contract => contract.contractNumber || contract.id).join('，');
      }
      
      if (this.orderForm.contract_ids && this.orderForm.contract_ids.length > 0) {
        // 优先使用contract_ids（支持多合同）
        const contracts = this.createMode === 'supplier' ? 
          this.supplierContractOptions : 
          this.contractOptions;
        const selectedContracts = contracts.filter(contract => 
          this.orderForm.contract_ids.includes(contract.id)
        );
        return selectedContracts.map(contract => contract.contractNumber || contract.id).join('，');
      } else if (this.isEditMode && this.orderForm.contract_id) {
        // 编辑模式：从单个合同获取（向后兼容）
        const contract = this.contractOptions.find(item => item.id === this.orderForm.contract_id);
        return contract ? (contract.contractNumber || contract.id) : '未知';
      }
      return '未选择';
    },
    
    
    // 显示项目名称（项目管理员显示所属项目，单位用户显示全部）
    displayProjectNames() {
      if (this.isEditMode && this.currentOrderData?.secondaryOrgNames?.length > 0) {
        return this.currentOrderData.secondaryOrgNames.join('，');
      }

      if (this.isEditMode && this.currentOrderData?.contractDetails?.length > 0) {
        const projects = this.getDisplayProjectsFromContracts(this.currentOrderData.contractDetails);
        return projects.length > 0
          ? projects.map(project => project.projectName).join('，')
          : '未关联项目';
      }

      const projects = this.getDisplayProjects();
      if (projects.length > 0) {
        return projects.map(project => project.projectName).join('，');
      }

      if (this.isEditMode && this.orderForm.contract_id) {
        const contract = this.contractOptions.find(item => item.id === this.orderForm.contract_id);
        if (contract) {
          const contractProjects = this.getDisplayProjectsFromContracts([contract]);
          return contractProjects.length > 0
            ? contractProjects.map(project => project.projectName).join('，')
            : '未关联项目';
        }
      }

      return '未关联项目';
    },
    
    // 供应商联系人
    supplierContactPerson() {
      if (this.orderForm.supplier_info && this.orderForm.supplier_info.contact_person) {
        return this.orderForm.supplier_info.contact_person;
      }
      return '';
    },
    
    // 供应商联系电话
    supplierContactPhone() {
      if (this.orderForm.supplier_info && this.orderForm.supplier_info.contact_phone) {
        return this.orderForm.supplier_info.contact_phone;
      }
      return '';
    },
    
    // 判断是否有变动价合同
    hasFloatingPriceContract() {
      // 从选中的合同中获取pricingMethod判断
      if (this.orderForm.contract_ids && this.orderForm.contract_ids.length > 0) {
        // 根据创建模式选择合同列表
        const contracts = this.createMode === 'supplier' 
          ? this.supplierContractOptions 
          : this.contractOptions;
        
        // 查找选中的合同（考虑ID类型转换）
        const selectedContracts = contracts.filter(contract => {
          const contractId = typeof contract.id === 'string' ? parseInt(contract.id) : contract.id;
          return this.orderForm.contract_ids.some(id => {
            const selectedId = typeof id === 'string' ? parseInt(id) : id;
            return contractId === selectedId;
          });
        });
        
        // 检查是否有变动价合同（从合同本身的pricingMethod判断）
        if (selectedContracts.length > 0) {
          return selectedContracts.some(contract => 
            contract.pricingMethod === 'FLOATING'
          );
        }
      }
      
      // 如果合同选项中没有找到，则从订单项中检查（作为备选）
      if (this.orderForm.order_items && this.orderForm.order_items.length > 0) {
        return this.orderForm.order_items.some(item => 
          item.pricingMethod === 'FLOATING'
        );
      }
      
      return false;
    },
    
    // 过滤后的销售订单列表（用于转发订单选择）
    filteredSalesOrders() {
      if (!this.orderSearchKeyword) {
        return this.salesOrders;
      }
      const keyword = this.orderSearchKeyword.toLowerCase();
      return this.salesOrders.filter(order => {
        const orderId = (order.orderId || order.id || '').toLowerCase();
        const orderTitle = (order.orderTitle || '').toLowerCase();
        return orderId.includes(keyword) || orderTitle.includes(keyword);
      });
    },
    
    // 当前选中的订单显示信息
    selectedOrderForDisplay() {
      if (!this.forwardForm.orderId) {
        return null;
      }
      return this.salesOrders.find(order => order.id === this.forwardForm.orderId) || null;
    }
  },
  created() {
    // 先获取用户信息，再获取订单列表
    this.getCurrentUnitId();
    // 不需要在此调用getOrderList，因为getCurrentUnitId中会调用
  },
  methods: {
    // 判断是否可以撤销订单（当前用户是该订单的供应商）
    canRevokeOrder(order) {
      const supplierId = order.supplierId || order.supplier_id || order.supplierUnitId;
      return supplierId === this.currentUnitId;
    },

    // 撤销订单（从进行中改回待确认）
    revokeOrder(orderId) {
      this.$confirm('确定要撤销该订单吗？撤销后订单状态将变为待确认。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        update_order_status(orderId, 'ACTIVE').then(() => {
          this.$message.success('订单已撤销');
          this.fetchOrders();
        }).catch(error => {
          console.error('撤销订单失败:', error);
          this.$message.error('撤销订单失败');
        });
      }).catch(() => {});
    },

    // 截断文本显示，默认为20个字符
    truncateText(text, length = 20) {
      if (!text) return '-';
      const str = String(text);
      return str.length > length ? `${str.slice(0, length)}...` : str;
    },

    // 格式化金额
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00';
      const num = parseFloat(amount);
      if (isNaN(num)) return '0.00';
      return num.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    
    // 格式化单价（保留8位小数，去除末尾无意义的0）
    formatUnitPrice(amount) {
      if (!amount && amount !== 0) return '0';
      const num = parseFloat(amount);
      if (isNaN(num)) return '0';
      // 先格式化为8位小数，然后去除末尾的0
      const formatted = num.toFixed(8);
      // 去除末尾的0和小数点（如果有）
      return formatted.replace(/\.?0+$/, '') || '0';
    },
    
    // 格式化数量（保留4位小数，去除末尾无意义的0）
    formatQuantity(quantity) {
      if (quantity === undefined || quantity === null) return '0';
      const num = parseFloat(quantity);
      if (isNaN(num)) return '0';
      // 先格式化为4位小数，然后去除末尾的0和小数点（如果有）
      const formatted = num.toFixed(4);
      return formatted.replace(/\.?0+$/, '') || '0';
    },

    // 格式化百分比
    formatPercentage(value) {
      if (value === null || value === undefined) return '0.00';
      return `${parseFloat(value).toFixed(2)}`;
    },
    
    // 获取默认到货日期（今天+7天）
    getDefaultDeliveryDate() {
      const date = new Date();
      date.setDate(date.getDate() + 7);
      return date.toISOString().split('T')[0]; // 格式化为YYYY-MM-DD
    },
    
    // 获取项目名称（用于转发订单显示二级机构）
    getProjectNames(contract) {
      if (!contract) return '未关联项目';
      
      if (contract.projects && contract.projects.length > 0) {
        return contract.projects.map(project => project.projectName).join('，');
      }
      
      return '未关联项目';
    },
    
    // 获取当前单位名称
    getCurrentUnitName() {
      // 从store或用户信息中获取当前单位名称
      const store = this.$store || {};
      const state = store.state || {};
      let userInfo = null;
      
      if (state.user && state.user.userInfo) {
        userInfo = state.user.userInfo;
      } else if (state.userInfo) {
        userInfo = state.userInfo;
      } else if (state.auth && state.auth.userInfo) {
        userInfo = state.auth.userInfo;
      }
      
      if (!userInfo) {
        const localUserInfo = localStorage.getItem('userInfo');
        if (localUserInfo) {
          try {
            userInfo = JSON.parse(localUserInfo);
          } catch (e) {
            console.error('解析localStorage中的userInfo失败:', e);
          }
        }
      }
      
      return userInfo?.unitName || userInfo?.unit_name || '当前单位';
    },
    
    // 获取原订单的项目名称（转发订单时保持原订单的二级机构）
    getOriginalProjectNames() {
      if (!this.selectedOrder) return '未关联项目';
      
      // 优先从订单对象的secondaryOrgNames获取
      if (this.selectedOrder.secondaryOrgNames && this.selectedOrder.secondaryOrgNames.length > 0) {
        return this.selectedOrder.secondaryOrgNames.join('，');
      }
      
      // 其次从secondaryOrgDetails获取
      if (this.selectedOrder.secondaryOrgDetails && this.selectedOrder.secondaryOrgDetails.length > 0) {
        const projectNames = this.selectedOrder.secondaryOrgDetails
          .map(org => org.projectName)
          .filter(name => name);
        return projectNames.length > 0 ? projectNames.join('，') : '未关联项目';
      }
      
      // 最后从contractDetails中获取项目信息（作为备选）
      if (this.selectedOrder.contractDetails && this.selectedOrder.contractDetails.length > 0) {
        const allProjects = [];
        this.selectedOrder.contractDetails.forEach(contract => {
          if (contract.projects && contract.projects.length > 0) {
            allProjects.push(...contract.projects.map(project => project.projectName));
          }
        });
        const uniqueProjects = [...new Set(allProjects)];
        return uniqueProjects.length > 0 ? uniqueProjects.join('，') : '未关联项目';
      }
      
      return '未关联项目';
    },
    
    // 获取当前用户的单位ID
    getCurrentUnitId() {
      try {
        // 添加防御性检查，避免访问不存在的属性
        const store = this.$store || {};
        const state = store.state || {};
        
        // 打印store结构，帮助调试
// 尝试从不同可能的路径获取用户信息
        let userInfo = null;
        
        if (state.user && state.user.userInfo) {
          // 如果是 state.user.userInfo 的结构
          userInfo = state.user.userInfo;
} else if (state.userInfo) {
          // 如果是 state.userInfo 的结构
          userInfo = state.userInfo;
} else if (state.auth && state.auth.userInfo) {
          // 如果是 state.auth.userInfo 的结构
          userInfo = state.auth.userInfo;
}
        
        // 如果找不到，可以考虑从localStorage获取
        if (!userInfo || !userInfo.unitId) {
          userInfo = getUserInfo() || userInfo;
          if (!userInfo || !userInfo.unitId) {
            const localUserInfo = localStorage.getItem('userInfo');
            if (localUserInfo) {
              try {
                userInfo = JSON.parse(localUserInfo);
              } catch (e) {
                console.error('解析localStorage中的userInfo失败:', e);
              }
            }
          }
        }
        
// 保存用户信息到调试区域显示
        this.userUnitInfo = userInfo;
        
        if (userInfo && userInfo.unitId) {
          this.currentUnitId = userInfo.unitId;
} else {
          console.warn('未能获取unitId，将使用默认值1进行测试');
          // 使用默认值便于测试
          this.currentUnitId = 1;
        }
        
        // 同时获取当前用户ID
        if (userInfo && userInfo.id) {
          this.currentUserId = userInfo.id;
} else if (userInfo && userInfo.userId) {
          this.currentUserId = userInfo.userId;
} else {
          console.warn('未能获取用户ID，将使用默认值1进行测试');
          this.currentUserId = 1;
        }
        
        // 获取用户角色ID和项目信息
        if (userInfo) {
          // 从role对象中获取角色ID
          this.currentUserRoleId = userInfo.role?.id || userInfo.roleId || userInfo.role_id;
          this.currentUserProjectIds = this.normalizeUserProjectIds(userInfo);
          // 登录信息不含 projectIds 时需从接口拉取
          this.refreshCurrentUserProjectIds();
        }
      } catch (err) {
        console.error('获取用户unitId出错:', err);
        console.warn('发生错误，将使用默认值1进行测试');
        // 出错时使用默认值便于测试
        this.currentUnitId = 1;
      }
      
      // 获取完单位ID后加载订单列表
      this.getOrderList();
    },
    
    // 从多来源解析当前登录用户信息
    resolveCurrentUserInfo() {
      const store = this.$store || {};
      const state = store.state || {};
      let userInfo = null;

      if (state.user && state.user.userInfo) {
        userInfo = state.user.userInfo;
      } else if (state.userInfo) {
        userInfo = state.userInfo;
      } else if (state.auth && state.auth.userInfo) {
        userInfo = state.auth.userInfo;
      }

      if (!userInfo) {
        userInfo = getUserInfo();
      }

      if (!userInfo) {
        const localUserInfo = localStorage.getItem('userInfo') || localStorage.getItem('user_info');
        if (localUserInfo) {
          try {
            userInfo = JSON.parse(localUserInfo);
          } catch (e) {
            console.error('解析localStorage中的userInfo失败:', e);
          }
        }
      }

      return userInfo;
    },

    // 规范化用户关联的项目 ID 列表
    normalizeUserProjectIds(userInfo) {
      if (!userInfo) {
        return [];
      }

      let ids = userInfo.projectIds ?? userInfo.project_ids;

      if (typeof ids === 'string') {
        const trimmed = ids.trim();
        if (trimmed.startsWith('[')) {
          try {
            ids = JSON.parse(trimmed);
          } catch (e) {
            ids = trimmed.split(',').map(item => item.trim()).filter(Boolean);
          }
        } else if (trimmed) {
          ids = trimmed.split(',').map(item => item.trim()).filter(Boolean);
        } else {
          ids = [];
        }
      }

      if (!Array.isArray(ids)) {
        const singleId = userInfo.projectId ?? userInfo.project_id;
        ids = singleId != null && singleId !== '' ? [singleId] : [];
      }

      return ids
        .map(id => Number(id))
        .filter(id => !Number.isNaN(id));
    },

    // 从接口刷新当前用户关联的项目 ID
    async refreshCurrentUserProjectIds(options = {}) {
      const { fillOrderInfo = false } = options;
      const userInfo = this.resolveCurrentUserInfo();

      if (userInfo) {
        const cachedIds = this.normalizeUserProjectIds(userInfo);
        if (cachedIds.length > 0) {
          this.currentUserProjectIds = cachedIds;
        }
      }

      if (!this.currentUserId) {
        return this.currentUserProjectIds;
      }

      try {
        const response = await query_user_by_id(this.currentUserId);
        if (response.data && response.data.success) {
          const userDetail = response.data.data;
          this.currentUserProjectIds = this.normalizeUserProjectIds(userDetail);

          if (fillOrderInfo && this.currentUserProjectIds.length > 0) {
            this.fetchProjectInfoForOrder(this.currentUserProjectIds[0]);
          } else if (this.currentUserProjectIds.length > 0 && !this.currentProjectInfo) {
            this.fetchProjectInfo(this.currentUserProjectIds[0]);
          }

          this.reapplyAddressOptionsIfNeeded();
        }
      } catch (error) {
        console.error('获取用户项目ID失败:', error);
      }

      return this.currentUserProjectIds;
    },

    // 项目 ID 加载完成后，刷新创建订单弹窗中的可选地址
    reapplyAddressOptionsIfNeeded() {
      if (!this.createOrderDialogVisible || !this.isProjectPersonnel) {
        return;
      }

      const contracts = this.getSelectedContracts();
      if (contracts.length > 0) {
        this.collectAvailableAddresses(contracts[0]);
      }
    },

    // 获取项目用户信息（重新查询用户详细信息）
    fetchProjectUserInfo() {
      this.refreshCurrentUserProjectIds();
    },
    
    // 为订单创建查询项目用户信息
    fetchProjectUserInfoForOrder() {
      this.refreshCurrentUserProjectIds({ fillOrderInfo: true });
    },
    
    // 获取项目信息
    fetchProjectInfo(projectId) {
      if (!projectId) {
        console.warn('项目ID不存在，无法获取项目信息');
        return;
      }
      
      getProjectById(projectId)
        .then(response => {
if (response.data && response.data.success) {
            this.currentProjectInfo = response.data.data;
} else {
            console.error('获取项目信息失败:', response.data?.message);
          }
        })
        .catch(error => {
          console.error('获取项目信息失败:', error);
        });
    },
    
    // 为订单创建获取项目信息并自动填充
    fetchProjectInfoForOrder(projectId) {
      if (!projectId) {
        console.warn('项目ID不存在，无法获取项目信息');
        return;
      }
      
      getProjectById(projectId)
        .then(response => {
if (response.data && response.data.success) {
            this.currentProjectInfo = response.data.data;
// 自动填充订单信息
            this.autoFillProjectUserInfo();
          } else {
            console.error('获取项目信息失败:', response.data?.message);
          }
        })
        .catch(error => {
          console.error('获取项目信息失败:', error);
        });
    },
    
    // 获取订单列表
    getOrderList() {
      this.loading = true;
      
      // 构建查询参数（新的采购订单API格式）
      const params = {
        orderId: this.searchParams.orderId || undefined,
        orderTitle: this.searchParams.orderTitle || undefined, // 使用订单标题搜索
        status: this.searchParams.status || undefined,
        orderType: this.searchParams.orderType || undefined, // 订单类型过滤
        page: this.pagination.page - 1, // 后端页码从0开始
        size: this.pagination.pageSize
      };
      
      // 移除undefined的值
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });
      
      // 详细调试输出
// 调用API获取订单数据
      query_purchase_orders(params)
        .then(response => {
if (response.data && response.data.success) {
            // 解析新API返回的数据结构
            const responseData = response.data.data;
            
            // 新API返回格式：{ content: [], totalElements: number, totalPages: number, ... }
            let orderList = [];
            let total = 0;
            
            if (responseData.content && Array.isArray(responseData.content)) {
              orderList = responseData.content;
              total = responseData.totalElements || 0;
} else if (responseData.data && Array.isArray(responseData.data)) {
              orderList = responseData.data;
              total = responseData.total || responseData.count || 0;
} else if (responseData.records && Array.isArray(responseData.records)) {
              orderList = responseData.records;
              total = responseData.total || responseData.count || 0;
} else if (Array.isArray(responseData)) {
              orderList = responseData;
              total = responseData.length;
} else {
              console.warn('未找到有效的订单数据数组');
            }
            
            // 为每个订单添加默认的orderType（如果不存在）
            const processedOrderList = orderList.map(order => ({
              ...order,
              orderType: order.orderType || '01' // 默认为正常订单
            }));
            
            // 设置订单列表
            this.orderList = processedOrderList;
            this.tableData = processedOrderList; // 同时设置表格数据
            
            // 设置总记录数
            this.pagination.total = total;
            
} else {
            this.$message.error(response.data?.message || '获取订单列表失败');
            console.error('获取订单列表失败:', response.data?.message);
            this.orderList = [];
            this.tableData = [];
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('==========请求错误==========');
          console.error('获取订单列表失败:', error);
          console.error('请求配置:', error.config);
          console.error('响应状态:', error.response?.status);
          console.error('响应数据:', error.response?.data);
          this.$message.error('获取订单列表失败: ' + (error.message || '未知错误'));
          this.orderList = [];
          this.tableData = [];
          this.loading = false;
        });
    },
    
    // 搜索
    handleSearch() {
      // 重置页码
      this.pagination.page = 1;
      // 重新获取订单列表
      this.getOrderList();
    },
    
    // 页码改变
    handleCurrentChange(currentPage) {
      this.pagination.page = currentPage;
      this.getOrderList();
    },
    
    // 每页条数改变
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.page = 1;
      this.getOrderList();
    },
    
    // 查看订单详情
    viewOrder(id) {
      this.$router.push({
        path: `/orders/supplier-order-detail/${id}`
      });
    },
    
    // 退货
    returnOrder(id) {
      this.$router.push({
        path: `/orders/return/${id}`
      });
    },
    
    // 删除订单
    deleteOrder(id) {
      this.$confirm('确认删除该订单?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.loading = true;
        // 调用删除订单API
        delete_order(id)
          .then(response => {
            if (response.data && response.data.success) {
              this.$message.success('订单已删除');
              this.getOrderList(); // 刷新列表
            } else {
              this.$message.error(response.data?.message || '删除订单失败');
          }
          this.loading = false;
          })
          .catch(error => {
            console.error('删除订单失败:', error);
            this.$message.error('删除订单失败: ' + (error.message || '未知错误'));
            this.loading = false;
          });
      })
    },
    
    
    // 判断订单是否为当前用户创建
    isCurrentUserOrder(order) {
      // 调试信息
// 检查订单的创建者ID是否与当前用户ID匹配
      const isMatch = order.create_id === this.currentUserId || 
                     order.creator_id === this.currentUserId ||
                     order.createId === this.currentUserId ||
                     order.creatorId === this.currentUserId;
      
return isMatch;
    },
    
    // 编辑订单
    async editOrder(id) {
      try {
        // 获取订单详情
        const response = await get_order_detail(id);
        if (response.data && response.data.success) {
          const orderData = response.data.data;
          
// 保存当前订单数据，用于显示合同信息
          this.currentOrderData = orderData;
          
          // 先确定订单类型，用于加载正确的合同选项
          let orderType = '直采'; // 默认直采
          if (orderData.contractType === 'CUSTOMER') {
            orderType = '集采';
          }
          
          // 先设置订单类型，这样loadContractOptions能加载正确的合同
          this.orderForm = {
            ...this.orderForm, // 保留现有表单数据
            order_type: orderType
          };
          
          // 先加载合同选项
          await this.loadContractOptions();
          
          // 处理合同信息 - 支持多合同
          let contractId = '';
          let contractIds = [];
          
          if (orderData.contractDetails && orderData.contractDetails.length > 0) {
            // 新格式：多合同订单
            contractIds = orderData.contractDetails.map(contract => contract.id);
            contractId = contractIds[0]; // 使用第一个合同ID作为主合同ID
} else {
            // 旧格式：单合同订单
            contractId = orderData.contractId || '';
            contractIds = contractId ? [contractId] : [];
          }
          
          // 再设置完整的表单数据
          this.orderForm = {
            id: orderData.id, // 添加订单ID用于更新
            createTime: orderData.createTime || orderData.create_time, // 保留订单创建时间
            orderId: orderData.orderId || orderData.id, // 保留订单ID
            order_type: orderType,
            contract_id: contractId,
            contract_ids: contractIds, // 支持多合同
            contract_type: orderData.contractType || '',
            supplier_id: orderData.supplierId || '',
            supplier_info: null, // 需要从其他地方获取
            supplier_name: orderData.supplierName || '',
            customer_id: orderData.customerId || '',
            create_id: orderData.createId || this.currentUnitId,
            order_title: orderData.orderTitle || '', // 实际返回的是orderTitle
            contact_person: orderData.contactPerson || '',
            contact_phone: orderData.contactPhone || '',
            contact_address: orderData.contactAddress || '',
            // 新增的联系信息字段
            contract_unit_contact_person: orderData.demandContactPerson || '',
            contract_unit_phone: orderData.demandContactPhone || '',
            // 供应商联系信息应该从订单的供应商信息中获取，而不是从联系人字段
            supplier_contact_person: orderData.supplierContactPerson || '',
            supplier_phone: orderData.supplierContactPhone || '',
            status: orderData.status || '待接受',
            // 地址选择相关
            address_source: 'unit',
            selected_project_id: null,
            available_addresses: {
              unit_address: '',
              project_addresses: []
            },
            order_items: (orderData.orderItems || [])
              .map(item => ({
                // 根据实际返回数据映射订单项字段 - 使用新的字段名
                id: item.id,
                createTime: item.createTime || item.create_time, // 保留创建时间
                orderId: item.orderId || orderData.id, // 保留订单ID
                sequenceNumber: item.sequenceNumber || 1, // 序号
                category: item.category || '',
                productName: item.materialsName || item.productName || '', // 材料名称
                specification: item.materialsSpec || item.specification || '', // 规格型号
                materialRequirement: item.materialRequirement || '', // 材质要求
                unit: item.unit || '',
                quantity: item.quantity || 0,
                pricingMethod: item.pricingMethod || 'FIXED', // 定价方式
                taxExclusiveUnitPrice: item.beforePrice || item.taxExclusiveUnitPrice || 0, // 不含税单价
                taxInclusiveUnitPrice: item.price || item.taxInclusiveUnitPrice || 0, // 含税单价
                taxInclusiveBasePrice: item.taxInclusiveBasePrice || null, // 含税固定价
                taxInclusiveFloatingPrice: item.taxInclusiveFloatingPrice || null, // 含税变动价
                taxRate: item.taxRate || 0, // 税率
                taxInclusiveTotalAmount: item.totalAmount || (item.price || 0) * (item.quantity || 0), // 含税合价
                deliveryDate: item.deliveryDate || this.getDefaultDeliveryDate(), // 到货日期
                remark: item.remark || '', // 备注
                supplier_name: item.supplierName || '', // 供应商名称
                productSupplierId: item.productSupplierId || orderData.supplierId, // 产品供应商ID
                forwardedOrderId: item.forwardedOrderId || null // 转发订单ID
              }))
          };
          
// 如果有合同信息，设置合同类型
          if (orderData.CustomerContract) {
            this.orderForm.contract_type = '客户合同';
          }
          
          // 保存已存在的订单项数据（用于合并）
          const existingOrderItems = this.orderForm.order_items || [];
          
          // 编辑模式：先清空订单项，然后加载合同的所有产品并合并已存在的订单项数据
          this.orderForm.order_items = [];
          
          // 加载合同的所有产品并合并已存在的订单项数据
          if (contractIds.length > 0) {
            await this.loadAndMergeContractItemsForEdit(contractIds, existingOrderItems);
          }
          
          // 计算总数量和总价
          this.calculateTotals();
          
          // 收集可用地址信息（编辑模式下）
          if (this.currentOrderData && this.currentOrderData.contractDetails && this.currentOrderData.contractDetails.length > 0) {
            this.collectAvailableAddresses(this.currentOrderData.contractDetails[0]);
            this.restoreAddressSelectionFromOrder(orderData);
          }
          
          // 加载需求方联系人列表（合同关联项目时只显示项目内用户）
          const firstContract = this.currentOrderData?.contractDetails?.[0];
          await this.loadUnitContactsForEdit(firstContract);
          
          // 加载供应商单位联系人列表（编辑模式下也允许选择其他供应商联系人）
          if (this.orderForm.supplier_id) {
            await this.loadSupplierUnitContacts(this.orderForm.supplier_id);
          }
          
          // 显示编辑对话框
          this.createOrderDialogVisible = true;
          this.isEditMode = true; // 标记为编辑模式
        } else {
          this.$message.error('获取订单详情失败');
        }
      } catch (error) {
        console.error('获取订单详情失败:', error);
        this.$message.error('获取订单详情失败');
      }
    },
    
    // 获取状态标签类型
    getStatusType(status) {
      switch (status) {
        case 'ACTIVE':
          return 'warning';
        case 'EXECUTING':
          return 'success';
        case 'SENDING':
          return 'primary';
        case 'RECEIVING':
          return 'info';
        case 'END':
          return 'success';
        default:
          return 'info';
      }
    },
    
    // 获取订单类型标签类型
    getOrderTypeTagType(orderType) {
      switch (orderType) {
        case '02':
          return 'danger';
        case '01':
        default:
          return 'success';
      }
    },
    
    // 获取订单类型显示文本
    getOrderTypeText(orderType) {
      switch (orderType) {
        case '02':
          return '退货';
        case '01':
        default:
          return '正常';
      }
    },
    
    // 显示创建订单对话框
    showCreateOrderDialog() {
      // 重置表单并显示创建订单对话框
      this.resetOrderForm();
      this.isEditMode = false; // 标记为创建模式
      this.createOrderDialogVisible = true;

      // 确保已加载用户关联的项目 ID（登录信息中通常不含 projectIds）
      this.refreshCurrentUserProjectIds({ fillOrderInfo: Number(this.currentUserRoleId) === 3 });
      
      // 加载当前用户信息并填充默认联系人
      this.loadCurrentUserContact();
      
      // 加载同单位联系人列表
      this.loadUnitContacts();
      
      // 根据创建模式加载相应的选项
      this.loadCreateOptions();
    },
    
    // 为项目用户自动填充信息
    autoFillProjectUserInfo() {
      if (!this.currentProjectInfo) {
        console.warn('项目信息不存在，无法自动填充');
        return;
      }
      
// 填充收货地址（使用项目地址）
      const address = this.buildProjectFullAddress(this.currentProjectInfo);
      this.orderForm.contact_address = address;
      this.orderForm.address_source = 'project';
      this.orderForm.selected_project_id = this.currentProjectInfo.id || null;
      
      // 填充联系人（使用材料管理员姓名）
      this.orderForm.contract_unit_contact_person = this.currentProjectInfo.materialManagerName || '材料管理员';
      
      // 填充联系电话（使用材料管理员联系电话）
      this.orderForm.contract_unit_phone = this.currentProjectInfo.materialManagerContact || '13800138000';
      
},
    
    // 加载创建选项（根据创建模式）
    loadCreateOptions() {
      if (this.createMode === 'supplier') {
        this.loadSupplierOptions();
      } else {
        this.loadContractOptions();
      }
    },
    
    // 加载供应商选项
    loadSupplierOptions() {
      // 使用API获取当前用户的供应商列表
      getAllSuppliersByUnitId(this.currentUnitId)
        .then(response => {
          if (response.data && response.data.success) {
            this.supplierOptions = response.data.data || [];
            if (this.supplierOptions.length === 0) {
              this.$message.warning('未找到供应商，请先添加供应商关系');
            }
          } else {
            this.$message.error(response.data?.message || '获取供应商列表失败');
          }
        })
        .catch(error => {
          console.error('获取供应商列表失败:', error);
          this.$message.error('获取供应商列表失败，请稍后重试');
        });
    },
    
    // 处理创建模式切换
    handleCreateModeChange(mode) {
      this.createMode = mode;
      // 清空相关数据
      this.orderForm.contract_ids = [];
      this.orderForm.selected_supplier_id = '';
      this.orderForm.contract_id = '';
      this.supplierContractOptions = [];
      this.orderForm.order_items = [];
      this.totalQuantity = 0;
      this.totalPrice = 0;
      // 重新加载选项
      this.loadCreateOptions();
    },
    
    // 处理供应商选择
    handleSupplierChange(supplierId) {
      if (!supplierId) {
        this.supplierContractOptions = [];
        this.orderForm.contract_ids = [];
        this.orderForm.order_items = [];
        this.totalQuantity = 0;
        this.totalPrice = 0;
        this.supplierUnitContacts = [];
        this.selectedSupplierContactId = null;
        return;
      }
      
      // 获取选中供应商的信息
      const selectedSupplier = this.supplierOptions.find(item => item.id === supplierId);
      if (selectedSupplier) {
        this.orderForm.supplier_id = supplierId;
        this.orderForm.supplier_name = selectedSupplier.unitName;
        this.orderForm.supplier_short_name = selectedSupplier.unitShortName || selectedSupplier.unitName;
        
        // 注意：不要将供应商信息填充到订单联系人字段
        // 订单联系人应该是需求方的联系人，不是供应商联系人
        
        // 加载供应商单位的联系人列表
        this.loadSupplierUnitContacts(supplierId);
      }
      
      // 加载该供应商的合同
      this.loadSupplierContracts(supplierId);
    },
    
    // 加载指定供应商的合同
    loadSupplierContracts(supplierId) {
      const params = {
        status: 'EXECUTING' // 只查询执行中的合同
      };
      
      query_purchase_contracts_all(this.currentUnitId, params)
        .then(response => {
          if (response.data && response.data.success) {
            let contractList = response.data.data || [];
            if (!Array.isArray(contractList)) {
              contractList = contractList ? [contractList] : [];
            }
            
            // 在客户端根据供应商ID过滤合同
            const filteredContracts = contractList.filter(contract => {
              // 获取合同的供应商ID
              let contractSupplierId = null;
              
              // 方式1：从supplierUnitId字段获取
              if (contract.supplierUnitId) {
                contractSupplierId = contract.supplierUnitId;
              }
              // 方式2：从supplierUnit对象获取
              else if (contract.supplierUnit && contract.supplierUnit.id) {
                contractSupplierId = contract.supplierUnit.id;
              }
              // 方式3：从contractParties中获取SUPPLIER类型的单位ID
              else if (contract.contractParties && contract.contractParties.length > 0) {
                const supplierParty = contract.contractParties.find(party => party.partyType === 'SUPPLIER');
                if (supplierParty) {
                  contractSupplierId = supplierParty.unitInfoId || (supplierParty.unitInfo && supplierParty.unitInfo.id);
                }
              }
              // 方式4：从supplier_id或supplierId字段获取
              else if (contract.supplier_id) {
                contractSupplierId = contract.supplier_id;
              }
              else if (contract.supplierId) {
                contractSupplierId = contract.supplierId;
              }
              
              // 比较供应商ID是否匹配
              return contractSupplierId !== null && (
                contractSupplierId === supplierId || 
                contractSupplierId == supplierId
              );
            });
            
            this.supplierContractOptions = filteredContracts;
            if (filteredContracts.length === 0) {
              this.$message.warning('该供应商没有执行中的合同');
            }
          } else {
            this.$message.error(response.data?.message || '获取供应商合同失败');
          }
        })
        .catch(error => {
          console.error('获取供应商合同失败:', error);
          this.$message.error('获取供应商合同失败，请稍后重试');
        });
    },
    
    // 处理多选合同变化
    async handleMultipleContractChange(contractIds) {
      if (!contractIds || contractIds.length === 0) {
        this.orderForm.order_items = [];
        this.totalQuantity = 0;
        this.totalPrice = 0;
        // 清空合同选择时，需求方联系人恢复为需求单位用户
        this.loadUnitContacts();
        
        // 编辑模式：清空当前订单数据中的合同信息
        if (this.isEditMode && this.currentOrderData) {
          this.currentOrderData.contractDetails = [];
        }
        return;
      }
      
      // 验证所选合同是否属于同一供应商
      this.validateContractSupplierConsistency(contractIds);
      
      // 验证所选合同的计价方式是否一致
      this.validateContractPricingMethodConsistency(contractIds);
      
      // 先加载用户关联项目，再收集地址（避免项目人员仍看到单位地址）
      await this.refreshCurrentUserProjectIds({ fillOrderInfo: Number(this.currentUserRoleId) === 3 });
      
      // 加载并合并合同清单
      this.loadAndMergeContractItems(contractIds);
    },
    
    // 验证合同供应商一致性
    validateContractSupplierConsistency(contractIds) {
      const contracts = this.createMode === 'supplier' ? 
        this.supplierContractOptions : 
        this.contractOptions;
      
      const selectedContracts = contracts.filter(contract => 
        contractIds.includes(contract.id)
      );
      
      // 编辑模式：如果contractOptions中没有找到合同，跳过验证
      if (selectedContracts.length === 0) {
        if (this.isEditMode) {
return true;
        } else {
          this.$message.error('未找到选中的合同信息');
          return false;
        }
      }
      
      // 检查所有合同的供应商是否一致
      const supplierIds = selectedContracts.map(contract => {
        // 从合同信息中提取供应商ID
        if (contract.contractParties) {
          const supplierParty = contract.contractParties.find(party => party.partyType === 'SUPPLIER');
          return supplierParty ? supplierParty.unitInfoId : null;
        }
        return contract.supplier_id || contract.supplierId;
      }).filter(id => id !== null);
      
      const uniqueSupplierIds = [...new Set(supplierIds)];
      
      if (uniqueSupplierIds.length > 1) {
        this.$message.error('所选合同必须属于同一供应商，请重新选择');
        this.orderForm.contract_ids = [];
        return false;
      }
      
      // 设置供应商信息
      if (uniqueSupplierIds.length === 1) {
        this.orderForm.supplier_id = uniqueSupplierIds[0];
        const firstContract = selectedContracts[0];
        if (firstContract.contractParties) {
          const supplierParty = firstContract.contractParties.find(party => party.partyType === 'SUPPLIER');
          if (supplierParty && supplierParty.unitInfo) {
            this.orderForm.supplier_name = supplierParty.unitInfo.unitName;
            this.orderForm.supplier_short_name = supplierParty.unitInfo.unitShortName || supplierParty.unitInfo.unitName;
            
            // 填充供应商联系信息
            this.orderForm.supplier_contact_person = supplierParty.unitInfo.contactPerson || '';
            this.orderForm.supplier_phone = supplierParty.unitInfo.phone || '';
            
            // 加载供应商单位的联系人列表
            this.loadSupplierUnitContacts(uniqueSupplierIds[0]);
            
            // 注意：不要将供应商信息填充到订单联系人字段
            // 订单联系人应该是需求方的联系人，不是供应商联系人
          }
        }
        
        // 填充合同单位联系信息（使用第一个合同的合同单位）
        // 如果合同有contactPerson和contactPhone字段，优先使用合同联系人信息
        // 即使之前已经填充了其他联系人信息或设置了selectedContactId，如果合同有联系人信息，也应该优先使用
        if (firstContract.contactPerson && firstContract.contactPhone) {
          // 优先使用合同的contactPerson和contactPhone字段
          this.orderForm.contract_unit_contact_person = firstContract.contactPerson;
          this.orderForm.contract_unit_phone = firstContract.contactPhone;
          
          // 将合同联系人添加到联系人列表中（如果不存在），并获取联系人ID
          const contactId = this.addContractContactToUnitContacts(
            firstContract.contactPerson,
            firstContract.contactPhone,
            firstContract.id
          );
          
          if (contactId) {
            this.selectedContactId = contactId;
}
          
} else if (!this.selectedContactId && !this.orderForm.contract_unit_contact_person && !this.orderForm.contract_unit_phone) {
          // 如果合同列表数据中没有contactPerson和contactPhone，且用户没有选择联系人且表单中联系人信息也为空，按照之前的逻辑处理
          // 但注意：这些信息可能不完整，会在loadAndMergeContractItems中重新填充
          if (firstContract.contractUnit) {
            this.orderForm.contract_unit_contact_person = firstContract.contractUnit.contactPerson || '';
            this.orderForm.contract_unit_phone = firstContract.contractUnit.phone || '';
          }
          
          // 优先从demandUnits中获取需求方联系信息
          if (firstContract.demandUnits && firstContract.demandUnits.length > 0) {
            const demandUnit = firstContract.demandUnits[0];
            this.orderForm.contract_unit_contact_person = demandUnit.contactPerson || '';
            this.orderForm.contract_unit_phone = demandUnit.phone || '';
          }
        }
        
        // 收集可用地址信息（单位用户可以选择地址来源）
        this.collectAvailableAddresses(firstContract);
        
        // 根据合同是否关联项目，加载对应的需求方联系人（项目内用户或需求单位用户）
        this.loadUnitContacts(firstContract);
        
        // 编辑模式：更新当前订单数据中的合同信息（从contractOptions获取）
        if (this.isEditMode && this.currentOrderData) {
          this.currentOrderData.contractDetails = selectedContracts.map(contract => ({
            id: contract.id,
            contractName: contract.contractName,
            contractNumber: contract.contractNumber,
            contractDate: contract.createTime,
            contractAmount: contract.contractAmount || 0,
            contractType: contract.contractType,
            pricingMethod: contract.pricingMethod,
            interestCalculation: contract.interestCalculation,
            settlementMethod: contract.settlementMethod,
            settlementMethods: contract.settlementMethods,
            settlementCycle: contract.settlementCycle,
            paymentDays: contract.paymentDays,
            interestFreeDays: contract.interestFreeDays,
            interestRateType: contract.interestRateType,
            fixedInterestRate: contract.fixedInterestRate,
            contractParties: contract.contractParties,
            contractItems: contract.contractItems,
            variableInterestRates: contract.variableInterestRates,
            contractUnitId: contract.contractUnitId,
            contractUnit: contract.contractUnit,
            supplierUnitId: contract.supplierUnitId,
            supplierUnit: contract.supplierUnit,
            demandUnitIds: contract.demandUnitIds,
            demandUnits: contract.demandUnits,
            projectIds: contract.projectIds,
            projects: contract.projects
          }));
        }
      }
      
      return true;
    },
    
    // 验证合同计价方式一致性
    validateContractPricingMethodConsistency(contractIds) {
      if (contractIds.length <= 1) {
        return true; // 单个合同不需要验证
      }
      
      const contracts = this.createMode === 'supplier' ? 
        this.supplierContractOptions : 
        this.contractOptions;
      
      const selectedContracts = contracts.filter(contract => 
        contractIds.includes(contract.id)
      );
      
      if (selectedContracts.length === 0) {
        this.$message.error('未找到选中的合同信息');
        return false;
      }
      
      // 检查所有合同的计价方式是否一致
      const pricingMethods = selectedContracts.map(contract => {
        // 从合同信息中提取计价方式
        if (contract.pricingMethod) {
          return contract.pricingMethod;
        }
        // 如果合同信息中没有计价方式，尝试从合同清单中获取
        if (contract.contractItems && contract.contractItems.length > 0) {
          const firstItem = contract.contractItems[0];
          return firstItem.pricingMethod || 'FIXED';
        }
        return 'FIXED'; // 默认为固定价
      });
      
      const uniquePricingMethods = [...new Set(pricingMethods)];
      
      if (uniquePricingMethods.length > 1) {
        const methodNames = uniquePricingMethods.map(method => 
          method === 'FLOATING' ? '变动价' : '固定价'
        );
        this.$message.error(`所选合同的计价方式不一致（${methodNames.join('、')}），请选择相同计价方式的合同`);
        this.orderForm.contract_ids = [];
        return false;
      }
      
      return true;
    },
    loadAndMergeContractItems(contractIds) {
      this.materialsLoading = true;
      
      // 并行加载所有选中合同的详情
      const contractPromises = contractIds.map(contractId => 
        getContractById(contractId)
      );
      
      Promise.all(contractPromises)
        .then(responses => {
          const allMaterials = [];
          const contractDetails = []; // 存储合同详情，用于编辑模式
          
          responses.forEach((response, index) => {
            if (response.data && response.data.success) {
              const contractData = response.data.data;
              const contractId = contractIds[index];
              
              // 保存合同详情（创建模式和编辑模式都需要）
              contractDetails.push(contractData);
              
              // 更新合同选项中的pricingMethod（如果合同选项中没有pricingMethod，则从合同详情中更新）
              const contracts = this.createMode === 'supplier' 
                ? this.supplierContractOptions 
                : this.contractOptions;
              const contractOption = contracts.find(c => c.id === contractId);
              if (contractOption && !contractOption.pricingMethod && contractData.pricingMethod) {
                contractOption.pricingMethod = contractData.pricingMethod;
              }
              
              // 提取材料清单
              let materials = [];
              if (contractData.contractItems && Array.isArray(contractData.contractItems)) {
                materials = contractData.contractItems;
              } else if (contractData.materials_list && Array.isArray(contractData.materials_list)) {
                materials = contractData.materials_list;
              } else if (contractData.contract_list && Array.isArray(contractData.contract_list)) {
                materials = contractData.contract_list;
              } else if (contractData.materials && Array.isArray(contractData.materials)) {
                materials = contractData.materials;
              } else if (contractData.items && Array.isArray(contractData.items)) {
                materials = contractData.items;
              }
              
          // 为每个材料项添加合同ID标识，并设置数量为空（先保存合同可用数量用于校验）
          materials.forEach(item => {
            item.sourceContractId = contractId;
            item.sourceContractName = contractData.contractName || `合同${contractId}`;
            item.contractQuantity = parseFloat(item.quantity || item.materials_quantity) || 0; // 保存合同数量供校验
            item.quantity = null; // 创建订单时数量默认为空，让用户自己填写
            item.taxInclusiveTotalAmount = null; // 数量为空时，总价也为空
            // 确保变动价相关字段被正确保留
            if (item.pricingMethod === 'FLOATING') {
              // 变动价合同：保留含税固定价和含税变动价
              item.taxInclusiveBasePrice = item.taxInclusiveBasePrice || null;
              item.taxInclusiveFloatingPrice = item.taxInclusiveFloatingPrice || null;
            }
          });
          
          allMaterials.push(...materials);
            }
          });
          
          // 编辑模式：更新当前订单数据中的合同信息
          if (this.isEditMode && this.currentOrderData && contractDetails.length > 0) {
            this.currentOrderData.contractDetails = contractDetails;
            
            // 编辑模式：设置供应商信息（从第一个合同获取）
            const firstContract = contractDetails[0];
            if (firstContract.contractParties) {
              const supplierParty = firstContract.contractParties.find(party => party.partyType === 'SUPPLIER');
              if (supplierParty && supplierParty.unitInfo) {
                this.orderForm.supplier_id = supplierParty.unitInfoId;
                this.orderForm.supplier_name = supplierParty.unitInfo.unitName;
                this.orderForm.supplier_short_name = supplierParty.unitInfo.unitShortName || supplierParty.unitInfo.unitName;
                
                // 填充供应商联系信息
                this.orderForm.supplier_contact_person = supplierParty.unitInfo.contactPerson || '';
                this.orderForm.supplier_phone = supplierParty.unitInfo.phone || '';
                
                // 加载供应商单位的联系人列表
                this.loadSupplierUnitContacts(supplierParty.unitInfoId);
                
                // 注意：不要将供应商信息填充到订单联系人字段
                // 订单联系人应该是需求方的联系人，不是供应商联系人
              }
            }
            
            // 填充合同单位联系信息（使用第一个合同的合同单位）
            // 如果合同有contactPerson和contactPhone字段，优先使用合同联系人信息
            // 即使之前已经填充了其他联系人信息或设置了selectedContactId，如果合同有联系人信息，也应该优先使用
            if (firstContract.contactPerson && firstContract.contactPhone) {
              // 优先使用合同的contactPerson和contactPhone字段
              this.orderForm.contract_unit_contact_person = firstContract.contactPerson;
              this.orderForm.contract_unit_phone = firstContract.contactPhone;
              
              // 将合同联系人添加到联系人列表中（如果不存在），并获取联系人ID
              const contactId = this.addContractContactToUnitContacts(
                firstContract.contactPerson,
                firstContract.contactPhone,
                firstContract.id
              );
              
              if (contactId) {
                this.selectedContactId = contactId;
}
              
} else if (!this.selectedContactId && !this.orderForm.contract_unit_contact_person && !this.orderForm.contract_unit_phone) {
              // 如果合同联系人为空，且用户没有选择联系人且表单中联系人信息也为空，按照之前的逻辑处理
              if (firstContract.contractUnit) {
                this.orderForm.contract_unit_contact_person = firstContract.contractUnit.contactPerson || '';
                this.orderForm.contract_unit_phone = firstContract.contractUnit.phone || '';
              }
              
              // 优先从demandUnits中获取需求方联系信息
              if (firstContract.demandUnits && firstContract.demandUnits.length > 0) {
                const demandUnit = firstContract.demandUnits[0];
                this.orderForm.contract_unit_contact_person = demandUnit.contactPerson || '';
                this.orderForm.contract_unit_phone = demandUnit.phone || '';
              }
            }
            
            // 收集可用地址信息（单位用户可以选择地址来源）
            this.collectAvailableAddresses(firstContract);
          }
          
          // 创建模式：填充合同单位联系信息（使用第一个合同的合同单位）
          if (!this.isEditMode && contractDetails.length > 0) {
            const firstContract = contractDetails[0];
            
            // 设置供应商信息（从第一个合同获取）
            if (firstContract.contractParties) {
              const supplierParty = firstContract.contractParties.find(party => party.partyType === 'SUPPLIER');
              if (supplierParty && supplierParty.unitInfo) {
                this.orderForm.supplier_id = supplierParty.unitInfoId;
                this.orderForm.supplier_name = supplierParty.unitInfo.unitName;
                this.orderForm.supplier_short_name = supplierParty.unitInfo.unitShortName || supplierParty.unitInfo.unitName;
                
                // 填充供应商联系信息
                this.orderForm.supplier_contact_person = supplierParty.unitInfo.contactPerson || '';
                this.orderForm.supplier_phone = supplierParty.unitInfo.phone || '';
                
                // 加载供应商单位的联系人列表
                this.loadSupplierUnitContacts(supplierParty.unitInfoId);
              }
            }
            
            // 填充合同单位联系信息
            // 如果合同有contactPerson和contactPhone字段，优先使用合同联系人信息
            // 即使之前已经填充了其他联系人信息或设置了selectedContactId，如果合同有联系人信息，也应该优先使用
            if (firstContract.contactPerson && firstContract.contactPhone) {
              // 优先使用合同的contactPerson和contactPhone字段
              this.orderForm.contract_unit_contact_person = firstContract.contactPerson;
              this.orderForm.contract_unit_phone = firstContract.contactPhone;
              
              // 将合同联系人添加到联系人列表中（如果不存在），并获取联系人ID
              const contactId = this.addContractContactToUnitContacts(
                firstContract.contactPerson,
                firstContract.contactPhone,
                firstContract.id
              );
              
              if (contactId) {
                this.selectedContactId = contactId;
}
              
} else if (!this.selectedContactId && !this.orderForm.contract_unit_contact_person && !this.orderForm.contract_unit_phone) {
              // 如果合同联系人为空，且用户没有选择联系人且表单中联系人信息也为空，按照之前的逻辑处理
              if (firstContract.contractUnit) {
                this.orderForm.contract_unit_contact_person = firstContract.contractUnit.contactPerson || '';
                this.orderForm.contract_unit_phone = firstContract.contractUnit.phone || '';
              }
              
              // 优先从demandUnits中获取需求方联系信息
              if (firstContract.demandUnits && firstContract.demandUnits.length > 0) {
                const demandUnit = firstContract.demandUnits[0];
                this.orderForm.contract_unit_contact_person = demandUnit.contactPerson || '';
                this.orderForm.contract_unit_phone = demandUnit.phone || '';
              }
            }
            
            // 收集可用地址信息（单位用户可以选择地址来源）
            this.collectAvailableAddresses(firstContract);
            
            // 根据合同是否关联项目，加载对应的需求方联系人（项目内用户或需求单位用户）
            this.loadUnitContacts(firstContract);
          }
          
          // 验证所有合同的计价方式是否一致
          if (contractDetails.length > 1) {
            const pricingMethods = contractDetails.map(contract => {
              // 从合同详情中提取计价方式
              if (contract.pricingMethod) {
                return contract.pricingMethod;
              }
              // 如果合同详情中没有计价方式，尝试从合同清单中获取
              if (contract.contractItems && contract.contractItems.length > 0) {
                const firstItem = contract.contractItems[0];
                return firstItem.pricingMethod || 'FIXED';
              }
              return 'FIXED'; // 默认为固定价
            });
            
            const uniquePricingMethods = [...new Set(pricingMethods)];
            
            if (uniquePricingMethods.length > 1) {
              const methodNames = uniquePricingMethods.map(method => 
                method === 'FLOATING' ? '变动价' : '固定价'
              );
              this.$message.error(`所选合同的计价方式不一致（${methodNames.join('、')}），请选择相同计价方式的合同`);
              this.orderForm.contract_ids = [];
              this.materialsLoading = false;
              return;
            }
          }
          
          // 合并材料清单（编辑模式下传递已存在的订单项）
          const existingOrderItems = this.isEditMode ? (this.orderForm.order_items || []) : [];
          this.orderForm.order_items = this.mergeContractItems(allMaterials, existingOrderItems);
          this.recalculateTotal();
          
          // 生成订单标题
          this.generateOrderTitle(contractIds);
        })
        .catch(error => {
          console.error('加载合同详情失败:', error);
          this.$message.error('加载合同详情失败，请稍后重试');
        })
        .finally(() => {
          this.materialsLoading = false;
        });
    },
    
    // 编辑模式下加载合同产品并合并已存在的订单项
    async loadAndMergeContractItemsForEdit(contractIds, existingOrderItems) {
      this.materialsLoading = true;
      
      try {
        // 并行加载所有选中合同的详情
        const contractPromises = contractIds.map(contractId => 
          getContractById(contractId)
        );
        
        const responses = await Promise.all(contractPromises);
        const allMaterials = [];
        const contractDetails = [];
        
        responses.forEach((response, index) => {
          if (response.data && response.data.success) {
            const contractData = response.data.data;
            const contractId = contractIds[index];
            
            // 保存合同详情
            contractDetails.push(contractData);
            
            // 更新合同选项中的pricingMethod（如果合同选项中没有pricingMethod，则从合同详情中更新）
            const contracts = this.createMode === 'supplier' 
              ? this.supplierContractOptions 
              : this.contractOptions;
            const contractOption = contracts.find(c => c.id === contractId);
            if (contractOption && !contractOption.pricingMethod && contractData.pricingMethod) {
              contractOption.pricingMethod = contractData.pricingMethod;
            }
            
            // 提取材料清单
            let materials = [];
            if (contractData.contractItems && Array.isArray(contractData.contractItems)) {
              materials = contractData.contractItems;
            } else if (contractData.materials_list && Array.isArray(contractData.materials_list)) {
              materials = contractData.materials_list;
            } else if (contractData.contract_list && Array.isArray(contractData.contract_list)) {
              materials = contractData.contract_list;
            } else if (contractData.materials && Array.isArray(contractData.materials)) {
              materials = contractData.materials;
            }
            
            // 为每个材料添加来源合同信息，并保存合同可用数量供校验
            materials = materials.map(material => ({
              ...material,
              sourceContractName: contractData.contractName || '',
              sourceContractId: contractId,
              contractQuantity: parseFloat(material.quantity || material.materials_quantity) || 0
            }));
            
            allMaterials.push(...materials);
          }
        });
        
        // 更新当前订单数据中的合同信息
        if (this.currentOrderData && contractDetails.length > 0) {
          this.currentOrderData.contractDetails = contractDetails;
          
          // 编辑模式：设置供应商信息（从第一个合同获取）
          const firstContract = contractDetails[0];
          if (firstContract.contractParties) {
            const supplierParty = firstContract.contractParties.find(party => party.partyType === 'SUPPLIER');
            if (supplierParty && supplierParty.unitInfo) {
              this.orderForm.supplier_id = supplierParty.unitInfoId;
              this.orderForm.supplier_name = supplierParty.unitInfo.unitName;
              this.orderForm.supplier_short_name = supplierParty.unitInfo.unitShortName || supplierParty.unitInfo.unitName;
              
              // 填充供应商联系信息
              this.orderForm.supplier_contact_person = supplierParty.unitInfo.contactPerson || '';
              this.orderForm.supplier_phone = supplierParty.unitInfo.phone || '';
              
              // 加载供应商单位的联系人列表
              this.loadSupplierUnitContacts(supplierParty.unitInfoId);
            }
          }
          
          // 填充合同单位联系信息（使用第一个合同的合同单位）
          if (firstContract.contactPerson && firstContract.contactPhone) {
            this.orderForm.contract_unit_contact_person = firstContract.contactPerson;
            this.orderForm.contract_unit_phone = firstContract.contactPhone;
            
            // 将合同联系人添加到联系人列表中（如果不存在），并获取联系人ID
            const contactId = this.addContractContactToUnitContacts(
              firstContract.contactPerson,
              firstContract.contactPhone,
              firstContract.id
            );
            
            if (contactId) {
              this.selectedContactId = contactId;
            }
          } else if (!this.selectedContactId && !this.orderForm.contract_unit_contact_person && !this.orderForm.contract_unit_phone) {
            if (firstContract.contractUnit) {
              this.orderForm.contract_unit_contact_person = firstContract.contractUnit.contactPerson || '';
              this.orderForm.contract_unit_phone = firstContract.contractUnit.phone || '';
            }
            
            // 优先从demandUnits中获取需求方联系信息
            if (firstContract.demandUnits && firstContract.demandUnits.length > 0) {
              const demandUnit = firstContract.demandUnits[0];
              this.orderForm.contract_unit_contact_person = demandUnit.contactPerson || '';
              this.orderForm.contract_unit_phone = demandUnit.phone || '';
            }
          }
          
          // 收集可用地址信息（单位用户可以选择地址来源）
          this.collectAvailableAddresses(firstContract);
        }
        
        // 合并材料清单，并合并已存在的订单项数据
        this.orderForm.order_items = this.mergeContractItems(allMaterials, existingOrderItems);
        this.recalculateTotal();
        
        // 生成订单标题
        this.generateOrderTitle(contractIds);
      } catch (error) {
        console.error('加载合同详情失败:', error);
        this.$message.error('加载合同详情失败，请稍后重试');
      } finally {
        this.materialsLoading = false;
      }
    },
    
    // 合并合同清单（相同价格、规格、材质、名称的项目合并）
    mergeContractItems(allMaterials, existingOrderItems = []) {
      const mergedMap = new Map();
      
      // 创建已存在订单项的映射（用于编辑模式下合并数据）
      const existingItemsMap = new Map();
      if (existingOrderItems && existingOrderItems.length > 0) {
        existingOrderItems.forEach(existingItem => {
          // 创建合并键：产品名称 + 规格 + 材质 + 定价方式 + 价格
          const pricingMethod = existingItem.pricingMethod || 'FIXED';
          const priceKey = pricingMethod === 'FLOATING' 
            ? `${existingItem.taxInclusiveBasePrice || 0}_${existingItem.taxInclusiveFloatingPrice || 0}`
            : `${existingItem.taxInclusiveUnitPrice || existingItem.totalAmount || existingItem.price || 0}`;
          const mergeKey = `${existingItem.productName || existingItem.materials_name || existingItem.name || ''}_${existingItem.specification || existingItem.materials_spec || existingItem.spec || ''}_${existingItem.materialRequirement || existingItem.material_requirement || ''}_${pricingMethod}_${priceKey}`;
          existingItemsMap.set(mergeKey, existingItem);
        });
      }
      
      // 显示所有项目，包括数量为0的
      allMaterials.forEach(item => {
        // 创建合并键：产品名称 + 规格 + 材质 + 定价方式 + 价格
        const pricingMethod = item.pricingMethod || 'FIXED';
        const priceKey = pricingMethod === 'FLOATING' 
          ? `${item.taxInclusiveBasePrice || 0}_${item.taxInclusiveFloatingPrice || 0}`
          : `${item.taxInclusiveUnitPrice || item.totalAmount || item.price || 0}`;
        const mergeKey = `${item.productName || item.materials_name || item.name || ''}_${item.specification || item.materials_spec || item.spec || ''}_${item.materialRequirement || item.material_requirement || ''}_${pricingMethod}_${priceKey}`;
        
        if (mergedMap.has(mergeKey)) {
          // 合并合同可用数量（item.quantity 可能已被置空，优先用 contractQuantity）
          const existingItem = mergedMap.get(mergeKey);
          const itemContractQty = item.contractQuantity ?? (parseFloat(item.quantity || item.materials_quantity) || 0);
          existingItem.contractQuantity = (existingItem.contractQuantity || 0) + itemContractQty;
          existingItem.quantity = (existingItem.quantity || 0) + itemContractQty;
          
          // 根据定价方式计算总价
          if (existingItem.pricingMethod === 'FLOATING') {
            // 变动价合同：只使用变动价计算，基础价为0
            const floatingPrice = existingItem.taxInclusiveFloatingPrice || 0;
            existingItem.taxInclusiveTotalAmount = floatingPrice * (existingItem.quantity || 0);
          } else {
            existingItem.taxInclusiveTotalAmount = (existingItem.taxInclusiveUnitPrice || 0) * (existingItem.quantity || 0);
          }
          
          // 添加来源合同信息
          if (!existingItem.sourceContracts) {
            existingItem.sourceContracts = [existingItem.sourceContractName];
          }
          if (item.sourceContractName && !existingItem.sourceContracts.includes(item.sourceContractName)) {
            existingItem.sourceContracts.push(item.sourceContractName);
          }
        } else {
          // 检查是否有已存在的订单项数据（编辑模式）
          const existingItem = existingItemsMap.get(mergeKey);
          
          // 创建新的合并项（item.quantity 可能已被置空，优先用 contractQuantity）
          const itemContractQty = item.contractQuantity ?? (parseFloat(item.quantity || item.materials_quantity) || 0);
          const mergedItem = {
            ...item,
            contractQuantity: itemContractQty,
            // 编辑模式：如果有已存在的订单项，使用已存在的数量和其他数据；否则数量默认为0
            quantity: existingItem ? (existingItem.quantity || 0) : 0,
            taxInclusiveTotalAmount: existingItem ? (existingItem.taxInclusiveTotalAmount || 0) : 0,
            deliveryDate: existingItem ? (existingItem.deliveryDate || item.deliveryDate || this.getDefaultDeliveryDate()) : (item.deliveryDate || this.getDefaultDeliveryDate()),
            sourceContracts: item.sourceContractName ? [item.sourceContractName] : [],
            // 确保定价方式字段存在
            pricingMethod: item.pricingMethod || 'FIXED',
            // 确保变动价字段存在（变动价合同需要保留含税固定价）
            taxInclusiveBasePrice: item.taxInclusiveBasePrice || null,
            taxInclusiveFloatingPrice: item.taxInclusiveFloatingPrice || null,
            // 编辑模式：保留已存在订单项的ID和其他字段
            ...(existingItem ? {
              id: existingItem.id,
              createTime: existingItem.createTime,
              orderId: existingItem.orderId,
              remark: existingItem.remark || item.remark || '',
              forwardedOrderId: existingItem.forwardedOrderId || null
            } : {})
          };
          
          // 如果数量不为0，重新计算总价
          if (mergedItem.quantity > 0) {
            if (mergedItem.pricingMethod === 'FLOATING') {
              const floatingPrice = mergedItem.taxInclusiveFloatingPrice || 0;
              mergedItem.taxInclusiveTotalAmount = floatingPrice * mergedItem.quantity;
            } else {
              mergedItem.taxInclusiveTotalAmount = (mergedItem.taxInclusiveUnitPrice || 0) * mergedItem.quantity;
            }
          }
          
          mergedMap.set(mergeKey, mergedItem);
        }
      });
      
      return Array.from(mergedMap.values()).map((item, index) => ({
        ...item,
        sequenceNumber: index + 1
      }));
    },
    
    // 生成订单标题
    generateOrderTitle(contractIds) {
      const contracts = this.createMode === 'supplier' ? 
        this.supplierContractOptions : 
        this.contractOptions;
      
      const selectedContracts = contracts.filter(contract => 
        contractIds.includes(contract.id)
      );
      
      if (selectedContracts.length === 0) return;
      
      // 获取客户单位名称（使用contractUnit）
      let customerUnitName = '';
      if (selectedContracts[0].contractUnit) {
        customerUnitName = selectedContracts[0].contractUnit.unitShortName || selectedContracts[0].contractUnit.unitName || '';
      } else if (selectedContracts[0].contractParties) {
        const customerParty = selectedContracts[0].contractParties.find(party => party.partyType === 'CUSTOMER');
        if (customerParty && customerParty.unitInfo) {
          customerUnitName = customerParty.unitInfo.unitShortName || customerParty.unitInfo.unitName || '';
        }
      }
      
      // 生成标题
      if (selectedContracts.length === 1) {
        const contractName = (selectedContracts[0].contractName || '').replace(/合同$/g, '');
        this.orderForm.order_title = `${customerUnitName}${contractName}采购订单`;
      } else {
        this.orderForm.order_title = `${customerUnitName}多合同采购订单`;
      }
    },
    

    
    // 重置订单表单
    resetOrderForm() {
      if (this.$refs.orderForm) {
        this.$refs.orderForm.resetFields();
      }
      
      // 保存原始的订单类型，如果无法获取则默认为直采
      const orderType = this.orderForm ? (this.orderForm.order_type || '直采') : '直采';
      
      this.orderForm = {
        order_type: orderType, // 保留当前选择的订单类型
        contract_id: '', // 编辑模式使用
        contract_ids: [], // 多选合同ID数组
        selected_supplier_id: '', // 选中的供应商ID
        contract_type: '', // 合同类型，重置为空
        supplier_id: '',
        supplier_info: null,
        supplier_name: '',
        supplier_short_name: '', // 供应商简称
        customer_id: '',
        customer_name: '', // 客户名称
        create_id: this.currentUnitId,
        order_title: '',
        contact_person: '',
        contact_phone: '',
        contact_address: '',
        // 合同单位联系信息
        contract_unit_contact_person: '',
        contract_unit_phone: '',
        // 供应商联系信息
        supplier_contact_person: '',
        supplier_phone: '',
        status: '待接受',
        order_items: [],
        // 地址选择相关
        address_source: 'unit', // 'unit' 或 'project'，默认使用单位地址
        selected_project_id: null,
        available_addresses: {
          unit_address: '',
          project_addresses: []
        }
      };
      
      this.contractOptions = [];
      this.supplierOptions = [];
      this.supplierContractOptions = [];
      this.createMode = 'supplier'; // 重置创建模式
      this.totalQuantity = 0;
      this.totalPrice = 0;
      this.isEditMode = false; // 重置编辑模式标记
      this.currentOrderData = null; // 清空当前订单数据
      this.selectedContactId = null; // 重置选中的联系人ID
      this.supplierUnitContacts = []; // 重置供应商单位联系人列表
      this.selectedSupplierContactId = null; // 重置选中的供应商联系人ID
      this.contractItemFilterKeyword = ''; // 重置合同清单筛选
    },
    
    // 加载合同选项
    loadContractOptions() {
      // 清空已有合同选项
      this.contractOptions = [];
      this.apiStatus = '请求中...';
      this.apiResponseData = null;
      
      if (this.orderForm.order_type === '直采') {
        // 直采订单 - 加载当前用户创建的采购合同
        this.loadPurchaseContracts();
      } else {
        // 集采订单 - 加载包含当前用户的销售合同
        this.loadCustomerContracts();
      }
    },
    
    // 加载采购合同列表（用于通过合同创建模式）
    loadPurchaseContracts() {
      // 使用新的API接口查询执行中的采购合同
      const params = {
        status: 'EXECUTING' // 只查询执行中的合同
      };
      
      // 使用新的采购合同API
      query_purchase_contracts_all(this.currentUnitId, params)
        .then(response => {
if (response.data && response.data.success) {
            // 新接口直接返回合同数组
            let contractList = response.data.data || [];
            
            // 确保返回的是数组格式
            if (!Array.isArray(contractList)) {
              contractList = contractList ? [contractList] : [];
            }
            
            if (contractList.length === 0) {
              this.$message.warning('未找到执行中的采购合同，请先创建合同并确保合同状态为执行中');
            }
            
            // 直接设置合同选项
            this.contractOptions = contractList;
} else {
            console.error('API请求失败:', response.data);
            this.$message.error(response.data?.message || '获取采购合同列表失败');
          }
        })
        .catch(error => {
          console.error('获取采购合同列表失败:', error);
          this.$message.error('获取采购合同列表失败，请稍后重试');
        });
    },
    
    

    
    // 加载销售合同列表
    loadCustomerContracts() {
// 设置参数 - 添加当前用户的ID筛选条件
      const params = {
        customer_id: this.currentUnitId, // 只查询包含当前用户作为客户的合同
      };
      
// 集采订单 - 使用销售合同API
      query_customer_contracts_pagination(params)
        .then(response => {
this.apiStatus = '已收到响应: ' + (response.status || '200');
          this.apiResponseData = response.data;
          
          if (response.data && response.data.success) {
            // 提取合同数据
            let contractList = [];
            const responseData = response.data.data;
            
if (Array.isArray(responseData)) {
              contractList = responseData;
} else if (responseData && responseData.data && Array.isArray(responseData.data)) {
              contractList = responseData.data;
} else if (responseData && responseData.records && Array.isArray(responseData.records)) {
              contractList = responseData.records;
} else {
              console.warn('未找到有效的合同数据数组，尝试直接处理返回数据');
              // 尝试直接处理返回数据
              if (typeof responseData === 'object' && responseData !== null) {
                contractList = [responseData]; // 将单个对象放入数组
}
            }
            
            if (contractList.length === 0) {
              console.warn('未能提取有效的销售合同数据');
              this.$message.warning('未找到包含您单位的销售合同，请联系销售方创建合同');
            }
            
            this.contractOptions = contractList;
} else {
            console.error('API请求失败:', response.data);
            this.apiStatus = '请求失败: ' + (response.data?.message || '未知错误');
            this.$message.error(response.data?.message || '获取销售合同列表失败');
          }
        })
        .catch(error => {
          console.error('获取销售合同列表失败:', error);
          this.apiStatus = '请求异常: ' + (error.message || '未知错误');
          this.$message.error('获取销售合同列表失败，请稍后重试');
        });
    },
    
    // 合同选择改变
    handleContractChange(contractId) {
      if (!contractId) {
        this.orderForm.order_items = [];
        this.orderForm.supplier_info = null;
        this.orderForm.supplier_id = '';
        this.orderForm.supplier_name = '';
        this.orderForm.supplier_short_name = '';
        this.orderForm.customer_name = '';
        this.orderForm.contact_person = '';
        this.orderForm.contact_phone = '';
        this.orderForm.contact_address = '';
        this.selectedContractName = '';
        this.selectedContractNumber = '';
        this.totalQuantity = 0;
        this.totalPrice = 0;
        return;
      }
      
      // 获取选中的合同信息
      const selectedContract = this.contractOptions.find(item => item.id === contractId);
      
      if (selectedContract) {
// 设置自动生成的订单标题
        // 新规则：需求方单位名称（优先用简称）+ 合同名称（去掉"合同"两字）+ "采购订单"
        let customerUnitName = '';
        let contractName = selectedContract.contractName || selectedContract.contract_name || '未知合同';
        
        // 获取需求方单位名称，优先使用contractUnit
        if (selectedContract.contractUnit) {
          customerUnitName = selectedContract.contractUnit.unitShortName || selectedContract.contractUnit.unitName || '';
        } else if (selectedContract.contractParties) {
          const customerParty = selectedContract.contractParties.find(party => party.partyType === 'CUSTOMER');
          if (customerParty && customerParty.unitInfo) {
            // 优先使用简称，如果没有简称则使用全称
            customerUnitName = customerParty.unitInfo.unitShortName || customerParty.unitInfo.unitName || '';
          }
        }
        
        // 如果通过contractParties没找到，尝试其他方式
        if (!customerUnitName && selectedContract.demandUnits && selectedContract.demandUnits.length > 0) {
          const firstCustomer = selectedContract.demandUnits[0];
          customerUnitName = firstCustomer.unitShortName || firstCustomer.unitName || '';
        }
        
        // 处理合同名称，去掉"合同"两字
        contractName = contractName.replace(/合同$/g, '');
        
        // 生成订单标题
        this.orderForm.order_title = `${customerUnitName}${contractName}采购订单`;
        
        // 保存合同类型
        this.orderForm.contract_type = selectedContract.contractType || selectedContract.contract_type || 'PURCHASE';
        
        // 根据新的合同数据结构提取供应商信息
        // 从contractParties中找到供应商信息
        const supplierParty = selectedContract.contractParties?.find(party => party.partyType === 'SUPPLIER');
        
        if (supplierParty && supplierParty.unitInfo) {
// 设置供应商ID和信息
          this.orderForm.supplier_id = supplierParty.unitInfoId;
          this.orderForm.supplier_name = supplierParty.unitInfo.unitName || '未知供应商';
          
          // 构建供应商信息对象
          this.orderForm.supplier_info = {
            unit_id: supplierParty.unitInfoId,
            unit_name: supplierParty.unitInfo.unitName || '未知供应商',
            contact_person: supplierParty.unitInfo.contactPerson || '',
            contact_phone: supplierParty.unitInfo.phone || ''
          };
          
          // 填充供应商联系信息
          this.orderForm.supplier_contact_person = supplierParty.unitInfo.contactPerson || '';
          this.orderForm.supplier_phone = supplierParty.unitInfo.phone || '';
          
          // 加载供应商单位的联系人列表
          this.loadSupplierUnitContacts(supplierParty.unitInfoId);
          
} else {
          console.warn('未找到供应商信息');
          this.orderForm.supplier_name = '未知供应商';
        }
        
        // 设置客户信息（当前用户作为客户）
        this.orderForm.customer_id = this.currentUnitId;
        this.orderForm.create_id = this.currentUnitId;
        
        // 设置显示字段
        this.selectedContractName = selectedContract.contractName || selectedContract.contract_name || '';
        this.selectedContractNumber = selectedContract.contractNumber || selectedContract.contract_id || '';
        
        // 填充客户名称
        if (selectedContract.contractUnit) {
          this.orderForm.customer_name = selectedContract.contractUnit.unitName || '';
        } else if (selectedContract.contractParties) {
          const customerParty = selectedContract.contractParties.find(party => party.partyType === 'CUSTOMER');
          if (customerParty && customerParty.unitInfo) {
            this.orderForm.customer_name = customerParty.unitInfo.unitName || '';
          }
        }
        
        // 填充合同单位联系信息
        // 如果合同有contactPerson和contactPhone字段，优先使用合同联系人信息
        // 即使之前已经填充了其他联系人信息或设置了selectedContactId，如果合同有联系人信息，也应该优先使用
        if (selectedContract.contactPerson && selectedContract.contactPhone) {
          // 优先使用合同的contactPerson和contactPhone字段
          this.orderForm.contract_unit_contact_person = selectedContract.contactPerson;
          this.orderForm.contract_unit_phone = selectedContract.contactPhone;
          
          // 将合同联系人添加到联系人列表中（如果不存在），并获取联系人ID
          const contactId = this.addContractContactToUnitContacts(
            selectedContract.contactPerson,
            selectedContract.contactPhone,
            selectedContract.id
          );
          
          if (contactId) {
            this.selectedContactId = contactId;
}
          
} else if (!this.selectedContactId && !this.orderForm.contract_unit_contact_person && !this.orderForm.contract_unit_phone) {
          // 如果合同列表数据中没有contactPerson和contactPhone，且用户没有选择联系人且表单中联系人信息也为空，按照之前的逻辑处理
          // 但注意：这些信息可能不完整，会在loadContractDetails中重新填充
          if (selectedContract.contractUnit) {
            this.orderForm.contract_unit_contact_person = selectedContract.contractUnit.contactPerson || '';
            this.orderForm.contract_unit_phone = selectedContract.contractUnit.phone || '';
          }
          
          // 优先从demandUnits中获取需求方联系信息
          if (selectedContract.demandUnits && selectedContract.demandUnits.length > 0) {
            const demandUnit = selectedContract.demandUnits[0];
            this.orderForm.contract_unit_contact_person = demandUnit.contactPerson || '';
            this.orderForm.contract_unit_phone = demandUnit.phone || '';
          }
        }
        
        // 收集可用地址信息（单位用户可以选择地址来源）
        this.collectAvailableAddresses(selectedContract);
        
        // 根据合同是否关联项目，加载对应的需求方联系人（项目内用户或需求单位用户）
        this.loadUnitContacts(selectedContract);
        
        // 设置供应商简称
        if (supplierParty && supplierParty.unitInfo) {
          this.orderForm.supplier_short_name = supplierParty.unitInfo.unitShortName || supplierParty.unitInfo.unitName || '';
        }
        
}
      
      // 加载合同详情
      this.loadContractDetails(contractId);
    },
    
    // 加载合同详情
    loadContractDetails(contractId) {
      this.materialsLoading = true;
// 使用新的通用合同详情接口
      getContractById(contractId)
        .then(this.handleContractResponse)
        .catch(this.handleContractError);
    },
    
    // 处理合同响应
    handleContractResponse(response) {
      if (response.data && response.data.success) {
        const contractData = response.data.data;
        
        // 解析合同材料清单
        let materials = [];
        
        // 根据新的合同数据结构提取材料清单
        if (contractData.contractItems && Array.isArray(contractData.contractItems)) {
          materials = contractData.contractItems;
        } else if (contractData.materials_list && Array.isArray(contractData.materials_list)) {
          materials = contractData.materials_list;
        } else if (contractData.contract_list && Array.isArray(contractData.contract_list)) {
          materials = contractData.contract_list;
        } else if (contractData.materials && Array.isArray(contractData.materials)) {
          materials = contractData.materials;
        } else if (contractData.items && Array.isArray(contractData.items)) {
          materials = contractData.items;
        }
        
        // 转换材料数据格式，显示所有项目（包括数量为0的）
        this.orderForm.order_items = materials
          .map(item => {
            // 获取默认到货日期：今天+7天
            const deliveryDateStr = this.getDefaultDeliveryDate();
            
            // 根据新的合同数据结构映射字段
            const mappedItem = {
              ...item,
              contractQuantity: parseFloat(item.quantity || item.materials_quantity) || 0, // 保存合同数量供校验
              quantity: null, // 创建订单时数量默认为空，让用户自己填写
              // 映射新的字段结构 - 与合同清单字段保持一致
              sequenceNumber: item.sequenceNumber || 1, // 序号
              category: item.category || item.materials_category || '其他',
              productName: item.productName || item.materials_name || item.name || '未命名材料',
              specification: item.specification || item.materials_spec || item.spec || '',
              materialRequirement: item.materialRequirement || '',
              unit: item.unit || '个',
              // 定价方式
              pricingMethod: item.pricingMethod || 'FIXED',
              // 固定价字段
              taxExclusiveUnitPrice: item.taxExclusiveUnitPrice || item.unitPrice || item.before_price || item.price_without_tax || 0,
              taxInclusiveUnitPrice: item.taxInclusiveUnitPrice || item.totalAmount || item.price || 0,
              // 变动价字段（变动价合同需要保留含税固定价）
              taxInclusiveBasePrice: item.taxInclusiveBasePrice || null,
              taxInclusiveFloatingPrice: item.taxInclusiveFloatingPrice || null,
              taxRate: item.taxRate || item.tax_rate || 0,
              taxInclusiveTotalAmount: null, // 数量为空时，总价也为空
              deliveryDate: item.deliveryDate || deliveryDateStr, // 默认到货日期：今天+7天
              remark: item.remark || '', // 备注信息
              supplier_name: item.supplier_name || ''
            };
            return mappedItem;
          });
        
        // 更新合同和订单信息
        const selectedContract = this.contractOptions.find(item => item.id === this.orderForm.contract_id);
        if (selectedContract) {
          // 更新表单上的联系信息等，使用新的订单标题规则
          let customerUnitName = '';
          let contractName = selectedContract.contractName || selectedContract.contract_name || '未知合同';
          
          // 获取需求方单位名称，优先使用简称
          if (selectedContract.contractParties) {
            const customerParty = selectedContract.contractParties.find(party => party.partyType === 'CUSTOMER');
            if (customerParty && customerParty.unitInfo) {
              customerUnitName = customerParty.unitInfo.unitShortName || customerParty.unitInfo.unitName || '';
            }
          }
          
          // 如果通过contractParties没找到，尝试其他方式
          if (!customerUnitName && selectedContract.demandUnits && selectedContract.demandUnits.length > 0) {
            const firstCustomer = selectedContract.demandUnits[0];
            customerUnitName = firstCustomer.unitShortName || firstCustomer.unitName || '';
          }
          
          // 处理合同名称，去掉"合同"两字
          contractName = contractName.replace(/合同$/g, '');
          
          // 生成订单标题
          this.orderForm.order_title = `${customerUnitName}${contractName}采购订单`;
          
          if (this.orderForm.order_type === '直采') {
            // 直采订单 - 更新供应商信息
            if (!this.orderForm.supplier_info && contractData.supplier) {
              this.orderForm.supplier_id = contractData.supplier_id;
              this.orderForm.supplier_info = {
                unit_id: contractData.supplier_id,
                unit_name: contractData.supplier.unit_name || '未知供应商',
                contact_person: contractData.supplier.contact_person || '',
                contact_phone: contractData.supplier.phone || ''
              };
              
              // 填充到表单可编辑字段
              this.orderForm.supplier_name = contractData.supplier.unit_name || '未知供应商';
              
              // 填充供应商联系信息
              this.orderForm.supplier_contact_person = contractData.supplier.contact_person || '';
              this.orderForm.supplier_phone = contractData.supplier.phone || '';
              
              // 加载供应商单位的联系人列表
              this.loadSupplierUnitContacts(contractData.supplier_id);
            }
          } else {
            // 集采订单 - 设置客户信息为当前用户
            this.orderForm.customer_id = this.currentUnitId;
            
            // 供应商ID使用合同创建者ID
            this.orderForm.supplier_id = selectedContract.create_id;
            
            // 尝试从合同中获取供应商信息
            if (contractData.create_unit) {
              this.orderForm.supplier_name = contractData.create_unit.unit_name || '';
            } else if (selectedContract.create_unit) {
              this.orderForm.supplier_name = selectedContract.create_unit.unit_name || '';
            }
            
            // 加载供应商单位的联系人列表
            if (this.orderForm.supplier_id) {
              this.loadSupplierUnitContacts(this.orderForm.supplier_id);
            }
          }
          
          // 从合同中获取需求方联系信息（从demandUnits数组中获取）
          // 如果合同有contactPerson和contactPhone字段，优先使用合同联系人信息
          // 即使之前已经填充了其他联系人信息或设置了selectedContactId，如果合同有联系人信息，也应该优先使用
          if (contractData.contactPerson && contractData.contactPhone) {
            // 优先使用合同的contactPerson和contactPhone字段
            this.orderForm.contract_unit_contact_person = contractData.contactPerson;
            this.orderForm.contract_unit_phone = contractData.contactPhone;
            
            // 将合同联系人添加到联系人列表中（如果不存在），并获取联系人ID
            const contactId = this.addContractContactToUnitContacts(
              contractData.contactPerson,
              contractData.contactPhone,
              contractData.id
            );
            
            if (contactId) {
              this.selectedContactId = contactId;
}
            
} else if (!this.selectedContactId && !this.orderForm.contract_unit_contact_person && !this.orderForm.contract_unit_phone) {
            // 如果合同联系人为空，且用户没有选择联系人且表单中联系人信息也为空，按照之前的逻辑处理
            if (contractData.demandUnits && contractData.demandUnits.length > 0) {
              // 使用第一个需求方单位的信息
              const demandUnit = contractData.demandUnits[0];
              this.orderForm.contract_unit_contact_person = demandUnit.contactPerson || '';
              this.orderForm.contract_unit_phone = demandUnit.phone || '';
            } else if (contractData.contractUnit) {
              // 如果没有demandUnits，则使用contractUnit作为备选
              this.orderForm.contract_unit_contact_person = contractData.contractUnit.contactPerson || '';
              this.orderForm.contract_unit_phone = contractData.contractUnit.phone || '';
            }
          }
          
          // 收集可用地址信息（单位用户可以选择地址来源）
          this.collectAvailableAddresses(contractData);
          
          // 根据合同是否关联项目，加载对应的需求方联系人（项目内用户或需求单位用户）
          this.loadUnitContacts(contractData);
        }
        
        this.recalculateTotal();
      } else {
        console.error('获取合同详情失败:', response.data);
        this.$message.error(response.data?.message || '获取合同材料清单失败');
      }
      this.materialsLoading = false;
    },
    
    // 处理合同错误
    handleContractError(error) {
      console.error('获取合同材料清单失败:', error);
      this.$message.error('获取合同材料清单失败，请稍后重试');
      this.materialsLoading = false;
    },
    
    // 计算总数量和总价
    calculateTotals() {
      this.recalculateTotal();
    },
    
    // 重新计算单项总价
    // 处理数量输入
    handleQuantityInput(item, value) {
      // 如果值为空字符串，设置为 null
      if (value === '' || value === null || value === undefined) {
        item.quantity = null;
        item.taxInclusiveTotalAmount = null;
        this.recalculateTotal();
        return;
      }
      // 输入过程中保持原始字符串，避免 parseFloat 丢失尾部的 0（如 22.04 中的 0）
      // 允许格式：数字、可选小数点、可选小数部分（如 22.、22.0、22.04）
      if (/^\d*\.?\d*$/.test(value)) {
        item.quantity = value;
      } else {
        const numValue = parseFloat(value);
        item.quantity = isNaN(numValue) ? null : numValue;
      }
      this.recalculateItemTotal(item);
    },
    
    // 处理数量失焦，格式化数字并校验是否超过合同数量
    handleQuantityBlur(item) {
      if (item.quantity !== null && item.quantity !== undefined && item.quantity !== '') {
        const numValue = parseFloat(item.quantity);
        if (!isNaN(numValue)) {
          // 校验是否超过合同可用数量（输入完成后校验）
          const contractQty = parseFloat(item.contractQuantity);
          if (!isNaN(contractQty) && contractQty > 0 && numValue > contractQty) {
            this.$message.warning(`产品"${item.productName || item.materials_name || ''}"数量超过合同可用数量（可用${contractQty}${item.unit || ''}）`);
          }
          // 保留4位小数
          item.quantity = parseFloat(numValue.toFixed(4));
          this.recalculateItemTotal(item);
        } else {
          item.quantity = null;
        }
      } else {
        item.quantity = null;
      }
    },
    
    // 增加数量（不超过合同可用数量）
    increaseQuantity(item) {
      const contractQty = parseFloat(item.contractQuantity);
      const currentValue = item.quantity === null || item.quantity === undefined || item.quantity === '' ? 0 : parseFloat(item.quantity);
      let newValue = currentValue + 0.0001;
      if (!isNaN(contractQty) && contractQty > 0 && newValue > contractQty) {
        newValue = contractQty;
        this.$message.warning(`产品"${item.productName || item.materials_name || ''}"数量不能超过合同可用数量（${contractQty}${item.unit || ''}）`);
      }
      item.quantity = parseFloat(newValue.toFixed(4));
      this.recalculateItemTotal(item);
    },
    
    // 减少数量
    decreaseQuantity(item) {
      const currentValue = item.quantity === null || item.quantity === undefined || item.quantity === '' ? 0 : parseFloat(item.quantity);
      if (currentValue > 0) {
        const newValue = Math.max(0, currentValue - 0.0001);
        if (newValue === 0) {
          item.quantity = null;
          item.taxInclusiveTotalAmount = null;
        } else {
          item.quantity = parseFloat(newValue.toFixed(4));
        }
        this.recalculateItemTotal(item);
      }
    },
    
    recalculateItemTotal(item) {
      // 如果数量为空或0，清空总价
      if (!item.quantity || parseFloat(item.quantity) === 0) {
        item.taxInclusiveTotalAmount = null;
      } else {
        if (item.pricingMethod === 'FLOATING') {
          // 变动价合同：使用变动价计算总价
          const floatingPrice = item.taxInclusiveFloatingPrice || 0;
          item.taxInclusiveTotalAmount = parseFloat((floatingPrice * item.quantity).toFixed(2));
        } else {
          // 固定价合同：使用含税单价计算总价
          const unitPrice = item.taxInclusiveUnitPrice || 0;
          item.taxInclusiveTotalAmount = parseFloat((unitPrice * item.quantity).toFixed(2));
        }
      }
      this.recalculateTotal();
    },

    // 重新计算总数量和总价
    recalculateTotal() {
      if (!this.orderForm.order_items || this.orderForm.order_items.length === 0) {
        this.totalQuantity = 0;
        this.totalPrice = 0;
        return;
      }
      
      // 计算总数量（支持4位小数）
      this.totalQuantity = this.orderForm.order_items.reduce((sum, item) => {
        return sum + (parseFloat(item.quantity) || 0);
      }, 0);
      
      // 计算总价
      this.totalPrice = this.orderForm.order_items.reduce((sum, item) => {
        return sum + (parseFloat(item.taxInclusiveTotalAmount) || (parseFloat(item.taxInclusiveUnitPrice) || 0) * (parseFloat(item.quantity) || 0));
      }, 0).toFixed(2);
    },
    
    // 提交订单表单
    submitOrderForm() {
      this.$refs.orderForm.validate(valid => {
        if (!valid) {
          return false;
        }
        
        if (!this.orderForm.order_items || this.orderForm.order_items.length === 0) {
          this.$message.error('订单必须包含至少一个材料项');
          return false;
        }
        
        // 检查是否至少有一个数量大于0的项目
        const hasValidItems = this.orderForm.order_items.some(item => {
          const quantity = parseFloat(item.quantity) || 0;
          return quantity > 0;
        });
        
        if (!hasValidItems) {
          this.$message.error('订单必须包含至少一个数量大于0的材料项');
          return false;
        }
        
        // 验证合同选择
        if (this.isEditMode) {
          if (!this.orderForm.contract_id) {
            this.$message.error('请选择合同');
            return false;
          }
        } else {
          if (!this.orderForm.contract_ids || this.orderForm.contract_ids.length === 0) {
            this.$message.error('请选择合同');
            return false;
          }
        }
        
        // 校验产品数量是否超过合同可用数量（保存前最后一道校验）
        const exceedItems = this.orderForm.order_items.filter(item => {
          const qty = parseFloat(item.quantity) || 0;
          const contractQty = parseFloat(item.contractQuantity);
          return qty > 0 && !isNaN(contractQty) && contractQty > 0 && qty > contractQty;
        });
        if (exceedItems.length > 0) {
          const names = exceedItems.map(i => `"${i.productName || i.materials_name || ''}"`).join('、');
          this.$message.error(`以下产品数量超过合同可用数量，请修改：${names}`);
          return false;
        }
        
        this.submitLoading = true;
        
        // 转换订单项格式为新API格式（按照API文档），过滤掉数量为0的项目
        const orderItems = this.orderForm.order_items
          .filter(item => {
            // 过滤掉数量为0的项目，不传给后台保存
            const quantity = parseFloat(item.quantity) || 0;
            return quantity > 0;
          })
          .map((item, index) => {
return {
            // 编辑模式下保留关键字段
            ...(this.isEditMode && item.id ? { id: item.id } : {}),
            ...(item.createTime ? { createTime: item.createTime } : {}),
            ...(item.orderId ? { orderId: item.orderId } : {}),
            sequenceNumber: item.sequenceNumber || (index + 1), // 序号
            productName: item.productName || '', // 产品名称
            specification: item.specification || '', // 规格型号
            materialRequirement: item.materialRequirement || '', // 材质要求
            unit: item.unit || '', // 单位
            quantity: parseFloat(item.quantity) || 0, // 数量
            pricingMethod: item.pricingMethod || 'FIXED', // 定价方式，默认固定价
            taxExclusiveUnitPrice: parseFloat(item.taxExclusiveUnitPrice) || 0, // 不含税单价
            taxInclusiveUnitPrice: parseFloat(item.taxInclusiveUnitPrice) || 0, // 含税单价
            taxInclusiveBasePrice: item.pricingMethod === 'FLOATING' 
              ? (parseFloat(item.taxInclusiveBasePrice) || null) 
              : null, // 含税固定价（仅变动价模式）
            taxInclusiveFloatingPrice: item.pricingMethod === 'FLOATING' 
              ? (parseFloat(item.taxInclusiveFloatingPrice) || null) 
              : null, // 含税变动价（仅变动价模式）
            taxInclusiveTotalAmount: item.pricingMethod === 'FLOATING' 
              ? (parseFloat(item.taxInclusiveFloatingPrice) || 0) * (parseFloat(item.quantity) || 0) 
              : (parseFloat(item.taxInclusiveUnitPrice) || 0) * (parseFloat(item.quantity) || 0), // 含税合计：变动价用变动价×数量，固定价用含税单价×数量
            taxRate: parseFloat(item.taxRate) || 13, // 税率
            remark: item.remark || '', // 备注
            deliveryDate: item.deliveryDate || this.getDefaultDeliveryDate(), // 到货日期
            productSupplierId: item.productSupplierId || item.product_supplier_id || this.orderForm.supplier_id || 1, // 产品供应商ID
            forwardedOrderId: item.forwardedOrderId || null, // 转发订单ID（用于产品转发控制）
            // 添加来源合同信息（用于多合同订单）
            ...(item.sourceContracts ? { sourceContracts: item.sourceContracts.join(', ') } : {})
          };
        });
        
// 计算总数量和总价
        const productsTotal = orderItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const priceTotal = orderItems.reduce((sum, item) => sum + (item.taxInclusiveTotalAmount || 0), 0);
        
// 准备合同ID数组
        let contractIds = [];
        if (this.isEditMode) {
          // 编辑模式：优先使用contract_ids，如果没有则使用contract_id
          if (this.orderForm.contract_ids && this.orderForm.contract_ids.length > 0) {
            contractIds = this.orderForm.contract_ids.map(id => parseInt(id));
} else if (this.orderForm.contract_id) {
            contractIds = [parseInt(this.orderForm.contract_id)];
}
        } else {
          // 创建模式：使用多选合同ID数组
          if (this.orderForm.contract_ids && this.orderForm.contract_ids.length > 0) {
            contractIds = this.orderForm.contract_ids.map(id => parseInt(id));
}
        }
        
// 准备提交的数据，按照新API文档格式
        const submitData = {
          // 编辑模式下包含订单ID
          ...(this.isEditMode && this.orderForm.id ? { id: this.orderForm.id } : {}),
          // 编辑模式下包含订单级别的createTime和orderId
          ...(this.isEditMode && this.orderForm.createTime ? { createTime: this.orderForm.createTime } : {}),
          ...(this.isEditMode && this.orderForm.orderId ? { orderId: this.orderForm.orderId } : {}),
          orderTitle: this.orderForm.order_title || '订单标题',
          // 主合同ID（用于向后兼容，使用第一个合同ID）
          contractId: contractIds.length > 0 ? contractIds[0] : 1,
          // 多合同支持：合同ID数组
          contractIds: contractIds,
          contractType: this.orderForm.contract_type === 'CUSTOMER' ? 'customer' : 'purchase',
          orderType: this.orderForm.order_type === '直采' ? '01' : '02',
          supplierId: parseInt(this.orderForm.supplier_id) || 1,
          // 需求单位ID列表（从合同信息中提取）
          demandUnitIds: this.getDemandUnitIds(),
          // 二级机构ID列表（从合同信息中提取）
          secondaryOrgIds: this.getSecondaryOrgIds(),
          // 收货地址（独立字段）
          deliveryAddress: this.orderForm.contact_address || '',
          // 需求方联系信息
          demandContactPerson: this.orderForm.contract_unit_contact_person || '',
          demandContactPhone: this.orderForm.contract_unit_phone || '',
          // 供应商联系信息
          contactPerson: this.orderForm.supplier_contact_person || '',
          contactPhone: this.orderForm.supplier_phone || '',
          contactAddress: this.orderForm.contact_address || '',
          productsTotal: productsTotal,
          priceTotal: priceTotal,
          orderItems: orderItems
        };
        
// 使用API创建或更新订单
        create_order(submitData)
          .then(response => {
            if (response.data && response.data.success) {
              this.$message.success(this.isEditMode ? '订单更新成功' : '订单创建成功');
              this.createOrderDialogVisible = false;
              this.resetOrderForm();
              this.getOrderList(); // 刷新订单列表
            } else {
              // 处理业务逻辑错误（success: false）
              const errorMessage = response.data?.message || (this.isEditMode ? '订单更新失败' : '订单创建失败');
              this.$message.error(errorMessage);
              console.error('订单创建/更新失败:', response.data);
            }
            this.submitLoading = false;
          })
          .catch(error => {
            console.error(this.isEditMode ? '订单更新失败:' : '订单创建失败:', error);
            
            // 优先使用后端返回的错误信息
            let errorMessage = '';
            if (error.response && error.response.data) {
              // HTTP状态码错误（如400、500等），尝试从error.response.data中获取错误信息
              errorMessage = error.response.data.message || error.response.data.error || error.message || '未知错误';
            } else if (error.message) {
              errorMessage = error.message;
            } else {
              errorMessage = '未知错误';
            }
            
            this.$message.error(errorMessage);
            this.submitLoading = false;
          });
      });
    },
    
    // 获取需求单位ID列表
    getDemandUnitIds() {
      const demandUnitIds = [];
      
      if (this.orderForm.contract_ids && this.orderForm.contract_ids.length > 0) {
        // 优先使用contract_ids（支持多合同）
        const contracts = this.createMode === 'supplier' ? 
          this.supplierContractOptions : 
          this.contractOptions;
        const selectedContracts = contracts.filter(contract => 
          this.orderForm.contract_ids.includes(contract.id)
        );
        
        selectedContracts.forEach(contract => {
          // 方式1：优先从demandUnitIds字段获取需求单位ID（这是主要方式）
          if (contract.demandUnitIds && Array.isArray(contract.demandUnitIds) && contract.demandUnitIds.length > 0) {
            contract.demandUnitIds.forEach(id => {
              if (id && !demandUnitIds.includes(id)) {
                demandUnitIds.push(id);
              }
            });
          }
          // 方式2：如果没有demandUnitIds，从demandUnits中提取ID
          else if (contract.demandUnits && contract.demandUnits.length > 0) {
            contract.demandUnits.forEach(unit => {
              if (unit.id && !demandUnitIds.includes(unit.id)) {
                demandUnitIds.push(unit.id);
              }
            });
          }
          // 方式3：最后从contractParties中获取CUSTOMER类型的单位ID（作为兜底）
          else if (contract.contractParties && contract.contractParties.length > 0) {
            contract.contractParties.forEach(party => {
              if (party.partyType === 'CUSTOMER' && party.unitInfo && party.unitInfo.id) {
                if (!demandUnitIds.includes(party.unitInfo.id)) {
                  demandUnitIds.push(party.unitInfo.id);
                }
              }
            });
          }
        });
      } else if (this.isEditMode && this.orderForm.contract_id) {
        // 编辑模式：从单个合同获取（向后兼容）
        const contract = this.contractOptions.find(item => item.id === this.orderForm.contract_id);
        if (contract) {
          // 方式1：优先从demandUnitIds字段获取需求单位ID（这是主要方式）
          if (contract.demandUnitIds && Array.isArray(contract.demandUnitIds) && contract.demandUnitIds.length > 0) {
            contract.demandUnitIds.forEach(id => {
              if (id && !demandUnitIds.includes(id)) {
                demandUnitIds.push(id);
              }
            });
          }
          // 方式2：如果没有demandUnitIds，从demandUnits中提取ID
          else if (contract.demandUnits && contract.demandUnits.length > 0) {
            contract.demandUnits.forEach(unit => {
              if (unit.id && !demandUnitIds.includes(unit.id)) {
                demandUnitIds.push(unit.id);
              }
            });
          }
          // 方式3：最后从contractParties中获取CUSTOMER类型的单位ID（作为兜底）
          else if (contract.contractParties && contract.contractParties.length > 0) {
            contract.contractParties.forEach(party => {
              if (party.partyType === 'CUSTOMER' && party.unitInfo && party.unitInfo.id) {
                if (!demandUnitIds.includes(party.unitInfo.id)) {
                  demandUnitIds.push(party.unitInfo.id);
                }
              }
            });
          }
        }
      }
      
      // 去重
      return [...new Set(demandUnitIds)];
    },
    
    // 获取二级机构ID列表
    getSecondaryOrgIds() {
      if (this.orderForm.address_source === 'unit') {
        return this.getCreatorMatchingProjectIds();
      }

      if (this.orderForm.selected_project_id) {
        return [this.orderForm.selected_project_id];
      }

      return [];
    },

    getSelectedContracts() {
      if (this.orderForm.contract_ids && this.orderForm.contract_ids.length > 0) {
        const contracts = this.createMode === 'supplier'
          ? this.supplierContractOptions
          : this.contractOptions;
        return contracts.filter(contract => this.orderForm.contract_ids.includes(contract.id));
      }

      if (this.isEditMode && this.orderForm.contract_id) {
        const contract = this.contractOptions.find(item => item.id === this.orderForm.contract_id);
        return contract ? [contract] : [];
      }

      return [];
    },

    getAllProjectsFromContracts(contracts = []) {
      const projects = [];
      const seenIds = new Set();

      contracts.forEach(contract => {
        if (contract.projects && contract.projects.length > 0) {
          contract.projects.forEach(project => {
            if (project.id && !seenIds.has(project.id)) {
              seenIds.add(project.id);
              projects.push(project);
            }
          });
        }
      });

      return projects;
    },

    getDisplayProjectsFromContracts(contracts = []) {
      const allProjects = this.getAllProjectsFromContracts(contracts);

      if (this.isProjectPersonnel) {
        return allProjects.filter(project =>
          this.currentUserProjectIds.some(id => Number(id) === Number(project.id))
        );
      }

      return allProjects;
    },

    getDisplayProjects() {
      return this.getDisplayProjectsFromContracts(this.getSelectedContracts());
    },

    getCreatorMatchingProjectIds() {
      if (!this.isProjectPersonnel) {
        return [];
      }

      return this.getDisplayProjects()
        .map(project => project.id)
        .filter(id => id != null);
    },

    buildProjectFullAddress(project) {
      if (!project) {
        return '';
      }

      let address = '';
      if (project.provinceName) {
        address += project.provinceName;
      }
      if (project.cityName && project.cityName !== project.provinceName) {
        address += project.cityName;
      }
      if (project.districtName) {
        address += project.districtName;
      }
      if (project.detailAddress) {
        address += project.detailAddress;
      }
      return address;
    },
    
    // 根据合同ID获取合同名称
    getContractNameById(contractId) {
      const contract = this.contractOptions.find(item => item.id === contractId);
      return contract ? contract.contractName : '未知合同';
    },
    
    // 根据合同ID获取合同编号
    getContractNumberById(contractId) {
      const contract = this.contractOptions.find(item => item.id === contractId);
      return contract ? (contract.contractNumber || contractId) : '未知';
    },
    
    // 移除材料项
    removeMaterialItem(row) {
      this.$confirm('确认删除该材料项?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const index = this.orderForm.order_items.indexOf(row);
        if (index > -1) {
          this.orderForm.order_items.splice(index, 1);
        }
        this.recalculateTotal();
        this.$message.success('已删除材料项');
      }).catch(() => {});
    },
    
    // 关闭对话框
    handleDialogClose() {
      this.$confirm('确认关闭？未保存的数据将会丢失', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.createOrderDialogVisible = false;
        this.resetOrderForm();
      }).catch(() => {});
    },
    
    // 显示转发订单对话框
    async showForwardOrderDialog() {
      this.forwardOrderDialogVisible = true;
      this.resetForwardForm();
      this.loadExecutingContracts();
      
      // 先加载同单位联系人列表，加载完成后再设置默认联系人
      await this.loadUnitContacts();
      // 联系人列表加载完成后，设置当前用户为默认选中
      this.loadCurrentUserContactForForward();
    },
    
    // 重置转发表单
    resetForwardForm() {
      this.forwardForm = {
        contractIds: [], // 多选合同ID数组
        orderId: '',
        orderTitle: '',
        deliveryAddress: '',
        demandContactPerson: '',
        demandContactPhone: ''
      };
      this.selectedContracts = [];
      this.selectedOrder = null;
      this.productComparison = [];
      this.validationResult = {
        isValid: false,
        message: ''
      };
      this.supplierGroups = [];
      this.hasMultipleSuppliers = false;
      this.forwardSelectedContactId = null; // 重置联系人选择
      this.forwardSelectedSupplierContactId = null; // 重置供应商联系人选择
      this.forwardSupplierUnitContacts = []; // 清空供应商联系人列表
      if (this.$refs.forwardForm) {
        this.$refs.forwardForm.resetFields();
      }
    },
    
    // 加载执行中的采购合同
    loadExecutingContracts() {
      const params = {
        status: 'EXECUTING' // 只查询执行中的合同
      };
      
      query_purchase_contracts_all(this.currentUnitId, params)
        .then(response => {
          if (response.data && response.data.success) {
            let contractList = response.data.data || [];
            if (!Array.isArray(contractList)) {
              contractList = contractList ? [contractList] : [];
            }
            
            // 过滤掉供应商是当前登录用户所属单位的合同（不能自己转发给自己）
            const filteredContracts = contractList.filter(contract => {
              // 获取供应商ID
              let supplierId = null;
              
              // 方式1：从supplierUnitId字段获取
              if (contract.supplierUnitId) {
                supplierId = contract.supplierUnitId;
              }
              // 方式2：从supplierUnit对象获取
              else if (contract.supplierUnit && contract.supplierUnit.id) {
                supplierId = contract.supplierUnit.id;
              }
              // 方式3：从contractParties中获取SUPPLIER类型的单位ID
              else if (contract.contractParties && contract.contractParties.length > 0) {
                const supplierParty = contract.contractParties.find(party => party.partyType === 'SUPPLIER');
                if (supplierParty) {
                  supplierId = supplierParty.unitInfoId || (supplierParty.unitInfo && supplierParty.unitInfo.id);
                }
              }
              
              // 如果供应商ID等于当前用户单位ID，则过滤掉
              return supplierId !== null && supplierId !== this.currentUnitId;
            });
            
            this.executingContracts = filteredContracts;
            if (filteredContracts.length === 0) {
              this.$message.warning('未找到可转发的执行中的采购合同（已过滤掉供应商为当前单位的合同）');
            }
          } else {
            this.$message.error(response.data?.message || '获取执行中的采购合同失败');
          }
        })
        .catch(error => {
          console.error('获取执行中的采购合同失败:', error);
          this.$message.error('获取执行中的采购合同失败，请稍后重试');
        });
    },
    
    // 处理多选合同变化
    handleMultipleContractSelect(contractIds) {
      if (!contractIds || contractIds.length === 0) {
        this.selectedContracts = [];
        this.loadUnitContacts(); // 清空合同时恢复为需求单位用户
        this.loadSalesOrders();
        return;
      }
      
      // 加载多个合同的详情
      this.loadMultipleContractDetails(contractIds);
    },
    
    // 加载多个合同详情
    async loadMultipleContractDetails(contractIds) {
      try {
        const contractPromises = contractIds.map(contractId =>
          getContractById(contractId).then(response => {
            if (response.data && response.data.success) {
              return response.data.data;
            } else {
              throw new Error(`获取合同${contractId}详情失败`);
            }
          })
        );

        const contracts = await Promise.all(contractPromises);
        
        // 验证合同：检查需求单位是否等于供应商单位
        for (const contract of contracts) {
          const supplierUnitId = contract.supplierUnitId || contract.supplierUnit?.id;
          const demandUnitIds = contract.demandUnitIds || (contract.demandUnits ? contract.demandUnits.map(u => u.id) : []);
          
          // 检查需求单位ID列表中是否包含供应商单位ID
          if (supplierUnitId && demandUnitIds.includes(supplierUnitId)) {
            const supplierName = contract.supplierUnit?.unitName || '未知供应商';
            const contractName = contract.contractName || contract.id;
            this.$message.error(`合同"${contractName}"的需求单位与供应商单位相同（${supplierName}），无法转发订单`);
            // 清空选中的合同
            this.forwardForm.contractIds = [];
            this.selectedContracts = [];
            return;
          }
        }
        
        this.selectedContracts = contracts;
        
        // 加载供应商单位的联系人列表（如果有选中的合同）
        if (contracts.length > 0) {
          const firstContract = contracts[0];
          const supplierUnitId = firstContract.supplierUnitId || firstContract.supplierUnit?.id;
          if (supplierUnitId) {
            await this.loadForwardSupplierUnitContacts(supplierUnitId);
          }
          // 根据合同是否关联项目，加载对应的需求方联系人（项目内用户或需求单位用户）
          await this.loadUnitContacts(firstContract);
        }

this.loadSalesOrders();

        // 如果已经选择了订单，重新生成标题、验证产品并更新联系人信息
        if (this.selectedOrder) {
this.generateForwardOrderTitle();
          this.validateProducts();
          // 更新联系人信息（如果合同有联系人信息，优先使用）
          this.fillOrderBasicInfo();
        }
      } catch (error) {
        console.error('获取合同详情失败:', error);
        this.$message.error('获取合同详情失败');
      }
    },
    
    // 加载销售订单列表
    loadSalesOrders() {
      this.salesOrdersLoading = true;
      const params = {
        page: 0,
        size: 100 // 获取更多销售订单
      };
      
      query_sales_orders(params)
        .then(response => {
          if (response.data && response.data.success) {
            const responseData = response.data.data;
            let orderList = [];
            
            if (responseData.content && Array.isArray(responseData.content)) {
              orderList = responseData.content;
            } else if (responseData.data && Array.isArray(responseData.data)) {
              orderList = responseData.data;
            } else if (Array.isArray(responseData)) {
              orderList = responseData;
            }
            
            this.salesOrders = orderList;
            if (orderList.length === 0) {
              this.$message.warning('未找到可转发的销售订单');
            }
            
            // 如果有已选中的订单，在表格中高亮显示
            this.$nextTick(() => {
              if (this.forwardForm.orderId && this.$refs.orderSelectTable) {
                const selectedOrder = orderList.find(order => order.id === this.forwardForm.orderId);
                if (selectedOrder) {
                  this.$refs.orderSelectTable.setCurrentRow(selectedOrder);
                }
              }
            });
          } else {
            this.$message.error(response.data?.message || '获取销售订单失败');
          }
          this.salesOrdersLoading = false;
        })
        .catch(error => {
          console.error('获取销售订单失败:', error);
          this.$message.error('获取销售订单失败，请稍后重试');
          this.salesOrdersLoading = false;
        });
    },
    
    // 处理订单选择
    handleOrderSelect(orderId) {
if (!orderId) {
        this.selectedOrder = null;
        this.validateProducts();
        return;
      }
      
      // 获取订单详情
      get_order_detail(orderId)
        .then(response => {
          if (response.data && response.data.success) {
            this.selectedOrder = response.data.data;
            this.fillOrderBasicInfo();
this.validateProducts();
          } else {
            this.$message.error('获取订单详情失败');
          }
        })
        .catch(error => {
          console.error('获取订单详情失败:', error);
          this.$message.error('获取订单详情失败');
        });
    },
    
    // 处理表格订单选择（从表格行选择）
    handleOrderTableSelect(currentRow) {
      if (currentRow && currentRow.id) {
        this.forwardForm.orderId = currentRow.id;
        this.handleOrderSelect(currentRow.id);
      } else {
        this.forwardForm.orderId = '';
        this.handleOrderSelect('');
      }
    },
    
    // 清除选中的订单
    clearSelectedOrder() {
      this.forwardForm.orderId = '';
      this.handleOrderSelect('');
      // 清除表格的选中状态
      this.$nextTick(() => {
        if (this.$refs.orderSelectTable) {
          this.$refs.orderSelectTable.setCurrentRow(null);
        }
      });
    },
    
    // 获取订单表格行的样式类名（用于高亮选中的行）
    getOrderRowClassName({ row }) {
      if (row.id === this.forwardForm.orderId) {
        return 'selected-order-row';
      }
      return '';
    },
    
    // 填充订单基本信息
    fillOrderBasicInfo() {
      if (!this.selectedOrder) return;
      
      // 按照原来的规则生成订单标题（基于选择的合同）
      this.generateForwardOrderTitle();
      
      // 填充收货地址（保持原订单地址）
      this.forwardForm.deliveryAddress = this.selectedOrder.contactAddress || this.selectedOrder.deliveryAddress || '';
      
      // 只有在用户还没有选择联系人时，才自动填充联系人信息
      if (!this.forwardSelectedContactId && (!this.forwardForm.demandContactPerson || !this.forwardForm.demandContactPhone)) {
        // 优先使用合同的contactPerson和contactPhone字段（如果存在且不为空）
        if (this.selectedContracts && this.selectedContracts.length > 0) {
          const firstContract = this.selectedContracts[0];
          if (firstContract.contactPerson && firstContract.contactPhone) {
            // 如果合同联系人不为空，使用合同联系人信息
            this.forwardForm.demandContactPerson = firstContract.contactPerson;
            this.forwardForm.demandContactPhone = firstContract.contactPhone;
            
            // 将合同联系人添加到联系人列表中（如果不存在），并获取联系人ID
            const contactId = this.addContractContactToUnitContacts(
              firstContract.contactPerson,
              firstContract.contactPhone,
              firstContract.id
            );
            
            if (contactId) {
              this.forwardSelectedContactId = contactId;
}
          } else {
            // 如果合同联系人为空，按照之前的逻辑处理（使用当前登录人）
            this.loadCurrentUserContactForForward();
          }
        } else {
          // 如果没有选择合同，按照之前的逻辑处理（使用当前登录人）
          this.loadCurrentUserContactForForward();
        }
      }
      // 如果用户已经选择了联系人，则保持用户的选择
      
      // 填充供应商联系人信息（只有在用户还没有选择供应商联系人时，才自动填充）
      if (!this.forwardSelectedSupplierContactId && (!this.forwardForm.supplierContactPerson || !this.forwardForm.supplierContactPhone)) {
        if (this.selectedContracts && this.selectedContracts.length > 0) {
          const firstContract = this.selectedContracts[0];
          if (firstContract.supplierUnit) {
            // 如果供应商单位有联系人信息，使用供应商单位的联系人信息
            if (firstContract.supplierUnit.contactPerson && firstContract.supplierUnit.phone) {
              this.forwardForm.supplierContactPerson = firstContract.supplierUnit.contactPerson;
              this.forwardForm.supplierContactPhone = firstContract.supplierUnit.phone;
              
              // 尝试在供应商联系人列表中匹配并选中
              if (this.forwardSupplierUnitContacts.length > 0) {
                const matchedContact = this.forwardSupplierUnitContacts.find(contact => {
                  const contactName = contact.realName || contact.real_name || contact.username || '';
                  const contactPhone = contact.phone || contact.contact_phone || '';
                  return (contactName === firstContract.supplierUnit.contactPerson) || 
                         (contactPhone === firstContract.supplierUnit.phone);
                });
                
                if (matchedContact) {
                  this.forwardSelectedSupplierContactId = matchedContact.id || matchedContact.userId;
}
              }
            }
          }
        }
      }
    },
    
    // 加载当前用户信息并填充默认联系人（转发订单专用）
    loadCurrentUserContactForForward() {
      try {
        const userInfo = getUserInfo();
        if (userInfo) {
          // 设置当前用户为默认选中
          const currentUserId = userInfo.id || userInfo.userId;
          if (currentUserId) {
            const currentUserInList = this.unitContacts.find(contact => 
              (contact.id || contact.userId) === currentUserId
            );
            if (currentUserInList) {
              this.forwardSelectedContactId = currentUserId;
              // 手动触发联系人选择变化，确保电话被填充
              this.handleForwardContactChange(currentUserId);
} else {
              // 如果联系人列表中找不到当前用户，直接填充用户信息
              const realName = userInfo.realName || userInfo.real_name || userInfo.username || '';
              const phone = userInfo.phone || userInfo.contact_phone || '';
              this.forwardForm.demandContactPerson = realName;
              this.forwardForm.demandContactPhone = phone;
}
          }
        }
      } catch (error) {
        console.error('加载当前用户联系人信息失败:', error);
      }
    },
    
    // 处理转发订单联系人选择变化
    handleForwardContactChange(contactId) {
      if (!contactId) {
        // 清空选择时，清空联系人信息
        this.forwardForm.demandContactPerson = '';
        this.forwardForm.demandContactPhone = '';
        return;
      }
      
      const selectedContact = this.unitContacts.find(contact => 
        (contact.id || contact.userId) === contactId
      );
      
      if (selectedContact) {
        const realName = selectedContact.realName || selectedContact.real_name || selectedContact.username || '';
        const phone = selectedContact.phone || selectedContact.contact_phone || '';
        
        this.forwardForm.demandContactPerson = realName;
        this.forwardForm.demandContactPhone = phone;
        
}
    },
    
    // 生成转发订单标题（按照原来的规则生成，避免重复"转发订单"）
    generateForwardOrderTitle() {
      if (!this.selectedContracts || this.selectedContracts.length === 0) {
        // 如果没有选择合同，使用原订单标题
        const originalTitle = this.selectedOrder.orderTitle || '';
        // 去掉原订单标题中所有重复的"转发订单 - "前缀（可能有多层嵌套），然后统一添加"转发订单 - "前缀
        const cleanTitle = originalTitle.replace(/^(转发订单 - )+/g, '');
        this.forwardForm.orderTitle = `转发订单 - ${cleanTitle || this.selectedOrder.orderId}`;
        return;
      }
      
      // 按照原来的规则生成标题：需求方单位名称（优先用简称）+ 合同名称（去掉"合同"两字）+ "采购订单"
      const firstContract = this.selectedContracts[0];
      
      // 获取客户单位名称（使用contractUnit）
      let customerUnitName = '';
      if (firstContract.contractUnit) {
        customerUnitName = firstContract.contractUnit.unitShortName || firstContract.contractUnit.unitName || '';
      } else if (firstContract.contractParties) {
        const customerParty = firstContract.contractParties.find(party => party.partyType === 'CUSTOMER');
        if (customerParty && customerParty.unitInfo) {
          customerUnitName = customerParty.unitInfo.unitShortName || customerParty.unitInfo.unitName || '';
        }
      }
      
      // 生成标题
      let newTitle = '';
      if (this.selectedContracts.length === 1) {
        const contractName = (firstContract.contractName || '').replace(/合同$/g, '');
        newTitle = `${customerUnitName}${contractName}采购订单`;
      } else {
        newTitle = `${customerUnitName}多合同采购订单`;
      }
      
      // 无论原订单标题是否包含"转发订单"，都统一添加"转发订单 - "前缀，确保只出现一次
      this.forwardForm.orderTitle = `转发订单 - ${newTitle}`;
    },
    
    // 验证产品清单
    validateProducts() {
      if (!this.selectedContracts || this.selectedContracts.length === 0 || !this.selectedOrder) {
        this.validationResult = {
          isValid: false,
          message: '请先选择合同和订单'
        };
        this.productComparison = [];
        return;
      }
      
      // 合并所有合同的产品清单
      const allContractItems = [];
      this.selectedContracts.forEach(contract => {
        if (contract.contractItems && contract.contractItems.length > 0) {
          contract.contractItems.forEach(item => {
            // 添加合同来源信息
            allContractItems.push({
              ...item,
              sourceContractId: contract.id,
              sourceContractName: contract.contractName
            });
          });
        }
      });
      
// 获取订单产品清单
      const orderItems = this.selectedOrder.orderItems || [];
      
      if (orderItems.length === 0) {
        this.validationResult = {
          isValid: false,
          message: '选中的订单没有产品清单'
        };
        this.productComparison = [];
        return;
      }
      
      // 创建产品对比数据
      const comparison = [];
      let allValid = true;
      const warningMessages = [];
      const errorMessages = [];
      
      orderItems.forEach(orderItem => {
        // 查找合同中的对应产品
        const contractItem = allContractItems.find(contractItem => 
          this.isSameProduct(orderItem, contractItem)
        );
        
if (!contractItem) {
          // 产品在合同中不存在，只警告，不阻止转发
          warningMessages.push(`产品"${orderItem.productName || orderItem.materialsName}"与所选合同产品规格型号不符，将不会包含在转发订单中`);
          comparison.push({
            productName: orderItem.productName || orderItem.materialsName || '未命名产品',
            specification: orderItem.specification || orderItem.materialsSpec || '',
            materialRequirement: orderItem.materialRequirement || orderItem.material_requirement || '',
            unit: orderItem.unit || '个',
            forwardQuantity: parseFloat(orderItem.quantity) || 0,
            contractQuantity: 0,
            forwardPrice: parseFloat(orderItem.taxInclusiveUnitPrice || orderItem.price || 0),
            contractPrice: 0,
            isValid: false,
            sourceContractId: null,
            sourceContractName: null,
            supplierName: '未知供应商',
            supplierId: null,
            originalOrderItem: orderItem,
            contractItem: null
          });
        } else {
          const orderQuantity = parseFloat(orderItem.quantity) || 0;
          const contractQuantity = parseFloat(contractItem.quantity) || 0;
          const isValid = contractQuantity >= orderQuantity;
          
          if (!isValid) {
            allValid = false;
            errorMessages.push(`产品"${orderItem.productName || orderItem.materialsName}"合同数量不足（需要${orderQuantity}，可用${contractQuantity}）`);
          }
          
          // 获取供应商信息
          // 方式1：从合同产品项直接获取
          let supplierName = contractItem.supplierUnit?.unitName || contractItem.supplierUnit?.unit_name;
          
          // 方式2：如果产品项没有供应商信息，从合同获取
          if (!supplierName) {
            const contract = this.selectedContracts.find(c => c.id === contractItem.sourceContractId);
            supplierName = contract?.supplierUnit?.unitName || contract?.supplierUnit?.unit_name;
          }
          
          // 方式3：如果还是没有，从合同的其他字段获取
          if (!supplierName) {
            const contract = this.selectedContracts.find(c => c.id === contractItem.sourceContractId);
            supplierName = contract?.supplierUnitName || contract?.supplier_unit_name;
          }
          
          // 如果还是没有，使用默认值
          if (!supplierName) {
            supplierName = '未知供应商';
          }
          
          // 获取供应商ID
          const contract = this.selectedContracts.find(c => c.id === contractItem.sourceContractId);
          const supplierId = contract?.supplierUnitId || contract?.supplier_unit_id || contractItem.supplierUnitId || contractItem.supplier_unit_id;
          
comparison.push({
            productName: orderItem.productName || orderItem.materialsName || '未命名产品',
            specification: orderItem.specification || orderItem.materialsSpec || '',
            materialRequirement: orderItem.materialRequirement || orderItem.material_requirement || '',
            unit: orderItem.unit || '个',
            forwardQuantity: orderQuantity,
            contractQuantity: contractQuantity,
            forwardPrice: parseFloat(orderItem.taxInclusiveUnitPrice || orderItem.price || 0),
            contractPrice: this.getContractUnitPrice(contractItem),
            isValid: isValid,
            sourceContractId: contractItem.sourceContractId, // 来源合同ID
            sourceContractName: contractItem.sourceContractName, // 来源合同名称
            supplierName: supplierName, // 供应商名称
            supplierId: supplierId, // 供应商ID
            originalOrderItem: orderItem, // 保存原始订单项数据
            contractItem: contractItem // 保存合同项数据
          });
        }
      });
      
      this.productComparison = comparison;
      
      // 构建提示信息
      let message = '';
      if (warningMessages.length > 0 && errorMessages.length > 0) {
        message = `注意：${warningMessages.join('；')}；警告：${errorMessages.join('；')}`;
      } else if (warningMessages.length > 0) {
        message = `注意：${warningMessages.join('；')}。仅转发合同中存在的产品。`;
      } else if (errorMessages.length > 0) {
        message = `警告：${errorMessages.join('；')}`;
      } else {
        message = '产品清单验证通过';
      }
      
      // 只要有至少一个产品匹配且数量足够，就允许转发
      const hasValidProducts = comparison.some(item => item.contractItem && item.isValid);
      
      this.validationResult = {
        isValid: hasValidProducts || (allValid && warningMessages.length === 0),
        message: message || '产品清单验证通过'
      };
      
      // 如果有有效产品，按供应商分组
      if (hasValidProducts) {
        this.groupProductsBySupplier();
      }
    },
    
    // 按供应商分组产品
    groupProductsBySupplier() {
      const supplierMap = new Map();
      
      // 只处理合同和订单都有的有效产品（contractItem 不为 null 且 isValid）
      this.productComparison.filter(item => item.contractItem && item.isValid).forEach(item => {
        const supplierId = item.supplierId;
        const supplierName = item.supplierName;
        
        if (!supplierMap.has(supplierId)) {
          supplierMap.set(supplierId, {
            supplierId: supplierId,
            supplierName: supplierName,
            products: []
          });
        }
        
        supplierMap.get(supplierId).products.push(item);
      });
      
      this.supplierGroups = Array.from(supplierMap.values());
      this.hasMultipleSuppliers = this.supplierGroups.length > 1;
      
},
    
    // 处理需求方选择变化
    handleCustomerChange() {
      // 更新订单分组预览
      this.updateGroupedOrders();
    },
    
    // 更新订单分组预览
    updateGroupedOrders() {
      if (!this.hasMultipleCustomers || !this.productComparison.length) {
        this.groupedOrders = [];
        return;
      }
      
      // 按需求方分组
      const groups = {};
      
      this.productComparison.forEach(item => {
        if (!item.selectedCustomerId) return;
        
        if (!groups[item.selectedCustomerId]) {
          const customer = this.availableCustomers.find(c => c.id === item.selectedCustomerId);
          groups[item.selectedCustomerId] = {
            customerId: item.selectedCustomerId,
            customerName: customer?.unitName || customer?.unit_name || '未知需求方',
            items: []
          };
        }
        
        groups[item.selectedCustomerId].items.push({
          productName: item.productName,
          specification: item.specification,
          quantity: item.forwardQuantity,
          unit: item.unit,
          price: item.contractPrice,
          totalPrice: item.contractPrice * item.forwardQuantity,
          originalOrderItem: item.originalOrderItem,
          contractItem: item.contractItem
        });
      });
      
      this.groupedOrders = Object.values(groups);
      
},
    
    // 产品匹配字段规范化（忽略标点符号，只比对文字和数字）
    normalizeProductMatchField(value) {
      let normalized = String(value || '').toLowerCase().trim();
      if (normalized === '-' || normalized === '—' || normalized === '/') return '';
      // 统一各种乘号为 * （×、✖、✕、﹡、＊ 等全角/特殊乘号）
      normalized = normalized.replace(/[×✖✕﹡＊]/g, '*');
      // 数字之间的 x 视为乘号（如 6x0.8 → 6*0.8）
      normalized = normalized.replace(/(\d)\s*x\s*(\d)/g, '$1*$2');
      // 统一全角数字为半角
      normalized = normalized.replace(/[０-９]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0));
      // 统一全角字母为半角
      normalized = normalized.replace(/[Ａ-Ｚａ-ｚ]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0));
      // 去除所有标点符号（只保留中文字、字母、数字、*）
      normalized = normalized.replace(/[^\u4e00-\u9fa5a-z0-9*≥≤]/g, '');
      return normalized;
    },

    // 获取合同产品含税单价（用于对比展示）
    getContractUnitPrice(contractItem) {
      if (!contractItem) return 0;
      const pricingMethod = contractItem.pricingMethod || 'FIXED';
      if (pricingMethod === 'FLOATING') {
        const basePrice = parseFloat(contractItem.taxInclusiveBasePrice);
        const floatingPrice = parseFloat(contractItem.taxInclusiveFloatingPrice || 0);
        if (!isNaN(basePrice)) {
          return basePrice + (isNaN(floatingPrice) ? 0 : floatingPrice);
        }
      }
      const unitPrice = parseFloat(
        contractItem.taxInclusiveUnitPrice ?? contractItem.taxExclusiveUnitPrice ?? 0
      );
      return isNaN(unitPrice) ? 0 : unitPrice;
    },

    // 判断是否为同一产品（名称 + 规格 + 材质要求）
    isSameProduct(orderItem, contractItem) {
      const orderName = this.normalizeProductMatchField(
        orderItem.productName || orderItem.materialsName
      );
      const contractName = this.normalizeProductMatchField(
        contractItem.productName || contractItem.materials_name
      );
      const orderSpec = this.normalizeProductMatchField(
        orderItem.specification || orderItem.materialsSpec
      );
      const contractSpec = this.normalizeProductMatchField(
        contractItem.specification || contractItem.materials_spec
      );
      const orderMaterial = this.normalizeProductMatchField(
        orderItem.materialRequirement || orderItem.material_requirement
      );
      const contractMaterial = this.normalizeProductMatchField(
        contractItem.materialRequirement || contractItem.material_requirement
      );

      return orderName === contractName
        && orderSpec === contractSpec
        && orderMaterial === contractMaterial;
    },
    
    // 提交转发订单
    submitForwardOrder() {
      this.$refs.forwardForm.validate(valid => {
        if (!valid) {
          return false;
        }
        
        if (!this.validationResult.isValid) {
          this.$message.error(this.validationResult.message);
          return false;
        }
        
        this.forwardSubmitLoading = true;
        
        // 根据是否有多个供应商决定创建方式
        if (this.hasMultipleSuppliers) {
          // 多供应商：为每个供应商创建单独的订单
          this.createMultipleOrdersBySupplier();
        } else {
          // 单供应商：创建单个订单
          this.createSingleOrder();
        }
      });
    },
    
    // 为每个供应商创建单独的订单
    createMultipleOrdersBySupplier() {
      try {
        // 先验证是否有有效产品
        const validGroups = this.supplierGroups.filter(group => 
          group.products && group.products.some(item => item.contractItem && item.isValid)
        );
        
        if (validGroups.length === 0) {
          this.$message.error('没有可转发的有效产品，请检查产品清单');
          this.forwardSubmitLoading = false;
          return;
        }
        
        const orderPromises = validGroups.map(group => {
          const forwardOrderData = this.buildForwardOrderDataForSupplier(group);
return create_order(forwardOrderData);
        });
        
        Promise.all(orderPromises)
          .then(responses => {
            const successCount = responses.filter(response => response.data && response.data.success).length;
            const totalCount = responses.length;
            
            if (successCount === totalCount) {
              this.$message.success(`成功创建 ${totalCount} 个转发订单（按供应商分组）`);
            } else {
              this.$message.warning(`成功创建 ${successCount}/${totalCount} 个转发订单`);
            }
            
            this.forwardOrderDialogVisible = false;
            this.resetForwardForm();
            this.getOrderList(); // 刷新订单列表
            this.forwardSubmitLoading = false;
          })
          .catch(error => {
            console.error('批量创建订单失败:', error);
            this.$message.error('批量创建订单失败: ' + (error.message || '未知错误'));
            this.forwardSubmitLoading = false;
          });
      } catch (error) {
        console.error('构建转发订单数据失败:', error);
        this.$message.error(error.message || '没有可转发的有效产品，请检查产品清单');
        this.forwardSubmitLoading = false;
      }
    },
    
    // 创建单个订单
    createSingleOrder() {
      try {
        const forwardOrderData = this.buildForwardOrderData();
        
create_order(forwardOrderData)
          .then(response => {
            if (response.data && response.data.success) {
              this.$message.success('订单转发成功');
              this.forwardOrderDialogVisible = false;
              this.resetForwardForm();
              this.getOrderList(); // 刷新订单列表
            } else {
              this.$message.error(response.data?.message || '订单转发失败');
            }
            this.forwardSubmitLoading = false;
          })
          .catch(error => {
            console.error('订单转发失败:', error);
            this.$message.error('订单转发失败: ' + (error.message || '未知错误'));
            this.forwardSubmitLoading = false;
          });
      } catch (error) {
        console.error('构建转发订单数据失败:', error);
        this.$message.error(error.message || '没有可转发的有效产品，请检查产品清单');
        this.forwardSubmitLoading = false;
      }
    },
    
    
    // 创建多个订单
    createMultipleOrders() {
      const orderPromises = this.groupedOrders.map((group) => {
        const forwardOrderData = this.buildForwardOrderDataForGroup(group);
        return create_order(forwardOrderData);
      });
      
      Promise.all(orderPromises)
        .then(responses => {
          const successCount = responses.filter(response => response.data && response.data.success).length;
          const totalCount = responses.length;
          
          if (successCount === totalCount) {
            this.$message.success(`成功创建 ${successCount} 个转发订单`);
          } else {
            this.$message.warning(`成功创建 ${successCount}/${totalCount} 个转发订单`);
          }
          
          this.forwardOrderDialogVisible = false;
          this.resetForwardForm();
          this.getOrderList(); // 刷新订单列表
          this.forwardSubmitLoading = false;
        })
        .catch(error => {
          console.error('批量创建订单失败:', error);
          this.$message.error('批量创建订单失败: ' + (error.message || '未知错误'));
          this.forwardSubmitLoading = false;
        });
    },
    
    // 为特定需求方组构建转发订单数据
    // 保留原产品备注，并在下一行追加转发来源说明
    mergeForwardRemark(originalRemark, forwardingRemark) {
      const original = String(originalRemark || '').trim();
      const forwarding = String(forwardingRemark || '').trim();
      if (!original) return forwarding.slice(0, 500);
      if (!forwarding) return original.slice(0, 500);
      const available = 500 - original.length - 1;
      return available > 0 ? `${original}\n${forwarding.slice(0, available)}` : original.slice(0, 500);
    },

    buildForwardOrderDataForGroup(group) {
      const customer = this.availableCustomers.find(c => c.id === group.customerId);
      
      // 只使用合同和订单都有的有效产品（contractItem 不为 null 且 isValid）
      const validItems = group.items.filter(item => item.contractItem && item.originalOrderItem);
      
      if (validItems.length === 0) {
        throw new Error(`需求方 ${group.customerName} 没有可转发的有效产品`);
      }
      
      // 转换订单项格式，使用转发订单的数量和合同的价格
      const forwardOrderItems = validItems.map((item, index) => {
        const pricingMethod = item.contractItem?.pricingMethod || item.originalOrderItem.pricingMethod || 'FIXED';
        
        // 变动价订单：优先使用合同项的taxInclusiveBasePrice，如果没有则从原订单项获取
        let taxInclusiveBasePrice = null;
        if (pricingMethod === 'FLOATING') {
          const contractBasePrice = item.contractItem?.taxInclusiveBasePrice;
          const orderBasePrice = item.originalOrderItem?.taxInclusiveBasePrice;
          taxInclusiveBasePrice = contractBasePrice !== undefined && contractBasePrice !== null
            ? parseFloat(contractBasePrice)
            : (orderBasePrice !== undefined && orderBasePrice !== null ? parseFloat(orderBasePrice) : null);
          if (isNaN(taxInclusiveBasePrice)) {
            taxInclusiveBasePrice = null;
          }
        }
        
        // 变动价订单：优先使用合同项的taxInclusiveFloatingPrice，如果没有则从原订单项获取
        let taxInclusiveFloatingPrice = null;
        if (pricingMethod === 'FLOATING') {
          const contractFloatingPrice = item.contractItem?.taxInclusiveFloatingPrice;
          const orderFloatingPrice = item.originalOrderItem?.taxInclusiveFloatingPrice;
          taxInclusiveFloatingPrice = contractFloatingPrice !== undefined && contractFloatingPrice !== null
            ? parseFloat(contractFloatingPrice)
            : (orderFloatingPrice !== undefined && orderFloatingPrice !== null ? parseFloat(orderFloatingPrice) : null);
          if (isNaN(taxInclusiveFloatingPrice)) {
            taxInclusiveFloatingPrice = null;
          }
        }
        
        return {
          sequenceNumber: index + 1,
          productName: item.productName,
          specification: item.specification,
          materialRequirement: item.originalOrderItem.materialRequirement || '',
          unit: item.unit,
          quantity: parseFloat(item.quantity) || 0,
          pricingMethod: pricingMethod,
          taxExclusiveUnitPrice: parseFloat(item.contractItem?.taxExclusiveUnitPrice || item.contractItem?.unitPrice || 0),
          taxInclusiveUnitPrice: this.getContractUnitPrice(item.contractItem),
          taxInclusiveBasePrice: taxInclusiveBasePrice,
          taxInclusiveFloatingPrice: taxInclusiveFloatingPrice,
          taxInclusiveTotalAmount: this.getContractUnitPrice(item.contractItem) * (parseFloat(item.quantity) || 0),
          taxRate: parseFloat(item.contractItem?.taxRate || 13),
          remark: this.mergeForwardRemark(
            item.originalOrderItem.remark,
            `转发自订单号: ${this.selectedOrder.orderId || this.selectedOrder.orderTitle}`
          ),
          deliveryDate: item.originalOrderItem.deliveryDate || this.getDefaultDeliveryDate(),
          productSupplierId: this.selectedContracts[0]?.supplierUnitId || 1,
          forwardedOrderId: null // 订单项级别的forwardedOrderId保持为null
        };
      });
      
      // 计算总数量和总价
      const productsTotal = forwardOrderItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const priceTotal = forwardOrderItems.reduce((sum, item) => sum + (item.taxInclusiveTotalAmount || 0), 0);
      
      // 获取第一个合同的供应商信息（多个合同应该有相同的供应商）
      const firstContract = this.selectedContracts[0];
      
      return {
        orderTitle: `${this.forwardForm.orderTitle} - ${group.customerName}`,
        forwardedOrderId: this.selectedOrder.id, // 订单级别的forwardedOrderId，记录被转发的订单ID
        contractId: firstContract.id, // 保留第一个合同ID作为主合同
        contractIds: this.forwardForm.contractIds, // 使用所有选中的合同ID
        contractType: 'purchase',
        orderType: '01', // 直采
        supplierId: firstContract.supplierUnitId || 1,
        demandUnitIds: [group.customerId], // 使用特定需求方ID
        secondaryOrgIds: this.getSecondaryOrgIdsFromContract(),
        deliveryAddress: (firstContract.demandUnits && firstContract.demandUnits.length > 0 && firstContract.demandUnits[0].address) || this.forwardForm.deliveryAddress || '',
        demandContactPerson: customer?.contactPerson || this.forwardForm.demandContactPerson,
        demandContactPhone: customer?.phone || this.forwardForm.demandContactPhone,
        contactPerson: this.forwardForm.supplierContactPerson || firstContract.supplierUnit?.contactPerson || '',
        contactPhone: this.forwardForm.supplierContactPhone || firstContract.supplierUnit?.phone || '',
        contactAddress: (firstContract.demandUnits && firstContract.demandUnits.length > 0 && firstContract.demandUnits[0].address) || this.forwardForm.deliveryAddress || '',
        productsTotal: productsTotal,
        priceTotal: priceTotal,
        orderItems: forwardOrderItems
      };
    },
    
    // 构建转发订单数据
    buildForwardOrderData() {
      // 只使用产品对比中合同和订单都有的产品（contractItem 不为 null）
      const validProducts = this.productComparison.filter(item => item.contractItem && item.isValid);
      
      if (validProducts.length === 0) {
        throw new Error('没有可转发的有效产品');
      }
      
      // 转换订单项格式，使用转发订单的数量和合同的价格
      const forwardOrderItems = validProducts.map((product, index) => {
        const orderItem = product.originalOrderItem;
        const contractItem = product.contractItem;
        const pricingMethod = contractItem?.pricingMethod || orderItem.pricingMethod || 'FIXED';
        
        // 变动价订单：优先使用合同项的taxInclusiveBasePrice，如果没有则从原订单项获取
        let taxInclusiveBasePrice = null;
        if (pricingMethod === 'FLOATING') {
          const contractBasePrice = contractItem?.taxInclusiveBasePrice;
          const orderBasePrice = orderItem?.taxInclusiveBasePrice;
          taxInclusiveBasePrice = contractBasePrice !== undefined && contractBasePrice !== null
            ? parseFloat(contractBasePrice)
            : (orderBasePrice !== undefined && orderBasePrice !== null ? parseFloat(orderBasePrice) : null);
          if (isNaN(taxInclusiveBasePrice)) {
            taxInclusiveBasePrice = null;
          }
        }
        
        // 变动价订单：优先使用合同项的taxInclusiveFloatingPrice，如果没有则从原订单项获取
        let taxInclusiveFloatingPrice = null;
        if (pricingMethod === 'FLOATING') {
          const contractFloatingPrice = contractItem?.taxInclusiveFloatingPrice;
          const orderFloatingPrice = orderItem?.taxInclusiveFloatingPrice;
          taxInclusiveFloatingPrice = contractFloatingPrice !== undefined && contractFloatingPrice !== null
            ? parseFloat(contractFloatingPrice)
            : (orderFloatingPrice !== undefined && orderFloatingPrice !== null ? parseFloat(orderFloatingPrice) : null);
          if (isNaN(taxInclusiveFloatingPrice)) {
            taxInclusiveFloatingPrice = null;
          }
        }
        
        return {
          sequenceNumber: index + 1,
          productName: orderItem.productName || orderItem.materialsName || '',
          specification: orderItem.specification || orderItem.materialsSpec || '',
          materialRequirement: orderItem.materialRequirement || '',
          unit: orderItem.unit || '个',
          quantity: product.forwardQuantity,
          pricingMethod: pricingMethod,
          taxExclusiveUnitPrice: parseFloat(contractItem?.taxExclusiveUnitPrice || contractItem?.unitPrice || 0),
          taxInclusiveUnitPrice: this.getContractUnitPrice(contractItem),
          taxInclusiveBasePrice: taxInclusiveBasePrice,
          taxInclusiveFloatingPrice: taxInclusiveFloatingPrice,
          taxInclusiveTotalAmount: this.getContractUnitPrice(contractItem) * product.forwardQuantity,
          taxRate: parseFloat(contractItem?.taxRate || 13),
          remark: this.mergeForwardRemark(
            orderItem.remark,
            `转发自订单号: ${this.selectedOrder.orderId || this.selectedOrder.orderTitle}`
          ),
          deliveryDate: orderItem.deliveryDate || this.getDefaultDeliveryDate(),
          productSupplierId: product.supplierId || this.selectedContracts[0]?.supplierUnitId || 1,
          forwardedOrderId: null // 订单项级别的forwardedOrderId保持为null
        };
      });
      
      // 计算总数量和总价
      const productsTotal = forwardOrderItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const priceTotal = forwardOrderItems.reduce((sum, item) => sum + (item.taxInclusiveTotalAmount || 0), 0);
      
      // 获取第一个合同的供应商信息（多个合同应该有相同的供应商）
      const firstContract = this.selectedContracts[0];
      
      return {
        orderTitle: this.forwardForm.orderTitle,
        forwardedOrderId: this.selectedOrder.id, // 订单级别的forwardedOrderId，记录被转发的订单ID
        contractId: firstContract.id, // 保留第一个合同ID作为主合同
        contractIds: this.forwardForm.contractIds, // 使用所有选中的合同ID
        contractType: 'purchase',
        orderType: '01', // 直采
        supplierId: firstContract.supplierUnitId || 1,
        demandUnitIds: this.getDemandUnitIdsFromContract(),
        secondaryOrgIds: this.getSecondaryOrgIdsFromContract(),
        deliveryAddress: this.forwardForm.deliveryAddress,
        demandContactPerson: this.forwardForm.demandContactPerson,
        demandContactPhone: this.forwardForm.demandContactPhone,
        contactPerson: this.forwardForm.supplierContactPerson || firstContract.supplierUnit?.contactPerson || '',
        contactPhone: this.forwardForm.supplierContactPhone || firstContract.supplierUnit?.phone || '',
        contactAddress: (firstContract.demandUnits && firstContract.demandUnits.length > 0 && firstContract.demandUnits[0].address) || this.forwardForm.deliveryAddress || '',
        productsTotal: productsTotal,
        priceTotal: priceTotal,
        orderItems: forwardOrderItems
      };
    },
    
    // 从合同中获取需求单位ID列表（转发订单时从合同提取需求单位）
    getDemandUnitIdsFromContract() {
      const demandUnitIds = [];
      
      if (!this.selectedContracts || this.selectedContracts.length === 0) {
        return [];
      }
      
      // 从合同中提取需求单位ID，完全按照合同数据来
      this.selectedContracts.forEach(contract => {
        // 方式1：优先从demandUnitIds字段获取需求单位ID（这是主要方式）
        if (contract.demandUnitIds && Array.isArray(contract.demandUnitIds) && contract.demandUnitIds.length > 0) {
          contract.demandUnitIds.forEach(id => {
            if (id && !demandUnitIds.includes(id)) {
              demandUnitIds.push(id);
            }
          });
        }
        // 方式2：如果没有demandUnitIds，从demandUnits中提取ID
        else if (contract.demandUnits && contract.demandUnits.length > 0) {
          contract.demandUnits.forEach(unit => {
            const unitId = unit.id || unit.unitId;
            if (unitId && !demandUnitIds.includes(unitId)) {
              demandUnitIds.push(unitId);
            }
          });
        }
      });
      
      return demandUnitIds;
    },
    
    // 从原订单获取二级机构ID列表（转发订单时保持原订单的二级机构）
    getSecondaryOrgIdsFromContract() {
      const secondaryOrgIds = [];
      
      if (!this.selectedOrder) {
        // 如果原订单没有二级机构信息，则从多个合同中获取
        if (this.selectedContracts && this.selectedContracts.length > 0) {
          this.selectedContracts.forEach(contract => {
            if (contract.projects && contract.projects.length > 0) {
              contract.projects.forEach(project => {
                if (project.id && !secondaryOrgIds.includes(project.id)) {
                  secondaryOrgIds.push(project.id);
                }
              });
            }
          });
        }
        return secondaryOrgIds;
      }
      
      // 转发订单时，保持原订单的二级机构信息
      // 优先从订单对象的secondaryOrgIds获取
      if (this.selectedOrder.secondaryOrgIds && this.selectedOrder.secondaryOrgIds.length > 0) {
        return [...this.selectedOrder.secondaryOrgIds];
      }
      
      // 其次从secondaryOrgDetails获取
      if (this.selectedOrder.secondaryOrgDetails && this.selectedOrder.secondaryOrgDetails.length > 0) {
        this.selectedOrder.secondaryOrgDetails.forEach(org => {
          if (org.id && !secondaryOrgIds.includes(org.id)) {
            secondaryOrgIds.push(org.id);
          }
        });
        if (secondaryOrgIds.length > 0) {
          return secondaryOrgIds;
        }
      }
      
      // 最后从contractDetails中获取项目ID（作为备选）
      if (this.selectedOrder.contractDetails && this.selectedOrder.contractDetails.length > 0) {
        this.selectedOrder.contractDetails.forEach(contract => {
          if (contract.projects && contract.projects.length > 0) {
            contract.projects.forEach(project => {
              if (project.id && !secondaryOrgIds.includes(project.id)) {
                secondaryOrgIds.push(project.id);
              }
            });
          }
        });
        if (secondaryOrgIds.length > 0) {
          return secondaryOrgIds;
        }
      }
      
      // 如果原订单没有二级机构信息，则从多个合同中获取
      if (this.selectedContracts && this.selectedContracts.length > 0) {
        this.selectedContracts.forEach(contract => {
          if (contract.projects && contract.projects.length > 0) {
            contract.projects.forEach(project => {
              if (project.id && !secondaryOrgIds.includes(project.id)) {
                secondaryOrgIds.push(project.id);
              }
            });
          }
        });
      }
      
      return secondaryOrgIds;
    },
    
    // 为特定供应商构建转发订单数据
    buildForwardOrderDataForSupplier(supplierGroup) {
      // 只使用合同和订单都有的有效产品（contractItem 不为 null 且 isValid）
      const validProducts = supplierGroup.products.filter(item => item.contractItem && item.isValid);
      
      if (validProducts.length === 0) {
        throw new Error(`供应商 ${supplierGroup.supplierName} 没有可转发的有效产品`);
      }
      
      // 转换订单项格式，使用转发订单的数量和合同的价格
      const forwardOrderItems = validProducts.map((item, index) => {
        const pricingMethod = item.contractItem?.pricingMethod || item.originalOrderItem.pricingMethod || 'FIXED';
        
        // 变动价订单：优先使用合同项的taxInclusiveBasePrice，如果没有则从原订单项获取
        let taxInclusiveBasePrice = null;
        if (pricingMethod === 'FLOATING') {
          const contractBasePrice = item.contractItem?.taxInclusiveBasePrice;
          const orderBasePrice = item.originalOrderItem?.taxInclusiveBasePrice;
          taxInclusiveBasePrice = contractBasePrice !== undefined && contractBasePrice !== null
            ? parseFloat(contractBasePrice)
            : (orderBasePrice !== undefined && orderBasePrice !== null ? parseFloat(orderBasePrice) : null);
          if (isNaN(taxInclusiveBasePrice)) {
            taxInclusiveBasePrice = null;
          }
        }
        
        // 变动价订单：优先使用合同项的taxInclusiveFloatingPrice，如果没有则从原订单项获取
        let taxInclusiveFloatingPrice = null;
        if (pricingMethod === 'FLOATING') {
          const contractFloatingPrice = item.contractItem?.taxInclusiveFloatingPrice;
          const orderFloatingPrice = item.originalOrderItem?.taxInclusiveFloatingPrice;
          taxInclusiveFloatingPrice = contractFloatingPrice !== undefined && contractFloatingPrice !== null
            ? parseFloat(contractFloatingPrice)
            : (orderFloatingPrice !== undefined && orderFloatingPrice !== null ? parseFloat(orderFloatingPrice) : null);
          if (isNaN(taxInclusiveFloatingPrice)) {
            taxInclusiveFloatingPrice = null;
          }
        }
        
        return {
          sequenceNumber: index + 1,
          productName: item.originalOrderItem.productName || item.originalOrderItem.materialsName || '',
          specification: item.originalOrderItem.specification || item.originalOrderItem.materialsSpec || '',
          materialRequirement: item.originalOrderItem.materialRequirement || '',
          unit: item.originalOrderItem.unit || '个',
          quantity: item.forwardQuantity,
          pricingMethod: pricingMethod,
          taxExclusiveUnitPrice: parseFloat(item.contractItem?.taxExclusiveUnitPrice || item.contractItem?.unitPrice || 0),
          taxInclusiveUnitPrice: this.getContractUnitPrice(item.contractItem),
          taxInclusiveBasePrice: taxInclusiveBasePrice,
          taxInclusiveFloatingPrice: taxInclusiveFloatingPrice,
          taxInclusiveTotalAmount: this.getContractUnitPrice(item.contractItem) * item.forwardQuantity,
          taxRate: parseFloat(item.contractItem?.taxRate || 13),
          remark: this.mergeForwardRemark(
            item.originalOrderItem.remark,
            `转发自订单号: ${this.selectedOrder.orderId || this.selectedOrder.orderTitle}`
          ),
          deliveryDate: item.originalOrderItem.deliveryDate || this.getDefaultDeliveryDate(),
          productSupplierId: item.supplierId,
          forwardedOrderId: null // 订单项级别的forwardedOrderId保持为null
        };
      });
      
      // 计算总数量和总价
      const productsTotal = forwardOrderItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const priceTotal = forwardOrderItems.reduce((sum, item) => sum + (item.taxInclusiveTotalAmount || 0), 0);
      
      // 获取该供应商对应的合同信息
      const supplierContract = this.selectedContracts.find(contract => 
        contract.supplierUnitId === supplierGroup.supplierId
      );
      
      return {
        orderTitle: `${this.forwardForm.orderTitle} - ${supplierGroup.supplierName}`,
        forwardedOrderId: this.selectedOrder.id, // 订单级别的forwardedOrderId，记录被转发的订单ID
        contractId: supplierContract?.id || this.selectedContracts[0]?.id, // 使用该供应商的合同ID
        contractIds: supplierContract ? [supplierContract.id] : this.forwardForm.contractIds, // 使用该供应商的合同ID
        contractType: 'purchase',
        orderType: '01', // 直采
        supplierId: supplierGroup.supplierId,
        demandUnitIds: this.getDemandUnitIdsFromContract(),
        secondaryOrgIds: this.getSecondaryOrgIdsFromContract(),
        deliveryAddress: this.forwardForm.deliveryAddress,
        demandContactPerson: this.forwardForm.demandContactPerson,
        demandContactPhone: this.forwardForm.demandContactPhone,
        contactPerson: this.forwardForm.supplierContactPerson || supplierContract?.supplierUnit?.contactPerson || '',
        contactPhone: this.forwardForm.supplierContactPhone || supplierContract?.supplierUnit?.phone || '',
        contactAddress: (supplierContract?.demandUnits && supplierContract.demandUnits.length > 0 && supplierContract.demandUnits[0].address) || this.forwardForm.deliveryAddress || '',
        productsTotal: productsTotal,
        priceTotal: priceTotal,
        orderItems: forwardOrderItems
      };
    },
    
    // 关闭转发订单对话框
    handleForwardDialogClose() {
      this.$confirm('确认关闭？未保存的数据将会丢失', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.forwardOrderDialogVisible = false;
        this.resetForwardForm();
      }).catch(() => {});
    },
    
    // 处理地址来源选择变化
    handleAddressSourceChange(source) {
      if (this.isProjectPersonnel && source === 'unit') {
        return;
      }

      this.orderForm.address_source = source;

      if (source === 'unit' && this.orderForm.available_addresses.unit_address) {
        this.orderForm.contact_address = this.orderForm.available_addresses.unit_address;
      } else if (source === 'project') {
        const projectAddresses = this.orderForm.available_addresses.project_addresses || [];
        const selectedProject = projectAddresses.find(
          project => project.id === this.orderForm.selected_project_id
        ) || projectAddresses[0];

        if (selectedProject) {
          this.orderForm.selected_project_id = selectedProject.id;
          this.orderForm.contact_address = selectedProject.address;
        }
      }
    },

    // 使用指定地址
    useAddress(source) {
      this.handleAddressSourceChange(source);
    },

    // 使用指定项目地址
    useProjectAddress(project) {
      if (!project) {
        return;
      }

      this.orderForm.address_source = 'project';
      this.orderForm.selected_project_id = project.id;
      this.orderForm.contact_address = project.address;
    },
    
    // 加载当前用户信息并填充默认联系人
    loadCurrentUserContact() {
      try {
        const userInfo = getUserInfo();
        if (userInfo) {
          // 填充当前用户的姓名和电话作为默认值
          const realName = userInfo.realName || userInfo.real_name || userInfo.username || '';
          const phone = userInfo.phone || userInfo.contact_phone || '';
          
          this.orderForm.contract_unit_contact_person = realName;
          this.orderForm.contract_unit_phone = phone;
          
}
      } catch (error) {
        console.error('加载当前用户联系人信息失败:', error);
      }
    },
    
    // 加载同单位联系人列表
    // 将合同联系人添加到联系人列表中（如果不存在）
    addContractContactToUnitContacts(contactPerson, contactPhone, contractId) {
      if (!contactPerson || !contactPhone) {
        return null;
      }
      
      // 检查联系人列表中是否已存在该联系人（按姓名或电话匹配）
      const existingContact = this.unitContacts.find(contact => {
        const contactName = contact.realName || contact.real_name || contact.username || '';
        const contactPhoneNum = contact.phone || contact.contact_phone || '';
        return contactName === contactPerson || contactPhoneNum === contactPhone;
      });
      
      if (existingContact) {
        // 如果已存在，返回现有联系人的ID
        return existingContact.id || existingContact.userId;
      }
      
      // 如果不存在，创建一个新的联系人对象并添加到列表中
      const contractContactId = `contract_contact_${contractId || Date.now()}`;
      const newContact = {
        id: contractContactId,
        userId: contractContactId,
        realName: contactPerson,
        real_name: contactPerson,
        username: contactPerson,
        phone: contactPhone,
        contact_phone: contactPhone,
        isContractContact: true // 标记为合同联系人
      };
      
      // 添加到联系人列表的开头（优先显示）
      this.unitContacts.unshift(newContact);
return contractContactId;
    },
    
    // 从合同数据中提取项目ID列表（合同关联项目时用于筛选联系人）
    getProjectIdsFromContract(contract) {
      if (!contract) return [];
      if (contract.projects && Array.isArray(contract.projects) && contract.projects.length > 0) {
        return contract.projects.map(p => p.id).filter(id => id != null);
      }
      if (contract.projectIds && Array.isArray(contract.projectIds) && contract.projectIds.length > 0) {
        return contract.projectIds.filter(id => id != null);
      }
      return [];
    },
    
    async loadUnitContacts(contractOrProjectIds) {
      // contractOrProjectIds: 合同对象、项目ID数组，或空（使用需求单位）
      let projectIds = [];
      if (contractOrProjectIds) {
        if (Array.isArray(contractOrProjectIds)) {
          projectIds = contractOrProjectIds;
        } else {
          projectIds = this.getProjectIdsFromContract(contractOrProjectIds);
        }
      }
      
      try {
        let response;
        if (projectIds.length > 0) {
          // 合同关联了项目：只加载项目内用户（users表中project_ids包含项目ID的用户）
          response = await getUsersByProjectIds(projectIds);
} else {
          // 合同未关联项目：加载需求单位用户
          if (!this.currentUnitId) {
            console.warn('当前单位ID不存在，无法加载联系人列表');
            return;
          }
          response = await getUsersByUnitId(this.currentUnitId);
        }
        
        if (response.data && response.data.success) {
          const allUsers = response.data.data || [];
          this.unitContacts = allUsers;
// 如果当前选中的联系人不在新列表中，清空选择
          if (this.selectedContactId) {
            const stillInList = this.unitContacts.some(c => (c.id || c.userId) === this.selectedContactId);
            if (!stillInList) {
              this.selectedContactId = null;
            }
          }
          
          // 联系人列表加载完成后，设置当前用户为默认选中（仅用于普通订单创建）
          const userInfo = getUserInfo();
          if (userInfo) {
            const currentUserId = userInfo.id || userInfo.userId;
            if (currentUserId) {
              const currentUserInList = this.unitContacts.find(contact => 
                (contact.id || contact.userId) === currentUserId
              );
              if (currentUserInList) {
                if (this.createOrderDialogVisible && !this.forwardOrderDialogVisible) {
                  this.selectedContactId = currentUserId;
                  this.handleContactChange(currentUserId);
}
              }
            }
          }
        } else {
          console.error('获取需求方联系人列表失败:', response.data?.message);
        }
      } catch (error) {
        console.error('获取需求方联系人列表失败:', error);
      }
    },
    
    // 加载同单位联系人列表（编辑模式），支持根据合同项目筛选
    async loadUnitContactsForEdit(contract) {
      const projectIds = contract ? this.getProjectIdsFromContract(contract) : [];
      
      try {
        let response;
        if (projectIds.length > 0) {
          response = await getUsersByProjectIds(projectIds);
        } else {
          if (!this.currentUnitId) {
            console.warn('当前单位ID不存在，无法加载联系人列表');
            return;
          }
          response = await getUsersByUnitId(this.currentUnitId);
        }
        
        if (response.data && response.data.success) {
          const allUsers = response.data.data || [];
          this.unitContacts = allUsers;
// 尝试匹配当前订单中的联系人
          const currentContactPerson = this.orderForm.contract_unit_contact_person;
          const currentContactPhone = this.orderForm.contract_unit_phone;
          
          if (currentContactPerson || currentContactPhone) {
            const matchedContact = this.unitContacts.find(contact => {
              const contactName = contact.realName || contact.real_name || contact.username || '';
              const contactPhone = contact.phone || contact.contact_phone || '';
              return (contactName === currentContactPerson) || (contactPhone === currentContactPhone);
            });
            
            if (matchedContact) {
              this.selectedContactId = matchedContact.id || matchedContact.userId;
} else {
              this.selectedContactId = null;
}
          }
        } else {
          console.error('获取需求方联系人列表失败:', response.data?.message);
        }
      } catch (error) {
        console.error('获取需求方联系人列表失败:', error);
      }
    },
    
    // 处理联系人选择变化
    handleContactChange(contactId) {
      if (!contactId) {
        // 清空选择时，清空联系人信息
        this.orderForm.contract_unit_contact_person = '';
        this.orderForm.contract_unit_phone = '';
        return;
      }
      
      const selectedContact = this.unitContacts.find(contact => 
        (contact.id || contact.userId) === contactId
      );
      
      if (selectedContact) {
        const realName = selectedContact.realName || selectedContact.real_name || selectedContact.username || '';
        const phone = selectedContact.phone || selectedContact.contact_phone || '';
        
        this.orderForm.contract_unit_contact_person = realName;
        this.orderForm.contract_unit_phone = phone;
        
}
    },
    
    // 加载供应商单位联系人列表
    async loadSupplierUnitContacts(supplierUnitId) {
      if (!supplierUnitId) {
        console.warn('供应商单位ID不存在，无法加载联系人列表');
        this.supplierUnitContacts = [];
        this.selectedSupplierContactId = null;
        return;
      }
      
      try {
        const response = await getUsersByUnitId(supplierUnitId);
        if (response.data && response.data.success) {
          const allUsers = response.data.data || [];
          this.supplierUnitContacts = allUsers;
// 尝试匹配当前表单中的供应商联系人
          const currentContactPerson = this.orderForm.supplier_contact_person;
          const currentContactPhone = this.orderForm.supplier_phone;
          
          if (currentContactPerson || currentContactPhone) {
            const matchedContact = this.supplierUnitContacts.find(contact => {
              const contactName = contact.realName || contact.real_name || contact.username || '';
              const contactPhone = contact.phone || contact.contact_phone || '';
              return (contactName === currentContactPerson) || (contactPhone === currentContactPhone);
            });
            
            if (matchedContact) {
              this.selectedSupplierContactId = matchedContact.id || matchedContact.userId;
} else {
              // 如果没有匹配到，尝试使用第一个联系人作为默认值
              if (this.supplierUnitContacts.length > 0) {
                const firstContact = this.supplierUnitContacts[0];
                this.selectedSupplierContactId = firstContact.id || firstContact.userId;
                this.handleSupplierContactChange(this.selectedSupplierContactId);
} else {
                this.selectedSupplierContactId = null;
              }
            }
          } else {
            // 如果表单中没有联系人信息，使用第一个联系人作为默认值
            if (this.supplierUnitContacts.length > 0) {
              const firstContact = this.supplierUnitContacts[0];
              this.selectedSupplierContactId = firstContact.id || firstContact.userId;
              this.handleSupplierContactChange(this.selectedSupplierContactId);
} else {
              this.selectedSupplierContactId = null;
            }
          }
        } else {
          console.error('获取供应商单位联系人列表失败:', response.data?.message);
          this.supplierUnitContacts = [];
        }
      } catch (error) {
        console.error('获取供应商单位联系人列表失败:', error);
        this.supplierUnitContacts = [];
      }
    },
    
    // 处理供应商联系人选择变化
    handleSupplierContactChange(contactId) {
      if (!contactId) {
        // 清空选择时，清空联系人信息
        this.orderForm.supplier_contact_person = '';
        this.orderForm.supplier_phone = '';
        return;
      }
      
      const selectedContact = this.supplierUnitContacts.find(contact => 
        (contact.id || contact.userId) === contactId
      );
      
      if (selectedContact) {
        const realName = selectedContact.realName || selectedContact.real_name || selectedContact.username || '';
        const phone = selectedContact.phone || selectedContact.contact_phone || '';
        
        this.orderForm.supplier_contact_person = realName;
        this.orderForm.supplier_phone = phone;
        
}
    },
    
    // 加载转发订单的供应商单位联系人列表
    async loadForwardSupplierUnitContacts(supplierUnitId) {
      if (!supplierUnitId) {
        console.warn('供应商单位ID不存在，无法加载联系人列表');
        this.forwardSupplierUnitContacts = [];
        this.forwardSelectedSupplierContactId = null;
        return;
      }

      try {
        const response = await getUsersByUnitId(supplierUnitId);
        if (response.data && response.data.success) {
          const allUsers = response.data.data || [];
          this.forwardSupplierUnitContacts = allUsers;
// 尝试匹配当前表单中的供应商联系人
          const currentContactPerson = this.forwardForm.supplierContactPerson;
          const currentContactPhone = this.forwardForm.supplierContactPhone;

          if (currentContactPerson || currentContactPhone) {
            const matchedContact = this.forwardSupplierUnitContacts.find(contact => {
              const contactName = contact.realName || contact.real_name || contact.username || '';
              const contactPhone = contact.phone || contact.contact_phone || '';
              return (contactName === currentContactPerson) || (contactPhone === currentContactPhone);
            });

            if (matchedContact) {
              this.forwardSelectedSupplierContactId = matchedContact.id || matchedContact.userId;
} else {
              // 如果没有匹配到，尝试使用第一个联系人作为默认值
              if (this.forwardSupplierUnitContacts.length > 0) {
                const firstContact = this.forwardSupplierUnitContacts[0];
                this.forwardSelectedSupplierContactId = firstContact.id || firstContact.userId;
                this.handleForwardSupplierContactChange(this.forwardSelectedSupplierContactId);
} else {
                this.forwardSelectedSupplierContactId = null;
              }
            }
          } else {
            // 如果表单中没有联系人信息，使用第一个联系人作为默认值
            if (this.forwardSupplierUnitContacts.length > 0) {
              const firstContact = this.forwardSupplierUnitContacts[0];
              this.forwardSelectedSupplierContactId = firstContact.id || firstContact.userId;
              this.handleForwardSupplierContactChange(this.forwardSelectedSupplierContactId);
} else {
              this.forwardSelectedSupplierContactId = null;
            }
          }
        } else {
          console.error('获取转发订单供应商单位联系人列表失败:', response.data?.message);
          this.forwardSupplierUnitContacts = [];
        }
      } catch (error) {
        console.error('获取转发订单供应商单位联系人列表失败:', error);
        this.forwardSupplierUnitContacts = [];
      }
    },

    // 处理转发订单供应商联系人选择变化
    handleForwardSupplierContactChange(contactId) {
      if (!contactId) {
        // 清空选择时，清空联系人信息
        this.forwardForm.supplierContactPerson = '';
        this.forwardForm.supplierContactPhone = '';
        return;
      }

      const selectedContact = this.forwardSupplierUnitContacts.find(contact =>
        (contact.id || contact.userId) === contactId
      );

      if (selectedContact) {
        const realName = selectedContact.realName || selectedContact.real_name || selectedContact.username || '';
        const phone = selectedContact.phone || selectedContact.contact_phone || '';

        this.forwardForm.supplierContactPerson = realName;
        this.forwardForm.supplierContactPhone = phone;

}
    },
    
    // 收集可用地址信息
    collectAvailableAddresses(contractData) {
      const contracts = this.getSelectedContracts();
      const contractList = contracts.length > 0 ? contracts : (contractData ? [contractData] : []);
      const firstContract = contractList[0];

      let unitAddress = '';
      if (firstContract?.demandUnits?.length > 0) {
        unitAddress = firstContract.demandUnits[0].address || '';
      }

      const projectAddresses = this.getDisplayProjectsFromContracts(contractList)
        .map(project => ({
          id: project.id,
          projectName: project.projectName,
          address: this.buildProjectFullAddress(project)
        }))
        .filter(project => project.address);

      this.orderForm.available_addresses = {
        unit_address: unitAddress,
        project_addresses: projectAddresses
      };

      const savedAddress = this.orderForm.contact_address;
      const matchedProjectByAddress = projectAddresses.find(project => project.address === savedAddress);
      const currentProjectStillAvailable = projectAddresses.some(
        project => project.id === this.orderForm.selected_project_id
      );

      if (matchedProjectByAddress) {
        this.orderForm.address_source = 'project';
        this.orderForm.selected_project_id = matchedProjectByAddress.id;
        this.orderForm.contact_address = matchedProjectByAddress.address;
      } else if (!this.isProjectPersonnel && savedAddress && unitAddress && savedAddress === unitAddress) {
        this.orderForm.address_source = 'unit';
        this.orderForm.selected_project_id = null;
        this.orderForm.contact_address = unitAddress;
      } else if (projectAddresses.length > 0) {
        const defaultProject = currentProjectStillAvailable
          ? projectAddresses.find(project => project.id === this.orderForm.selected_project_id)
          : projectAddresses[0];

        this.orderForm.address_source = 'project';
        this.orderForm.selected_project_id = defaultProject.id;
        this.orderForm.contact_address = defaultProject.address;
      } else if (unitAddress && !this.isProjectPersonnel) {
        this.orderForm.address_source = 'unit';
        this.orderForm.selected_project_id = null;
        this.orderForm.contact_address = unitAddress;
      }
    },

    restoreAddressSelectionFromOrder(orderData) {
      const savedAddress = orderData.deliveryAddress || orderData.contactAddress || this.orderForm.contact_address;
      if (!savedAddress) {
        return;
      }

      const projectAddresses = this.orderForm.available_addresses.project_addresses || [];
      const matchingProject = projectAddresses.find(project => project.address === savedAddress);

      if (matchingProject) {
        this.orderForm.address_source = 'project';
        this.orderForm.selected_project_id = matchingProject.id;
        this.orderForm.contact_address = savedAddress;
        return;
      }

      const unitAddress = this.orderForm.available_addresses.unit_address;
      if (!this.isProjectPersonnel && unitAddress && unitAddress === savedAddress) {
        this.orderForm.address_source = 'unit';
        this.orderForm.selected_project_id = null;
        this.orderForm.contact_address = savedAddress;
        return;
      }

      this.orderForm.contact_address = savedAddress;
      if (orderData.secondaryOrgIds?.length === 1) {
        this.orderForm.address_source = 'project';
        this.orderForm.selected_project_id = orderData.secondaryOrgIds[0];
      } else if (!this.isProjectPersonnel && orderData.secondaryOrgIds?.length > 1) {
        this.orderForm.address_source = 'unit';
        this.orderForm.selected_project_id = null;
      } else if (this.isProjectPersonnel && orderData.secondaryOrgIds?.length > 0) {
        this.orderForm.address_source = 'project';
        this.orderForm.selected_project_id = orderData.secondaryOrgIds[0];
      }
    },
    
    // 脱敏姓名
    maskName(name) {
      return maskNameUtil(name);
    },
    
    // 脱敏电话
    maskPhone(phone) {
      return phone || '';
    }
  }
};
</script>

<style scoped>
.supplier-orders-list-container {
  padding: 20px;
  background-color: #fff;
}
  
  .page-header {
    margin-bottom: 20px;
}
    
    .header-title {
      display: flex;
      align-items: center;
}
      
      .title-mark {
        width: 4px;
        height: 18px;
        background-color: #2c73eb;
        margin-right: 8px;
      }
      
.header-title span {
        font-size: 18px;
        font-weight: 500;
        color: #303133;
  }
  
  .search-area {
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}
    
    .search-input {
      width: 220px;
      margin-right: 15px;
      margin-bottom: 10px;
    }
    
.search-area .el-button {
      padding: 11px 20px;
      margin-bottom: 10px;
  }
  
  .pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: center;
  }

/* 创建订单相关样式 */
.materials-list {
  margin-top: 10px;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 10px;
  background-color: #f9f9f9;
}

.contract-items-filter {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 12px;
}

.contract-items-filter-input {
  width: 320px;
}

.contract-items-filter-tip {
  font-size: 12px;
  color: #909399;
}

.total-info {
  margin-top: 15px;
  text-align: right;
  font-weight: bold;
  padding: 10px;
  border-top: 1px solid #EBEEF5;
}

/* 供应商信息样式 */
.supplier-info {
  margin: 10px 0;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
  line-height: 1.6;
}

.danger-button {
  color: #F56C6C;
}
.danger-button:hover {
  color: #F78989;
}

/* 订单类型标签样式 */
.el-tag {
  padding: 4px 8px;
}

.el-tag--success {
  background-color: rgba(103, 194, 58, 0.1);
}

.el-tag--primary {
  background-color: rgba(64, 158, 255, 0.1);
}

/* 调试区域样式 */
.debug-section {
  margin-top: 20px;
  padding: 15px;
  background-color: #f0f9ff;
  border: 1px dashed #409eff;
  border-radius: 4px;
}

.debug-section h3 {
  margin-top: 0;
  margin-bottom: 10px;
  color: #409eff;
}

.debug-section pre {
  white-space: pre-wrap;
  word-break: break-all;
  background-color: #f5f7fa;
  padding: 10px;
  border-radius: 4px;
  max-height: 300px;
  overflow: auto;
}

/* 表单提示样式 */
.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin-top: 5px;
}

/* 材料清单表格横向滚动样式 */
.materials-list {
  overflow-x: auto;
  width: 100%;
}

.materials-list .el-table {
  min-width: 1000px; /* 设置最小宽度确保所有列都能显示 */
}

/* 多合同订单相关样式 */
.source-contracts {
  color: #409eff;
  cursor: pointer;
  font-size: 12px;
}

.contract-info {
  margin-top: 15px;
  font-size: 12px;
  color: #606266;
}




/* 创建模式选择样式 */
.el-radio-group {
  margin-bottom: 10px;
}

.el-radio {
  margin-right: 20px;
}

/* 表单提示样式增强 */
.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin-top: 5px;
  padding: 5px 8px;
  background-color: #f0f9ff;
  border-radius: 4px;
  border-left: 3px solid #409eff;
}

/* 供应商选择区域样式 */
.supplier-selection-area {
  background-color: #f9f9f9;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 15px;
}

/* 合同选择区域样式 */
.contract-selection-area {
  background-color: #f0f9ff;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  border: 1px solid #b3d8ff;
}

/* 材料清单操作按钮样式 */
.el-table .el-button--mini {
  padding: 5px 8px;
  font-size: 12px;
}

.el-table .el-button--danger {
  background-color: #f56c6c;
  border-color: #f56c6c;
}

.el-table .el-button--danger:hover {
  background-color: #f78989;
  border-color: #f78989;
}

/* 订单信息展示区域样式 */
.order-info-display {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 20px;
  margin: 20px 0;
}

.order-title-input .el-input__inner {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  background-color: #fff;
}

.contract-info-display {
  margin-top: 15px;
}

/* 多合同显示样式 */
.multi-contract-display {
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 16px;
  background-color: #fafafa;
}

.multi-contract-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e4e7ed;
}

.multi-contract-header h4 {
  margin: 0 0 4px 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
}

.common-info {
  margin-bottom: 20px;
  padding: 12px;
  background-color: #fff;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

.supplier-contracts {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.supplier-contract-item {
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 16px;
  position: relative;
}

.supplier-contract-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #409eff;
  border-radius: 2px 0 0 2px;
}

.supplier-contract-header {
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.supplier-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.contract-count {
  font-size: 12px;
  color: #909399;
  margin-left: 8px;
}

.supplier-contract-details {
  padding-left: 8px;
}

.info-row {
  margin-bottom: 12px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-item {
  display: flex;
  align-items: center;
  min-height: 32px;
}

.info-item label {
  min-width: 100px;
  font-weight: 500;
  color: #606266;
  margin-right: 10px;
  flex-shrink: 0;
}

.info-item span {
  color: #303133;
  flex: 1;
  word-break: break-all;
}

.info-item .el-input {
  flex: 1;
}

.info-item .el-input .el-input__inner {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
}

.info-item .el-input .el-input__inner:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

/* 转发订单相关样式 */
.order-basic-info {
  margin-top: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

/* 订单选择表格选中行样式 */
.selected-order-row {
  background-color: #ecf5ff !important;
}

.selected-order-row:hover {
  background-color: #d4e8ff !important;
}

.order-basic-info h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
}

.product-comparison {
  margin-top: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

.product-comparison h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
}

.validation-success {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background-color: #f0f9ff;
  border: 1px solid #b3d8ff;
  border-radius: 4px;
  color: #409eff;
  margin-bottom: 15px;
}

.validation-success i {
  margin-right: 8px;
  font-size: 16px;
}

.validation-error {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background-color: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 4px;
  color: #f56c6c;
  margin-bottom: 15px;
}

.validation-error i {
  margin-right: 8px;
  font-size: 16px;
}

/* 地址选择器样式 */
.address-selector {
  width: 100%;
}

/* 多需求方相关样式 */
.multi-customer-tip {
  margin-bottom: 15px;
}

.order-group-preview {
  margin-top: 20px;
}

.order-group {
  margin-bottom: 20px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.group-header {
  background-color: #f5f7fa;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e4e7ed;
}

.group-title {
  font-weight: 500;
  color: #303133;
}

.group-count {
  color: #909399;
  font-size: 12px;
}

.address-options {
  margin-top: 8px;
  padding: 8px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  max-height: 160px;
  overflow-y: auto;
}

.address-option {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  padding: 4px 0;
}

.address-option:last-child {
  margin-bottom: 0;
}

.address-label {
  font-size: 12px;
  color: #606266;
  min-width: 60px;
  margin-right: 8px;
}

.address-text {
  flex: 1;
  font-size: 12px;
  color: #303133;
  word-break: break-all;
  margin-right: 8px;
}

.address-option .el-button {
  padding: 2px 8px;
  font-size: 12px;
}

/* 供应商分组预览样式 */
.supplier-groups-preview {
  margin-top: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
}

.supplier-groups-preview h4 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 16px;
}

.supplier-groups {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.supplier-group {
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
}

.group-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.supplier-name {
  font-weight: bold;
  color: #409eff;
  font-size: 16px;
}

.product-count {
  margin-left: 10px;
  color: #909399;
  font-size: 14px;
}

.group-products {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 4px;
}

.product-name {
  font-weight: 500;
  color: #303133;
  min-width: 120px;
}

.product-spec {
  color: #606266;
  min-width: 100px;
}

.product-quantity {
  color: #409eff;
  font-weight: 500;
  min-width: 80px;
}
</style>

<style lang="scss">
.spec-tooltip {
  max-width: 600px !important;
  white-space: normal;
  word-break: break-all;
}

/* 创建/转发订单弹窗：占满视口，合同清单约 2 屏高度并在表格内滚动 */
.order-form-dialog {
  margin-top: 2vh !important;
  margin-bottom: 2vh !important;
  display: flex;
  flex-direction: column;
  max-height: 96vh;

  .el-dialog__header {
    flex-shrink: 0;
  }

  .el-dialog__body {
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }

  .el-dialog__footer {
    flex-shrink: 0;
    border-top: 1px solid #ebeef5;
  }

  .contract-items-form-item {
    margin-bottom: 0;
  }
}
</style>
